import { useSyncExternalStore } from 'react';

/**
 * Two languages, one dictionary each, no library.
 *
 * The current language lives in a module-level store rather than a React
 * context, so the switch re-renders everything without threading a prop
 * through a thousand lines of screens that do not otherwise care.
 *
 * A missing Russian string falls back to English. Never a blank, never a
 * raw key — a client seeing `book.pickATime` is worse off than one seeing
 * English.
 *
 * Nothing a coach typed passes through here: session type names, package
 * names, notes and locations are their words and are rendered verbatim.
 */

export type Lang = 'en' | 'ru';

export const asLang = (v: string | null | undefined): Lang =>
  v?.slice(0, 2).toLowerCase() === 'ru' ? 'ru' : 'en';

export const localeOf = (lang: Lang) => (lang === 'ru' ? 'ru-RU' : 'en-GB');

/** 1 занятие · 2 занятия · 5 занятий */
export function plural(lang: Lang, n: number, forms: readonly string[]): string {
  if (lang !== 'ru') return n === 1 ? forms[0] : forms[1];
  // A fraction takes the genitive singular: 0,75 кредита, 1,5 кредита.
  if (!Number.isInteger(n)) return forms[1];
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return forms[1];
  return forms[2];
}

// ---------- the store ----------

let current: Lang = 'en';
const listeners = new Set<() => void>();

export const getLang = (): Lang => current;

/** A skill's name in the app's language: the Russian one when the coach set it. */
export function axisName(a: { name: string; name_ru?: string | null }) {
  return getLang() === 'ru' ? (a.name_ru || a.name) : a.name;
}
export function setLang(next: Lang) {
  if (next === current) return;
  current = next;
  // Tells screen readers which language to pronounce, and lets CSS reach
  // the few places where Russian needs different spacing to fit.
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  for (const f of listeners) f();
}
function subscribe(f: () => void) {
  listeners.add(f);
  return () => { listeners.delete(f); };
}
export const useLang = (): Lang => useSyncExternalStore(subscribe, getLang, getLang);

// ---------- dates ----------

const fmt = (d: Date, lang: Lang, o: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(localeOf(lang), o).format(d);

/** "Saturday, 26 September" / "суббота, 26 сентября" */
export const fmtLongDayL = (d: Date, lang: Lang) =>
  fmt(d, lang, { weekday: 'long', day: 'numeric', month: 'long' });
/** "Sat 26 Sept" / "сб, 26 сент." */
export const fmtShortDayL = (d: Date, lang: Lang) =>
  fmt(d, lang, { weekday: 'short', day: 'numeric', month: 'short' });
export const fmtTimeL = (d: Date | string, lang: Lang) =>
  fmt(typeof d === 'string' ? new Date(d) : d, lang,
      { hour: '2-digit', minute: '2-digit', hour12: false });
/** The word between a date and a time. */
export const atWord = (lang: Lang) => (lang === 'ru' ? 'в' : 'at');

type Vars = Record<string, string | number>;
const fill = (s: string, vars?: Vars) =>
  vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`)) : s;

/**
 * Russian writes numbers its own way: «2 856 €», «7,2». A Russian string's
 * "€{x}" moves the sign after the amount, number values get Russian
 * grouping and a decimal comma, and a value already turned into text with
 * toFixed ("4.8") only has its point swapped, so its digits stay as they
 * were. Whole numbers passed as text are left alone — that is how ids and
 * years would arrive, and "2 026" is not a year.
 */
function russianNumbers(s: string, vars?: Vars): [string, Vars | undefined] {
  const text = s.replace(/€\{(\w+)\}/g, '{$1}\u00a0€');
  if (!vars) return [text, vars];
  const out: Vars = {};
  for (const [k, v] of Object.entries(vars)) {
    out[k] = typeof v === 'number' ? RU_NUM.format(v)
      : /^[-−+]?\d+\.\d+$/.test(v) ? v.replace('.', ',')
      : v;
  }
  return [text, out];
}
const RU_NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** A number for the screen: «7,2» and «2 856» in Russian, 7.2 and 2,856 in English. */
export function fmtNum(n: number | string, digits?: number): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  return new Intl.NumberFormat(localeOf(getLang()), digits === undefined
    ? { maximumFractionDigits: 2 }
    : { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
}

/** An amount in euros: «2 856 €» in Russian, «€2,856» in English. */
export function fmtMoney(n: number | string): string {
  const s = fmtNum(n);
  return getLang() === 'ru' ? `${s}\u00a0€` : `€${s}`;
}

// ---------- dictionary ----------

const EN = {
  'lang.en': 'EN',
  'lang.ru': 'RU',

  // App shell
  'app.creditsLeft': ['credit left', 'credits left'],
  'tab.book': 'Book',
  'tab.bookings': 'My sessions',
  'tab.credits': 'Credits',
  'vis.me': '🔒 Only you',
  'vis.player': '👁 Player sees',
  'co.rate.sharedLabel': 'Note for {name}',
  'co.rate.homeworkLabel': 'Homework (optional)',
  'co.rate.clearWhenDone': 'Clear it when it is done.',
  'jr.lessons': ['lesson', 'lessons'],
  'jr.noNotes': 'No notes',
  'jr.count': '{n} {lessons}',
  'jr.since': 'since {date} · {n} {sessions}',
  'jr.newGoal': 'New goal:',
  'jr.newGoalFrom': 'New goal from {coach}:',
  'jr.drills': 'What you did',
  'jr.drillsPlayer': 'What we did',
  'jr.shared': 'Note for {name}',
  'jr.fromCoach': 'From {coach}',
  'jr.homework': 'Homework',
  'jr.common': 'For everyone on the session',
  'jr.yourTask': 'Your task',
  'jr.ratings': 'Ratings',
  'jr.private': 'Private note',
  'jr.asked': '{name} asked to work on',
  'jr.askedPlayer': 'You asked for',
  'jr.fromPlayer': '💬 From {name}',
  'jr.missed': 'Missed',
  'jr.min': '{n} min',
  'jr.nothing': 'Nothing written for this lesson.',
  'jr.empty': 'No lessons yet. They appear here after each one.',
  'lv.back': '‹ Back',
  'lv.couldNotOpen': "Couldn't open that lesson.",
  'lv.credits': ['credit', 'credits'],
  'lv.nCredits': '{n} {credits}',
  'lv.noDrills': 'Nothing written yet. Tap Edit to add the drills.',
  'lv.ratings': 'Ratings',
  'lv.vsLast': 'change vs last time',
  'lv.first': 'first',
  'lv.notRated': 'Not rated yet.',
  'lv.noNote': 'No note yet.',
  'lv.noPrivate': 'No private note.',
  'lv.homework': 'Homework:',
  'jr.failed': 'Could not load your lessons. Open this tab again to retry.',
  'tab.profile': 'My profile',

  // Booking
  'book.pickTime': 'Pick a time',
  'book.noTimes': 'Nothing open in the next few weeks.',
  'book.repeatWeekly': 'Repeat weekly',
  'book.weeks': ['week', 'weeks'],
  'book.payWith': 'Pay with',
  'book.credits': 'Credits',
  'book.cash': 'Cash',
  'book.book': 'Book',
  'book.booking': 'Booking…',
  'book.booked': 'Booked {n} {sessions}.',
  'book.creditsLeft': '\n{n} credits left.',
  'book.totalCash': ' €{amount} total, paid at each session.',
  'book.skippedTaken': '\n\n{n} {weeks} already taken and skipped.',
  'book.skippedHorizon': '\n\n{n} {weeks} further ahead than your coach takes bookings. Book those nearer the time.',
  'book.workingOn': 'What do you want to work on?',

  // Bookings list
  'note.save': 'Save',
  'note.saving': 'Saving…',
  'note.failed': 'Could not save that.',
  'bookings.none': 'Nothing booked yet',
  'bookings.noneHint': "Pick a time from the Book tab and you'll see it here.",
  'bookings.book': 'Book a session',
  'bookings.today': 'Today',
  'bookings.tomorrow': 'Tomorrow',
  'bookings.later': 'Later',
  'bookings.private': 'Private session · {min} min',
  'bookings.in': 'in {time}',
  'cal.feedTitle': 'Calendar',
  'cal.feedName': 'Sessions in my calendar',
  'cal.feedHint': 'Updates by itself. Past sessions stay, cancelled ones disappear.',
  'cal.feedSubscribe': 'Subscribe',
  'cal.which': 'Which calendar do you use?',
  'cal.title': 'Add to calendar',
  'cal.google': 'Google',
  'cal.appleOther': 'Apple / other',
  'cal.confirmGoogle': 'Add to Google Calendar? (Cancel for Apple/other)',
  'bookings.toPay': '€{amount} to pay at the session',
  'bookings.toPaySplit': 'To pay at the session: €{amount} (€{covered} already on your balance)',
  'bookings.freeCancelPassed': 'Free cancellation has passed',
  'bookings.freeCancelFor': 'Free cancellation for {time}',
  'bookings.move': 'Move',
  'bookings.cancel': 'Cancel booking',
  'bookings.addFriend': 'Invite a friend',
  'bookings.waitlist': 'Waitlist position {n} · not charged',

  // Cancelling
  'cancel.title': 'Cancel this session?',
  'cancel.lateWarning': "This is inside the cancellation window, so the credit won't come back. Cancel anyway?",
  'cancel.lateWarningCash': 'This is inside the cancellation window, so the session will still be charged: €{amount}. Cancel anyway?',
  'cancel.confirm': 'Cancel {title} on {day}? Your credit will be returned.',
  'cancel.back': 'Back',

  // Move
  'move.title': 'Move your session',
  'move.currently': 'Currently {day} at {time}.',
  'move.pickNew': 'Pick a new time',
  'move.nothingOpen': 'Nothing else open in the next 30 days.',
  'move.moving': 'Moving…',
  'move.moveTo': 'Move to {time}',
  'move.pickATime': 'Pick a time',
  'move.moved': 'Moved to {day} at {time}.',
  'move.tooLate': 'Too close to the session to move it. Message your coach.',
  'move.slotGone': 'That time has just been taken. Pick another.',
  'move.failed': 'Could not move that. Try again in a moment.',

  // Credits
  'credits.balance': 'Your credits',
  'credits.history': 'History',
  'credits.packages': 'Your packages',
  'credits.anySession': 'Any session',
  'credits.groupOnly': 'Group classes',
  'credits.privateOnly': 'One to one',
  'credits.expires': 'Expires {day} · {left} left',
  'credits.none': 'No credits yet',
  'credits.noneHint': 'Message your coach to buy a package, or book and pay per session.',
  'ledger.purchase': 'Package bought',
  'ledger.booked': 'Booked {title} · {time}',
  'ledger.bookedPlain': 'Session booked',
  'ledger.refund': 'Cancelled in good time',
  'ledger.expiry': 'Credits expired',
  'ledger.noShow': 'Missed session',
  'ledger.adjustment': 'Adjustment',

  // ---------- coach: the shell and the clients list ----------
  // Russian on the coach side never guesses a client's sex: no past-tense
  // verb with the client as subject, no "he"/"she", no declined name.
  'co.coachView': 'coach view',
  'co.more': '…more',
  'co.tab.today': 'Calendar',
  'co.tab.money': 'Money',
  'co.tab.clients': 'Clients',
  'co.tab.setup': 'Setup',
  'co.set.version': 'Version {v}',
  'co.clients.title': 'Clients',
  'co.clients.search': 'Search by name, @username or phone',
  'co.off.add': "+ Add client manually",
  'co.off.addTitle': "New client",
  'co.off.name': "Name",
  'co.off.phone': "Phone",
  'co.off.note': "Note to self",
  'co.off.optional': "optional",
  'co.off.onlyYou': "only you see it",
  'co.off.notePh': "e.g. plays on Wednesdays with her husband",
  'co.off.info': "The bot never messages this client and never offers them freed slots. Bookings, moves and payments go through you. When they open your link in Telegram, tap «Link» and everything moves to their bot profile.",
  'co.off.cancel': "Cancel",
  'co.off.addBtn': "Add",
  'co.off.adding': "Adding…",
  'co.off.tag': "not in the bot",
  'co.off.addedOn': "added manually · {day}",
  'co.off.linkAsk': "Opened your link in Telegram?",
  'co.off.linkGo': "Link →",
  'co.off.pickTitle': "Link to Telegram",
  'co.off.pickWho': "Who in the bot is {name}?",
  'co.off.pickSearch': "Search: name or @username",
  'co.off.alike': "Likely match",
  'co.off.samePhone': "✓ same phone",
  'co.off.sameName': "✓ same name",
  'co.off.others': "Others who opened your link",
  'co.off.waiting': "waiting for your approval",
  'co.off.since': "in the bot since {day}",
  'co.off.onlyBot': "Only clients who opened the bot or the app.",
  'co.off.noneFound': "Nobody found.",
  'co.off.nobodyYet': "Nobody in the bot to link to yet. Send them your link first.",
  'co.off.confirmTitle': "Link {name}?",
  'co.off.manual': "added manually",
  'co.off.inBot': "in the bot",
  'co.off.moves': "Moves to {name}",
  'co.off.mvSessions': "{n} {sessions}",
  'co.off.mvUpcoming': ", {n} upcoming",
  'co.off.mvCredits': "Credits: {n}",
  'co.off.mvPackages': "Packages: {n}",
  'co.off.mvPayments': "Cash payments: {n}",
  'co.off.mvOwed': "Owed: {amount}",
  'co.off.mvNotes': "Ratings, notes and “Working on”",
  'co.off.mvRest': "Tags, personal prices, your note and the phone number",
  'co.off.keep': "The profile keeps the name “{name}”. Language and @username come from Telegram. The manual profile disappears. This can’t be undone.",
  'co.off.clash': "Both are booked on {when}. Cancel one of the bookings, then link.",
  'co.off.close': "Close",
  'co.off.linkBtn': "Link",
  'co.off.done': "{name} is in the bot now.",
  'co.off.doneUpcoming': "{name} is in the bot now. Reminders will go out for upcoming sessions: {n}.",
  'co.off.reqMatch': "Looks like a client you added manually",
  'co.off.aiOff': "AI is off: this client is not in the bot.",
  'co.clients.all': 'All',
  'co.clients.someOf': '{n} of {total}',
  'co.clients.none': 'No clients yet.',
  'co.clients.noneFiltered': 'Nobody has this tag yet.',
  'co.clients.onlyShown': 'Only {tag} shown.',
  'co.clients.clearFilter': 'Clear the filter',
  'co.clients.firstSession': 'First session {when}',
  'co.clients.noSessionsYet': 'no sessions yet',
  'co.clients.editTags': 'Edit tags',
  'co.status.lapsed': 'Gone quiet',
  'co.status.slipping': 'Slipping',
  'co.status.new': 'New',
  'co.why.lapsed': 'No lesson for {n} {days}',
  'co.why.slipping': '{n} days since the last one, usually every {gap}',
  'co.why.new': 'First sessions',
  'co.freq.twiceWeekly': 'About twice a week',
  'co.freq.weekly': 'Weekly',
  'co.freq.fortnightly': 'Every couple of weeks',
  'co.freq.occasional': 'Occasionally',
  'co.freq.none': 'Not in the last 8 weeks',
  'co.level': 'Level {n}',
  'co.noLevel': 'No level set',
  'co.progress.up': '{n} this month',
  'co.progress.down': '{n} this month',
  'co.progress.steady': 'Steady this month',
  'co.progress.none': 'Not enough ratings yet',
  'co.days': ['day', 'days'],
  'co.tag.add': 'Add tag',
  'co.tag.addTo': 'Add a tag to {name}',
  'co.tags.title': 'Tags for {name}',
  'co.tags.hint': 'Tap to add or remove. Only you see these.',
  'co.tags.new': 'New tag',
  'co.tags.newPlaceholder': 'Mondays 7am, Tournament squad…',
  'co.tags.add': 'Add',
  'co.tags.done': 'Done',
  'co.tags.edit': 'Edit tag',
  'co.tags.name': 'Name',
  'co.tags.colour': 'Colour',
  'co.tags.save': 'Save',
  'co.tags.cancel': 'Cancel',
  'co.tags.delete': 'Delete tag',
  'co.tags.deleteTitle': 'Delete “{tag}”?',
  'co.tags.deleteBody': '{n} {clients} have this tag. It comes off them. Their sessions, notes and history are untouched.',
  'co.tags.deleteEmpty': 'Nobody has this tag. Delete it?',
  'co.tags.keep': 'Keep it',
  'co.tags.confirmDelete': 'Delete',
  'co.tags.clients': ['client', 'clients'],
  'co.tags.exists': 'You already have a tag with that name.',
  'co.tags.failed': 'Could not save that tag.',
  'co.tags.newTag': '+ New tag',
  'co.tags.none': 'No tags yet.',
  'co.colour.teal': 'Teal',
  'co.colour.blue': 'Blue',
  'co.colour.amber': 'Amber',
  'co.colour.purple': 'Purple',
  'co.colour.red': 'Red',
  'co.colour.grey': 'Grey',

  // ---------- coach: Today ----------
  'co.today.nextUp': 'Next up',
  'co.month.open': 'Choose a date',
  'co.month.prev': 'Previous month',
  'co.month.next': 'Next month',
  'co.month.sessions': 'sessions',
  'co.month.unpaid': 'unpaid',
  'co.today.onCourt': 'On court now',
  'co.today.nothingToday': 'Nothing booked today',
  'co.today.nothing': 'Nothing booked',
  'co.today.nobodyToTell': 'Nothing booked — nobody to tell.',
  'co.today.toCollect': '€{amount} to collect',
  'co.today.allDone': 'All done for today',
  'co.today.left': '{n} min left',
  'co.today.group': 'Group · {n} {players}',
  'co.today.players': ['player', 'players'],
  'co.today.showSession': 'Show the {time} session',
  'co.today.numTaught': 'taught',
  'co.today.numEarned': 'earned',
  'co.today.numToCollect': 'to collect',
  'co.today.tomorrow': 'Tomorrow: {time} · {who}',
  'co.today.tomorrowNone': 'Nothing booked tomorrow',
  'co.live.open': 'Open the {time} session',
  'co.live.over': 'Lesson over',
  'co.live.endedAt': 'ended at {time}',
  'co.rate.lastTime': 'last time {n}',
  'co.rate.lastTimeSet': 'Set {n}, as last time',
  'co.rate.restored': 'Restored what had not been sent',
  'co.hub.attendance': 'Attendance: {name}',
  'co.hub.came': 'Came',
  'co.hub.noShow': 'Didn’t come',
  'co.hub.cash': 'Pays cash',
  'co.hub.balance': 'Balance: {n} {credits}',
  'co.hub.balanceOf': 'Balance ({name}): {n} {credits}',
  'co.hub.write': 'Message',
  'co.hub.call': 'Call',
  'co.hub.profile': 'Profile',
  'co.hub.addPlayer': '+ Player',
  'co.today.sessionsTaught': '{n} {sessions} taught today',
  'co.today.sessions': ['session', 'sessions'],
  'co.today.inMin': 'in {n} min',
  'co.today.until': 'until {time}',
  'co.today.day': 'Today',
  'co.today.count': '{n} {sessions}{done}',
  'co.today.done': ' · {n} done',
  'co.today.cancelDay': 'Cancel day',
  'co.today.openSlot': 'Open slot',
  'co.today.now': 'Now',
  'co.today.add': '+ Add',
  'co.rm.more': 'More: {name}',
  'co.rm.action': 'Remove from session',
  'co.rm.title': 'Remove from session: {name}',
  'co.rm.body': 'The booking was a mistake and is undone in full: credits go back and the charge for the session is dropped. No late cancel, no no-show, nothing in their attendance. The others go back to the price for one fewer.',
  'co.rm.silent': 'Don’t tell them',
  'co.rm.silentHint': 'For someone who never knew about the booking.',
  'co.rm.willSay': '{name} gets a short message in Telegram: added by mistake, nothing charged.',
  'co.rm.wontSay': 'No message to {name}.',
  'co.rm.noBot': '{name} isn’t in the bot, so there’s no message either way.',
  'co.rm.do': 'Remove',
  'co.rm.done': '{name} is out of the session.',
  'co.rm.doneCredits': ' {n} {credits} returned.',
  'co.rm.doneTold': ' They’ll get a message.',
  'co.rm.doneSilent': ' No message sent.',
  'co.rm.rated': '{name} can’t be removed: this session already has ratings or notes. Removing works only before ratings, notes or a payment.',
  'co.rm.paid': '{name} can’t be removed: cash for this session has already been received from them.',
  'co.rm.last': '{name} is the only one in this session. To call it off, use «Cancel».',
  'co.today.came': 'Came',
  'co.today.noShow': 'No-show',
  'co.today.lateCancel': 'Late cancel',
  'co.today.markNoShow': 'No-show',
  'co.today.rate': 'Rate',
  'co.today.got': 'Got €{amount}',
  'co.today.gotDots': 'Received…',
  'co.today.owes': 'Owes €{amount}',
  'co.today.splitPill': '€{amount} + {n} cr.',
  'co.today.splitLine': '€{amount} cash + {n} {credits}',
  'co.paid.title': 'Payment: {name}',
  'co.paid.due': 'Due',
  'co.paid.stillOwed': 'Still owed',
  'co.paid.received': 'Received',
  'co.paid.extra': '+{extra} → {n} {credits} to their balance',
  'co.paid.rate': 'at this lesson’s price: {rate} per credit',
  'co.paid.noRate': 'This lesson has no price per credit, so the extra can’t become credits.',
  'co.paid.short': '{left} stays owed',
  'co.paid.fromCredits': 'The remaining {amount} comes off the balance ({n} {credits})',
  'co.paid.fromPayerCredits': 'The remaining {amount} comes off {payer}’s balance ({n} {credits})',
  'co.paid.mix': '{cash} in cash + {n} {credits} · owed {left}',
  'co.paid.rounding': ' (rounding {amount})',
  'co.paid.restRounding': 'The remaining {amount} is rounding — nothing will be owed.',
  // «Оплата» as a receipt (components/LessonPayment.tsx, docs/design/lesson-payment.md).
  'lp.useBalance': 'Use the balance',
  'lp.balanceSub': 'On the balance: {n} cr. · {rate} a lesson',
  'lp.balanceSubPayer': '{payer}’s balance: {n} cr.',
  'lp.rest': 'Left to pay',
  'lp.next': '+ Pay for the next lesson',
  'lp.nextCreditsSub': 'Booked on credits: {n} cr. go to the balance for it',
  'lp.toBalance': '+ {amount} to the balance ({n} cr.)',
  'lp.toBalanceSub': 'No lesson booked ahead',
  'lp.total': 'Received {amount} · {method}',
  'lp.partToday': '{amount} today',
  'lp.partNext': '{amount} for {day}',
  'lp.partNextShort': '{amount} for {day} ({left} still owed)',
  'lp.partBalance': '{amount} to the balance ({n} cr.)',
  'lp.fromBalance': '+ {n} cr. from the balance · {left} cr. left',
  'lp.save': 'Received {amount}',
  'lp.saveBalance': 'Take from the balance',
  'lp.short': '{left} stays owed for this lesson',
  'lp.err.prepay_gone': 'The next lesson changed meanwhile — open the payment again.',
  'lp.err.changed_since': 'The lesson changed after the payment — it can’t be undone, fix it by hand.',
  'lp.err.credits_used': 'The credits from this payment are already used — change that lesson first.',
  'lp.err.read_only': 'Older than 60 days — view only.',
  'lp.err.more_is_new_payment': 'More money is a new payment: use «Received…».',
  'lp.err.method_required': 'Choose how they paid.',
  'lp.err.already_undone': 'This payment is already undone.',
  'lp.err.nothing_to_pay': 'Nothing to record.',
  'lp.err.other': 'Couldn’t save: {code}',
  'lp.paidBtn': '✓ Paid',
  'lp.prepaidBtn': '✓ Paid ahead',
  'lp.editTitle': 'Change payment: {name}',
  'lp.edit': 'Change payment',
  'lp.paidLine': 'Paid',
  'lp.byCredits': '{n} cr. from the balance',
  'lp.receivedBy': 'Received {at} · {who}',
  'lp.sameReceipt': 'in the same payment: {parts}',
  'lp.ahead': 'ahead',
  'lp.amountThis': 'Amount for this lesson',
  'lp.amountHint': 'Less: the difference is owed again. More is a new payment.',
  'lp.methodAll': 'The method changes for the whole {amount} payment.',
  'lp.undo': 'Undo payment {amount}',
  'lp.undoConfirm': 'Undo the payment of {amount}? The lessons in it are owed again and the credits it used return to the balance.',
  'lp.saveChange': 'Save',
  'lp.back': 'Back',
  'lp.changes': 'Changes',
  'lp.ch.method': 'method: {old} → {new}',
  'lp.ch.amount': 'amount: {old} → {new}',
  'lp.ch.undo': 'payment undone',
  'lp.ch.paid': 'received {amount}',
  'lp.undoneLine': 'Undone {at} · {who}',
  'lp.history': 'Payment history',
  'lp.onBalance': 'Balance: {n} cr.',
  'lp.f.all': 'All',
  'lp.f.money': 'Money',
  'lp.f.credits': 'Credits',
  'lp.f.changes': 'Changes',
  'lp.h.received': 'Received {amount}',
  'lp.h.partCash': '{amount} for the lesson {day}',
  'lp.h.partCredits': '{n} cr. from the balance',
  'lp.h.partAhead': '{amount} for {day} — ahead',
  'lp.h.partToBalance': '{amount} → {n} cr. to the balance',
  'lp.h.forLesson': ' for {day}',
  'lp.h.lesson': 'Lesson {day}',
  'lp.h.topup': 'Top-up {amount} → {n} cr.',
  'lp.h.package': 'Package «{name}» {amount}',
  'lp.h.overpay': 'Overpayment {amount} → {n} cr.',
  'lp.h.refunded': 'refunded',
  'lp.h.use': 'Used {n} cr.',
  'lp.h.refund': 'Returned {n} cr.',
  'lp.h.expiry': 'Expired {n} cr.',
  'lp.h.adjust': 'Adjusted {n} cr.',
  'lp.h.change': 'Changed: {what}',
  'lp.h.empty': 'No payments yet.',
  'lp.h.undone': 'undone',
  'lp.open': 'Payment history',
  'lp.due': 'To pay {amount}',
  // «Заменить оплату»: one lesson's payment traded for another's.
  // A tap outside a sheet that has been typed in.
  'sheet.closeUnsaved': 'Close without saving?',
  // «Постоянные»: the client is still coming, but nothing is booked ahead.
  // Deliberately not the words or the colour of «Reach out», which is
  // about somebody who has stopped coming.
  // «Постоянные»: the client is still coming, but nothing is booked ahead.
  'co.reg.chip': 'Booked to {date}',
  'co.reg.none': 'Nothing booked yet',
  'co.reg.filter': 'Regulars · {n}',
  'co.reg.onNow': '★ {name} is a regular now',
  'co.reg.offNow': '{name} is no longer marked as a regular',
  'co.reg.mode': 'Reminders to clients',
  'co.reg.modeOff': 'Off',
  'co.reg.modeManual': 'By hand',
  'co.reg.modeAuto': 'Automatic',
  'co.reg.modeOffHint': 'No reminders.',
  'co.reg.modeManualHint': 'Once a day the bot sends you a list. The client gets a reminder only after you press «Remind».',
  'co.reg.modeAutoHint': 'The bot reminds the client itself and tells you.',
  'co.reg.onlyShown': 'Only the clients marked as regulars.',
  'co.reg.label': 'Regular',
  // Строка-переключатель на карточке: название и что он делает.
  'co.reg.rowLabel': 'Regular client',
  'co.reg.labelHint': 'We will remind them to book when their bookings run out',
  'co.reg.remind': 'Remind',
  'co.reg.reminded': 'The reminder is on its way.',
  'co.reg.sendTitle': 'Send the reminder?',
  'co.reg.sendWho': 'Your client will receive this message:',
  'co.reg.sendHint': 'The greeting and the lesson line are written by the app. Your own text is in Setup → Regular clients.',
  'co.reg.send': 'Send',
  'co.reg.errNoBot': '{name} has never opened the bot — there is nowhere to send it.',
  'co.reg.againAsk': 'A reminder to {name} went out less than six days ago. Send another one?',
  'co.reg.errNothing': 'Nothing to remind about.',
  'co.reg.rowTitle': 'Regular clients',
  'co.reg.rowDays': 'warn {n} {days} ahead',
  'co.reg.setLead': 'Only clients with ★ get reminders: the people you expect every week.',
  'co.reg.setTitle': 'Regulars',
  'co.reg.setDays': 'Warn me when bookings run out',
  'co.reg.horizon': 'Regulars can book ahead',
  'co.reg.horizonHint': 'Everyone else: {n} weeks. Bookings already made stay if you remove ★.',
  'co.reg.horizonSame': 'Same',
  'co.reg.weeksChip': '{n} wk',
  'co.reg.setDaysHint': 'How many days before their last booked lesson',
  'co.reg.dayChip': '{n} d',
  'co.reg.textAsIs': 'An edited line goes to every client exactly as written, untranslated.',
  'co.reg.setReset': 'Restore the standard text',
  'co.reg.setSave': 'Save the text',
  'co.reg.previewLine': 'This is what the client sees. You can change your own line.',
  'co.sw.title': 'Change the payment',
  'co.sw.now': '{name} · {lesson} — {how} now',
  'co.sw.inCash': '{amount} in cash',
  'co.sw.inCredits': '{n} {credits}',
  'co.sw.useFree': 'Use a free credit',
  'co.sw.freeLeft': '{n} {credits} free',
  'co.sw.taught': 'taught',
  'co.sw.booked': 'booked',
  'co.sw.becomesCash': 'becomes {price} in cash',
  'co.sw.backToBalance': 'Back to the balance',
  'co.sw.backToBalanceSub': 'the credit is free again',
  'co.sw.givesTo': 'takes the credit, {amount} no longer owed',
  'co.sw.confirm': 'Change the payment',
  'co.sw.prevFree': 'A free credit pays for {lesson}. Nothing else changes.',
  'co.sw.prevMove': 'The credit from {from} moves to {to}; {from} becomes {price} in cash.',
  'co.sw.prevBack': '{lesson} becomes {price} in cash and the credit goes back to the balance.',
  'co.sw.prevHand': 'The credit from {lesson} moves to {to}; {lesson} becomes {price} in cash.',
  'co.sw.errPaid': 'This lesson is already paid for. Nothing was changed.',
  'co.sw.errCancelled': 'That lesson is cancelled.',
  'co.sw.errNoCredit': 'No free credit, and no other lesson is holding one.',
  'co.sw.errNoCreditOnIt': 'This lesson is not paid with a credit.',
  'co.sw.errDonorEmpty': 'That lesson is no longer holding a credit.',
  'co.sw.errDonorSmall': 'That lesson holds less than this one costs.',
  'co.sw.errReceiverHas': 'That lesson already has a credit on it.',
  'co.sw.errOtherClient': 'That lesson belongs to somebody else.',
  'co.sw.errSame': 'That is this same lesson.',
  'co.sw.errGone': 'That lesson is gone — open the screen again.',
  'co.sw.action': 'Change the payment',
  'co.sw.useCredit': 'Use a credit',
  'co.sw.warnLater': 'Heads-up: after this, {n} {lessons} ahead will not be covered by credits — {amount} to pay at the lesson.',
  'co.sw.warnLaterNoPrice': 'Heads-up: after this, {n} {lessons} ahead will not be covered by credits.',
  'co.sw.lessonsW': ['lesson', 'lessons'],
  // Never a dead tap: what the payment sheet says when a save is refused.
  'co.paid.failed': 'Not saved: {message}',
  'co.paid.badAmount': 'Enter an amount above zero.',
  'co.paid.needRate': 'Set a price for this lesson before taking more than it costs.',
  'co.paid.btn': 'Received {amount}',
  'co.paid.onBalance': 'On balance: {n} {credits} · {amount}',
  'co.paid.onBalanceNoRate': 'On balance: {n} {credits}',
  'co.paid.fromBalance': 'Pay from balance',
  'co.paid.fromBalanceDone': '{n} {credits} taken · {left} left',
  'co.paid.fromBalanceOwed': ' · {cash} still to pay in cash',
  'co.today.paid': 'Paid',
  'co.today.credit': 'Credit',
  // Slice 2: credits are spent when the lesson happens (credit-on-use).
  'co.today.creditTaken': 'Credit taken',
  'co.today.notCharged': 'Not charged',
  'co.today.notChargedWhy': 'There were no credits for this lesson.',
  'cov.planned': 'Planned',
  'cov.covered': 'covered by credits',
  'cov.due': 'needs payment',
  'cov.legacyMore1': '1 more booked earlier — its credit is already taken',
  'cov.legacyMore': '{n} more booked earlier — their credit is already taken',
  'cov.legacyOnly1': '1 booked earlier — its credit is already taken',
  'cov.legacyOnly': '{n} booked earlier — their credit is already taken',
  'cov.paidBy': 'Shared account · paid by {name}',
  'cov.alsoFor': 'Shared account · also pays for: {names}',
  'pay.covered': 'covered',
  'pay.due': '{amount} at the lesson',
  'pay.legacy': 'credit already taken',
  'pay.legacyPart1': '1 credit already taken',
  'pay.legacyPartN': '{n} {credits} already taken',
  'me.onAccount': ['credit on your account', 'credits on your account'],
  'me.ahead': 'Booked ahead: {n}',
  'me.allCovered': 'all on your package',
  'me.coveredPart': '{w} on your package',
  'me.duePart1': 'one paid at the lesson ({amount})',
  'me.duePartN': '{w} paid at the lessons ({amount})',
  'me.dueAll': 'paid at the lessons ({amount})',
  'book.notCovered': 'This lesson is not covered by your package — {amount} at the lesson',
  'book.notCoveredN': '{n} of {total} are not covered by your package — {amount} at the lessons',
  'book.displaces': 'Your lesson on {day} will then be paid at the lesson — {amount}',
  'book.displacesN': '{n} of your booked lessons will then be paid at the lessons — {amount}',
  'co.today.noReminders': ' · no reminders',
  'co.today.privateNote': '🔒 Private note, only you see this',
  'co.today.addPrivateNote': '🔒 Add a private note',
  'co.today.saveNote': 'Save note',
  'co.today.noShowConfirm': 'Mark this as a no-show? The session still counts — the slot was held.',
  'co.today.paidConfirm': 'Mark €{amount} as received?',
  'co.today.cancelTitle': 'Cancel {name}',
  'co.today.cancel': 'Cancel',
  'co.today.cancelBody': '{day} at {time}. Full refund, and they are told.',
  'co.today.cancelNoMessage': 'They cannot be messaged — ring them.',
  'co.today.cancelDo': 'Cancel and tell them',
  'co.today.sessionMore': 'More: the {time} session',
  'co.today.cancelSession': 'Cancel session',
  'co.today.cancelSessionTitle': 'Cancel the {time} session',
  'co.today.cancelSessionBody': '{day}, {time}. Everyone in it — {names} — is told, and anything paid comes back.',
  'co.today.cancelDayConfirm': 'Cancel {n} {sessions} on {day}?\n\nEveryone is refunded and told why. This cannot be undone.',
  'co.today.cancelDayDone': '{n} cancelled, {r} {credits} returned.',
  'co.today.cancelDayBlocked': '\n\nThe day is blocked so nobody can rebook it.',
  'co.today.affected': '{n} {sessions} affected:',
  'co.today.unreachable': '{names} cannot be messaged — ring them.',
  'co.today.why': 'Why',
  'co.today.blockLabel': 'Block the day so it cannot be rebooked',
  'co.today.cancelDayTitle': 'Cancel {day}',
  'co.today.reasonLabel': 'Anything to add (optional)',
  'co.today.blockDay': 'Turn off if you are moving to another court',
  'co.today.cancelling': 'Cancelling…',
  'co.today.cancelAll': 'Cancel {n} and tell everyone',
  'co.reason.rain': 'Rain',
  'co.reason.noCourt': 'No court',
  'co.reason.illness': 'Illness',
  'co.reason.emergency': 'Emergency',
  'co.reason.other': 'Other',
  'co.pkg.confirmTitle': 'Confirm payment',
  'co.pkg.waiting': '{n} {packages} waiting for payment',
  'co.pkg.packages': ['package', 'packages'],
  'co.pkg.onlyWhenPaid': 'Only confirm once the money is actually in your hand.',
  'co.pkg.sessionsLabel': 'Sessions',
  'co.pkg.received': 'Amount received',
  'co.pkg.decline': 'Decline',
  'co.pkg.confirm': 'Confirm',
  'co.pkg.back': 'Back',
  'co.pkg.perSession': '€{amount} per session',
  'co.pkg.sessions': '{n} {sessions}',
  'co.pkg.granted': 'Done. {name} now has {n} {sessions}.',
  'co.pkg.declineConfirm': 'Decline this request? Nothing is charged.',
  'co.rate.searchClient': 'Search',
  'co.add.title': 'Add someone to {time}',
  'co.add.eachUses': 'Each uses {n} credit — a pair session costs half.',
  'co.add.nobody': 'Nobody matches.',
  'co.add.confirm': 'Add {who} to the {time} session for {price}?',
  'co.add.confirmCovered': 'Add {who} to the {time} session? It’s already paid for — nothing to pay.',
  'co.add.do': 'Add',
  'co.rp.action': 'Replace',
  'co.rp.title': 'Replace: {name}',
  'co.rp.confirm': 'Replace {old} → {new}?',
  'co.rp.oldLine': '{old}: nothing charged.',
  'co.rp.samePrice': 'Same seat, same price: {price}. Nobody else’s price changes.',
  'co.rp.sameCovered': 'The seat is already paid for — nothing to pay. Nobody else’s price changes.',
  'co.rp.recent': '{name} was added under 10 minutes ago — they probably never knew.',
  'co.rp.do': 'Replace',
  'co.rp.done': 'Done: {new} instead of {old}.',
  'co.rp.doneCredits': ' {old}: {n} {credits} returned.',
  'co.rs.title': 'Move the session: {name}',
  'co.rs.hint': 'The session has started and {name} already has {items}. Pick the right client: all of it moves to them.',
  'co.rs.confirm': 'Move the {when} session from {old} to {new}: {items}',
  'co.rs.confirmPlain': 'Move the {when} session from {old} to {new}?',
  'co.rs.hintSolo': 'Pick the right client: the whole booking moves to them — {items}.',
  'co.rs.hintPlain': 'Pick the right client: the booking moves to them.',
  'co.rs.ratings': 'ratings',
  'co.rs.notes': 'notes',
  'co.rs.payment': 'payment {amount}',
  'co.rs.do': 'Move',
  'co.rs.done': 'Done: the {when} session is now {new}’s.',
  'co.rs.charged': ' {n} {credits} taken from {new}.',
  'co.add.credits': '{n} credits',
  'co.countdown.min': 'in {n} min',
  'co.countdown.hMin': 'in {h} h {m}',
  'co.countdown.h': 'in {h} h',
  'co.rate.useCredits': 'Use their credits',
  'co.rate.cash': 'Cash',
  'co.rate.alreadyPaid': 'Already paid',
  'co.rate.added': '{name} added. You can rate them now.',
  'co.rate.addedClient': '{name} added. They get a confirmation and a reminder.',
  'co.rate.noCredits': '{name} has no credits — add them as cash instead.',
  'co.rate.alreadyIn': 'They are already in this session.',
  'co.rate.couldNotAdd': 'Could not add: {message}',
  'co.rate.drills': 'Bandeja to the back glass, 3 × 20',
  'co.rate.sharedNote': 'Calmer at the net today.',
  'co.rate.homework': '50 bandejas against the wall',
  'co.rate.workingOn': 'What they should work on next',
  'co.rate.title': 'How did {name} do?',
  'co.rate.dragHint': 'Drag each line. Left end (0) is not trained today.',
  'co.review.dragHint': 'Drag each line. Left end (0) is not shown in this match.',
  'co.rate.ratedCount': '{n} of {total} rated',
  'co.rate.noAxes': 'No rating axes set up yet.',
  'co.rate.aboutLabel': 'What it was about',
  'co.rate.aboutHint': 'The skills you rated above 0. Add anything else here.',
  'co.rate.other': '+ other',
  'co.set.titleAxes': 'Skills you rate',
  'co.axis.none': 'No skills yet.',
  // Sports (sql/046): padel, and tennis for a coach who teaches it too.
  'co.sport.padel': 'Padel',
  'co.sport.tennis': 'Tennis',
  'co.sport.both': 'Both',
  'co.cd.sportHint': 'What they are offered to book, and how their progress reads.',
  'co.cd.sportUnset': 'Not set yet: their first lesson will decide.',
  'co.set.linkPadel': 'Client link · Padel',
  'co.set.linkTennis': 'Client link · Tennis',
  'co.set.sport': 'Sport',
  'co.set.sportLocked': 'This type already has lessons, so its sport stays as it is.',
  'co.set.tennisOn': 'I teach tennis too',
  'co.set.tennisHint': 'Adds eight tennis skills to rate lessons on (Serve, Return, Forehand…), and a sport for each lesson type.',
  'co.set.tennisBusy': 'Take your tennis lesson types off sale first.',
  'co.axis.noRu': 'no Russian name yet',
  'co.axis.guideAll': 'guide written',
  'co.axis.guideSome': 'guide {n}/{total}',
  'co.axis.newSkill': '+ New skill',
  'co.axis.new': 'New skill',
  'co.axis.newPlaceholder': 'Serve & return, Net play…',
  'co.axis.add': 'Add',
  'co.axis.edit': 'Edit skill',
  'co.axis.nameEn': 'Name in English',
  'co.axis.nameRu': 'Name in Russian',
  'co.axis.nameRuPlaceholder': 'What you would call it in Russian',
  'co.axis.suggest': 'Suggest',
  'co.axis.thinking': 'Thinking…',
  'co.axis.guides': 'What each band means',
  'co.axis.draft': 'Draft with AI',
  'co.axis.drafting': 'Drafting…',
  'co.axis.linePlaceholderEn': 'One line, in English',
  'co.axis.linePlaceholderRu': 'The same line, in Russian',
  'co.axis.save': 'Save',
  'co.axis.saving': 'Saving…',
  'co.axis.cancel': 'Cancel',
  'co.axis.exists': 'You already have a skill with that name.',
  'co.axis.failed': 'Could not save that skill.',
  'co.axis.aiOff': 'AI is not set up on this server.',
  'co.axis.aiFailed': 'That did not come back. Try again.',
  'co.rate.w0': 'Not trained today',
  'co.review.w0': 'Not shown in this match',
  'co.rate.w1': 'Low beginner',
  'co.rate.w3': 'High beginner',
  'co.rate.w5': 'Low intermediate',
  'co.rate.w7': 'High intermediate',
  'co.rate.w9': 'Advanced',
  'co.rate.drillsLabel': 'What you did, one drill per line',
  'co.rate.workingOnLabel': 'Working on',
  'co.rate.clientSees': '{name} sees this. Clear it when it is done.',
  'co.rate.later': 'Later',
  'co.gn.loading': 'Session notes',
  'co.gn.title': 'Session {time}',
  'co.gn.saving': 'Saving…',
  'co.gn.saved': 'Saved · {time}',
  'co.gn.forEveryone': 'For everyone',
  'co.gn.homeworkAll': 'Homework · for everyone',
  'co.gn.commonPh': 'What applies to everyone — or one quick note to split with AI',
  'co.gn.ai': '✨ Split with AI',
  'co.gn.aiWorking': 'Splitting…',
  'co.gn.aiHint': 'Splits “For everyone on the session” into a shared part and a part for each player — you check before saving.',
  'co.gn.aiDone': 'Split with AI — check the parts below.',
  'co.gn.undo': 'Undo',
  'co.gn.aiOptedOut': 'AI unavailable: AI is off for the notes of {names}. Split it by hand.',
  'co.gn.aiNotInBot': 'AI unavailable: {names} never started the bot (a client you added), so their notes don’t go to AI. Split it by hand.',
  'co.gn.ratingsOf': 'Ratings · {name}',
  'co.gn.fromCoachFor': 'From the coach · {name}',
  'co.gn.notePh': 'Only what is about this player',
  'co.gn.personalTask': 'Personal task',
  'co.gn.addPersonalTask': '+ personal task',
  'co.gn.finish': 'Finish →',
  'co.gn.previewTitle': 'What the players will see',
  'co.gn.back': '← Back',
  'co.gn.confirm': 'Confirm ✅',
  'co.gn.stillOn': 'Session still on — players will see it at {time}',
  'co.gn.willGet': '{name} will get:',
  'co.gn.fromCoach': 'From the coach',
  'co.gn.noBot': 'Not in the bot: no message, they see it in the app.',
  'co.ss.did': 'What you did',
  'co.ss.common': 'Note for everyone',
  'co.ss.commonPh': 'Every player will see this',
  'co.pn.common': 'Both',
  'co.pn.commonPh': 'What you did together — both will see it',
  'co.pn.personal': 'Just for {name}',
  'co.pn.personalPh': 'Only {name} will see this',
  'co.pn.homework': 'Homework',
  'co.pn.plan': 'Session plan and topic',
  'co.pn.goal': '+ Working on next',
  'co.pn.preview': 'What the players will see',
  'co.pn.save': 'Save',
  'co.pn.saving': 'Saving…',
  'co.pn.ai': '✨ Split with AI',
  'co.pn.aiHint': 'Optional: write one note under “Both” and AI splits it into the shared part and a note for each.',
  'co.pn.failedPlayer': 'Nothing was saved: {name}’s part failed. Everything you wrote is still here — try again.',
  'co.pn.failed': 'Nothing was saved ({message}). Everything you wrote is still here — try again.',
  'co.today.notesWillShow': 'Notes saved · players see them at {time}',
  'co.rate.saving': 'Saving…',
  'co.rate.save': 'Save',
  'co.rate.draftSaving': 'Saving draft…',
  'co.rate.draftSaved': 'Draft saved · {name} sees nothing yet',
  'co.rate.duringLesson': 'Lesson still on: after Save, {name} sees it at {time}',
  'co.rate.willShow': 'Saved · {name} sees it at {time}',
  'co.today.continueRate': 'Continue rating',
  'co.today.editRate': 'Edit rating',
  'co.today.rated': 'Rated ✓',
  'co.today.noteOnly': 'Note ✓',
  'co.today.chipNote': 'note',
  'co.today.chipHw': 'homework',
  'co.today.rateDraft': 'Rating draft · not saved yet',
  'co.today.rateWillShow': 'Rating saved · the player sees it at {time}',
  'co.rate.couldNotSave': 'Could not save: {message}',
  'co.review.watch': 'Watch the match',
  'co.review.title': '{name} · {result}{score}',
  'co.review.versus': 'v {name}',
  'co.review.toReview': '{n} {matches} to review',
  'co.review.matches': ['match', 'matches'],
  'co.review.paid': ' paid',
  'co.review.open': 'Review',
  'co.review.worked': 'What worked',
  'co.review.toWorkOn': 'What to work on',
  'co.review.saveReview': 'Save review',
  'co.review.won': 'Won',
  'co.review.lost': 'Lost',
  'co.review.saveChanges': 'Save changes',
  'co.match.title': 'Matches',
  'co.match.none': 'No matches logged yet. When one is, it shows up here.',
  'co.match.reviewed': 'Reviewed {day}',
  'co.match.requested': 'Review requested',
  'co.match.notReviewed': 'Not reviewed',
  'co.failed': 'Failed: {message}',
  'bill.title': 'Subscription',
  'bill.until': 'Subscription until {date}',
  'bill.noDate': 'No date set',
  'bill.active': 'Paid',
  'bill.grace': 'Payment due · {n} days left',
  'bill.overdue': 'Overdue · {n} days',
  'bill.off': 'Closed',
  'bill.unbilled': 'Not billed',
  'bill.noPlan': 'No plan',
  'bill.planMonthly': 'Monthly · {price}',
  'bill.planYearly': 'Yearly · {price}',
  'bill.planFounding': 'Founding · {price} a month',
  'bill.howToPay': 'How to pay',
  'bill.noDetails': 'Payment details are not set yet. Message the owner.',
  'bill.afterPay': 'Once you pay, {owner} marks it and the date moves on.',
  'bill.overdueTitle': 'Subscription {n} {days} overdue',
  'bill.overdueBody': 'Pay {amount} to carry on. Your clients don’t see this and book as usual.',
  'bill.offTitle': 'Access to PadelDesk is closed',
  'bill.offBody': 'Your clients can’t make new bookings; sessions already booked stay. Pay the subscription and access opens again once {owner} marks it.',
  'bill.offAction': 'Your account is switched off: you can look, but not change anything.',
  'bill.you': '(you)',
  'bill.ownerRow': 'Owner · not billed',
  'bill.detailsTitle': 'Payment details in reminders',
  'bill.detailsPlaceholder': 'Revolut, IBAN, reference…',
  'bill.detailsHint': 'Quoted as written in the bot reminders and under “How to pay”.',
  'bill.since': 'Coach since {date}',
  'bill.plan': 'Plan',
  'bill.row': "Plan",
  'bill.lineTrial': "Trial until {date} · then {price}",
  'bill.linePaid': "{plan} · {price} · paid until {date}",
  'bill.lineOverdue': "Overdue since {date}",
  // The three states, as every billing screen says them.
  'bill.tTrial': "Trial until {date}",
  'bill.tPaid': "Paid until {date}",
  'bill.tOverdue': "Overdue since {date}",
  'bill.subTrial': "First payment {date} · {price}",
  'bill.subNext': "Next payment {date} · {price}",
  'bill.trialChip': "Trial",
  'bill.perMonthP': "{amount}/month",
  'bill.perYearP': "{amount}/year",
  'bill.nameMonthly': "Monthly",
  'bill.nameYearly': "Yearly",
  'bill.nameFounding': "Founding",
  'bill.sPlan': "Plan",
  'bill.sPrice': "Price",
  'bill.sNext': "Next payment",
  'bill.sTrialUntil': "Trial until",
  'bill.sWaitingSince': "Overdue since",
  'bill.sDetails': "Payment details",
  'bill.iPaid': "I've paid",
  'bill.thanks': "Thanks, we'll check and confirm",
  'bill.close': "Close",
  'bill.bTrial': "Trial until {date}",
  'bill.bTrialBody': "First payment {date} · {price}. Tap to see how to pay.",
  'bill.bPaid': "Paid until {date}",
  'bill.bPaidBody': "Next payment {date} · {price}. Tap to see how to pay.",
  'bill.bOverdue': "Overdue since {date}",
  'bill.bOverdueBody': "Your clients don't see this. Tap to see how to pay.",
  'bill.bOverdueCloses': "Access closes in {n} {days} without payment. Tap to see how to pay.",
  'bill.bWaiting': "Awaiting confirmation",
  'bill.monthly': 'Monthly',
  'bill.yearly': 'Yearly',
  'bill.founding': 'Founding',
  'bill.perMo': '{price}/mo',
  'bill.monthlyPrice': 'Monthly price',
  'bill.yearNote': 'A year is ten months: {year}.',
  'bill.paidUntil': 'Paid until',
  'bill.graceLine': 'Payment due · {late} days past the date, {left} days left',
  'bill.overdueLine': 'Overdue · {n} days · the coach sees a red banner',
  'bill.setDate': 'Change date',
  'bill.markPaid': 'Mark paid',
  'bill.plusMonth': '+1 month · {amount}',
  'bill.plusYear': '+1 year · {amount}',
  'bill.becomes': 'One payment moves it to {date} (from the paid-until date, not from today).',
  'bill.confirmPaid': 'Mark {amount} from {name} as received? Paid until becomes {date}, and {name} gets a message in the bot.',
  'bill.confirmOff': 'Close access for {name}? They won’t be able to do anything, and their clients can’t make new bookings. Booked sessions and their reminders stay.',
  'bill.confirmOn': 'Open access for {name} again?',
  'bill.history': 'Payment history',
  'bill.noPayments': 'No payments marked yet.',
  'bill.to': 'to {date}',
  'book.closedTitle': 'Booking is closed for now',
  'book.closedBody': 'Message your coach to book. Your sessions are kept.',
  'bill.days': ['day', 'days'],

  // ---------- coach: the player profile ----------
  'co.pp.sideRight': 'Revés · right side',
  'co.pp.sideLeft': 'Drive · left side',
  'co.pp.sideBoth': 'Both sides',
  'co.pp.handRight': 'Right-handed',
  'co.pp.handLeft': 'Left-handed',
  'co.pp.lvl.beginner': 'Beginner',
  'co.pp.lvl.improver': 'Improver',
  'co.pp.lvl.intermediate': 'Intermediate',
  'co.pp.lvl.intermediatePlus': 'Intermediate+',
  'co.pp.lvl.advanced': 'Advanced',
  'co.pp.lvl.competition': 'Competition',
  'co.pp.lvl.pro': 'Pro',
  'co.pp.edit': 'Edit',
  'co.pp.addSideHand': '+ Side and hand',
  'co.pp.playtomicRange': 'Playtomic levels go from 0 to 7.',
  'co.pp.right': 'Right',
  'co.pp.reves': 'Revés',
  'co.pp.left': 'Left',
  'co.pp.drive': 'Drive',
  'co.pp.both': 'Both',
  'co.pp.either': 'Either',
  'co.pp.level': 'Level',
  'co.pp.levelSetByYou': 'Level · set by you',
  'co.pp.side': 'Side',
  'co.pp.hand': 'Hand',
  'co.pp.basedOn': 'Based on {list}',
  'co.pp.goals': ['goal', 'goals'],
  'co.pp.plans': ['session plan', 'session plans'],
  'co.pp.requests': ['request from the player', 'requests from the player'],
  'co.pp.ratingsOn': 'ratings on {n} {skills}',
  'co.pp.skillsDat': ['skill', 'skills'],
  'co.pp.notes': ['note', 'notes'],
  'co.pp.setLevel': 'Set the level',
  'co.pp.levelHint': '0 to 7, the scale padel players know from Playtomic.',
  'co.pp.playtomicSays': ' Playtomic says {n}.',
  'co.pp.levelWhy': 'Why (optional, only you see this)',
  'co.pp.levelWhyPlaceholder': 'Bandeja finally lands deep',
  'co.pp.levelKept': 'Every change is kept, so you can see how the level moves over time.',
  'co.pp.saveLevel': 'Save level',
  'co.pp.levelFromTo': 'Level from {from} to {to}',
  'co.pp.filterAll': 'All',
  'co.pp.filterGroup': 'Group',
  'co.pp.filterNotes': 'With notes',
  'co.pp.groupOf': 'Group of {n}',
  'co.pp.whereTime': 'Where the time went',
  'co.pp.focusHint': 'Tag what each session was about when you rate it, and this shows where the time goes.',
  'co.pp.nothingYet': 'Nothing here yet.',
  'co.pp.sessions': 'Sessions',
  'co.pp.onCourt': 'On court',
  'co.pp.turnedUp': 'Turned up',
  'co.tbl.day': 'Day',
  'co.tbl.time': 'Time',
  'co.tbl.session': 'Session',
  'co.tbl.rating': 'Rating',
  'co.tbl.payment': 'Payment',
  'co.tbl.rated': 'Rated',
  'co.tbl.rate': 'Rate',
  'co.tbl.missed': 'Missed',
  'co.tbl.came': 'Came to {came} of {total}',
  'co.tbl.missedN': 'missed {n}',
  'co.pp.playersSee': 'Players see this',
  'co.pp.onlyYouForNow': '🔒 Only you for now',
  'co.pp.drillsPlaceholder': 'Bandeja to the back glass, 3 × 20\n2 v 1 at the net from 4-4',
  'co.pp.sharedPlaceholder': 'Calmer at the net today. Finish the bandeja higher.',
  'co.pp.homeworkPlaceholder': '50 bandejas against the wall',
  'co.pp.enter': 'Enter',
  'co.pp.aiNotSetUp': 'AI is not set up on the server yet.',
  'co.pp.aiNothing': 'There are no notes or ratings for this client yet.',
  'co.pp.aiRefused': 'The AI declined to summarise these notes.',
  'co.pp.aiFailed': 'The summary could not be made just now. Try again in a minute.',
  'co.pp.aiTitle': '✦ AI summary of your notes',
  'co.pp.aiBlurb': 'Reads everything you have written about this client, plus their ratings, and tells you what keeps coming up.',
  'co.pp.aiNotYet': 'Once you have written a few session notes or ratings, this can read them for you.',
  'co.pp.aiReading': 'Reading your notes…',
  'co.pp.aiRead': 'Read my notes',
  'co.pp.hideSources': 'Hide sources',
  'co.pp.showSources': 'Show sources',
  'co.pp.saving': 'Saving…',
  'co.pp.cancel': 'Cancel',
  'co.pp.couldNotSave': 'Could not save: {message}',

  // ---------- coach: Setup ----------
  // The Settings menu: group headings, row titles and the summaries under them.
  'co.set.menuMe': 'You',
  'co.set.menuWork': 'Work',
  'co.set.menuClients': 'Clients',
  'co.set.menuApp': 'App',
  'co.set.rowProfile': 'Profile',
  'co.set.rowLink': 'Your client link',
  'co.set.rowPrices': 'Prices and packages',
  'co.set.rowCourt': 'Court',
  'co.set.rowClients': 'New clients and tags',
  'co.set.rowAi': 'AI and diary',
  'co.set.rowCalendar': 'My calendar',
  'co.set.sumHoursNone': 'No hours set',
  'co.set.sumLinkNone': 'Not sent to anyone yet',
  'co.set.sumLinkSent': 'Sent to your clients',
  'co.set.sumCourtNone': 'Court price not set',
  'co.set.sumAiNone': 'No skills chosen',
  'co.set.sumCalendar': 'Apple · Google',
  'co.set.sumRules': 'Cancel {cutoff}h before · booking {days} days ahead',
  'co.set.sumPeak': 'peak {price}',
  'co.set.noPrice': 'No price',
  'co.set.minutesN': '{n} min',
  'co.set.creditsN': '{n} {sessions}',
  'co.set.sessionsW': ['session', 'sessions'],
  'co.set.daysN': '{n} {days}',
  'co.set.tagsN': '{n} {tags}',
  'co.set.tagsW': ['tag', 'tags'],
  'co.set.axesN': '{n} {axes}',
  'co.set.axesW': ['skill', 'skills'],
  'co.set.perHourN': '€{n}/hour',
  'co.set.hBeforeN': '{n}h before',
  'co.set.pkgsN': '{n} {pkgs}',
  'co.set.pkgsW': ['package', 'packages'],
  'co.set.approveManual': 'I approve each one',
  'co.set.approveAuto': 'They book straight away',
  'co.set.aiLead': 'What you rate after a session, the drills you keep, and the evening brief.',
  'co.set.pricesLead': 'What a client sees when booking, and what you sell them.',
  // Working hours, a row per day.
  'co.set.hoursLead': 'When you can be booked. Tap an interval to change it, «+» for a second one that day.',
  'co.set.dayOff': 'day off',
  'co.set.addInterval': 'Add an interval',
  'co.set.interval': 'Interval',
  'co.set.intervalHint': 'Clients see slots inside this, as long as nothing else is booked.',
  'co.set.from': 'From',
  'co.set.until': 'Until',
  'co.set.copyMon': 'Mon–Fri the same as Monday',
  'co.set.copyMonConfirm': 'Give Tuesday to Friday the same hours as Monday?',
  'co.set.copyMonEmpty': 'Set Monday first.',
  'co.set.newSession': 'New session',
  'co.set.addSession': '+ Add a session',
  'co.set.newPackage': 'New package',
  'co.set.addPackage': '+ Add a package',
  // The first-run checklist.
  'co.set.chkLeft': '{n} {steps} left',
  'co.set.chkOf': '{done} of {total}',
  'co.set.stepsW': ['step', 'steps'],
  'co.set.chkHours': 'Working hours',
  'co.set.chkPrice': 'Session price',
  'co.set.chkCourt': 'Court cost',
  'co.set.chkAddress': 'Club address',
  'co.set.chkLink': 'Send your link to a client',
  'co.set.titleLanguage': 'Language',
  'co.set.titleInfo': 'Info for clients',
  'co.info.hint': 'Your clients see this on My profile, and get it in the bot when you approve them.',
  'co.info.empty': 'Not filled in yet — clients don’t see “Info”, and no card is sent when you approve them. Fill in at least the address or “How to find us”.',
  'co.info.address': 'Address',
  'co.info.addressPh': 'e.g. Padel Club, … St',
  'co.info.maps': 'Google Maps link',
  'co.info.mapsPh': 'https://maps.app.goo.gl/…',
  'co.info.directions': 'How to find us',
  'co.info.directionsPh': 'Entrance, court number, who to ask for',
  'co.info.notes': 'Anything else (what to bring, parking…)',
  'co.info.notesPh': 'Racket, water, shoes, parking',
  'co.info.auto': 'Filled in automatically',
  'co.info.autoFrom': 'from sessions and rules',
  'co.info.preview': 'Preview as client',
  'co.info.hidePreview': 'Hide preview',
  'co.info.askSend': 'Send Info to all clients? {n} will get it.',
  'co.info.sent': 'Sending to {n} {clients}, a few at a time.',
  'co.info.saved': 'Saved. Clients see it on My profile.',
  'co.info.savedEmpty': 'Saved. Clients will see “Info” once the address or “How to find us” is filled in.',
  'co.info.badUrl': 'The map link doesn’t look like a link (https://…), so it wasn’t saved.',
  'co.info.sendAll': 'Send the updated Info to everyone →',
  'co.info.reach': '{n} {clients} in the bot will get it.',
  'co.info.notInBot': ' {n} more never started the bot.',
  'co.info.history': 'Sent',
  'co.info.statusFilled': 'Filled in',
  'co.info.statusEmpty': 'Not filled in',
  'co.info.statusSent': 'sent {when}',
  'co.info.statusNotSent': 'not sent yet',
  'co.set.tagsCount': 'Tags · {n}',
  'co.set.axesCount': 'Skills · {n}',
  'co.set.tagsStatus': 'Groups for your clients',
  'co.set.axesStatus': 'What you rate after a session',
  'co.set.titleSetup': 'Clients and sessions',
  'co.set.done': 'Done',
  'co.info.historyInfo': 'Info',
  'co.info.clientsW': ['client', 'clients'],
  'info.title': 'Info · {coach}',
  'info.close': 'Hide',
  'info.openMap': 'Open map',
  'info.prices': 'Price',
  'info.hour': '1 hour',
  'info.where': 'Where we train',
  'info.find': 'How to find us',
  'info.openMaps': 'Open in Google Maps',
  'info.always': 'always current',
  'info.cancel': 'Cancelling a session',
  'info.cancelFree': 'Free — up to {h} {hours} before.',
  'info.cancelLate': 'Later — the session is charged in full.',
  'info.hoursW': ['hour', 'hours'],
  'info.goodToKnow': 'Good to know',
  'co.set.titleTags': 'Tags',
  'co.set.appLanguage': 'App language',
  'co.set.languageHint': 'Changes this app and the messages the bot sends you.',
  'co.set.titlePrices': 'Session prices',
  'co.set.titlePackages': 'Packages',
  'co.set.titleRules': 'Booking rules',
  'co.set.titleCourts': 'Courts',
  'co.set.titleHours': 'Working hours',
  'co.set.titlePeak': 'Peak court rates',
  'co.set.titleCalendar': 'Your calendar',
  'co.set.subscribe': 'Subscribe in my calendar',
  'co.set.subscribeHint': 'Your calendar updates itself: new bookings appear, and past sessions and no-shows stay for two years. Cancellations are removed, late ones included.',
  'co.set.sendToCalendar': 'Send my sessions to my calendar',
  'co.set.sendToCalendarHint': 'Every upcoming session, with who is in it and any cash to collect. Run it again whenever you want to refresh.',
  'co.set.setCourts': 'Set the courts you have',
  'co.set.onlyWithCourt': 'Only sell hours I have a court for',
  'co.set.onlyWithCourtHint': 'Leave this off until you have filled a few weeks in, or clients will see nothing available.',
  'co.set.priceNote': 'Changing a price only affects new bookings. Anyone holding a package keeps the rate they paid.',
  'co.set.hoursUpdated': 'Hours updated',
  'co.set.everyDay': 'Every day',
  'co.set.dayOnly': '{day} only',
  'co.set.courtCosts': 'Court costs',
  'co.set.perHour': '€ / hour',
  'co.set.cancel': 'Cancel',
  'bill.overdueCloses': 'Pay {amount} to carry on. Otherwise access closes in {n} {days} and your clients won’t be able to book.',
  'biz.title': 'Business',
  'biz.never': 'never',
  'biz.today': 'today, {time}',
  'biz.yesterday': 'yesterday',
  'biz.daysAgo': '{n} {days} ago',
  'biz.income': 'Income from coach plans',
  'biz.incomeSub': 'a month · yearly plans ÷12',
  'biz.paid': 'Paid',
  'biz.grace': 'Payment due',
  'biz.overdue': 'Overdue',
  'biz.closed': 'Closed',
  'biz.clientsAll': 'Clients across all coaches',
  'biz.activeAll': 'active in the last 30 days: {n}',
  'biz.coaches': 'Coaches',
  'biz.urgentFirst': 'needing attention first',
  'biz.closesIn': 'closes in {n} {days}',
  'biz.autoClosed': 'closed automatically',
  'biz.members': 'club members',
  'biz.lastSeen': 'last opened: {when}',
  'biz.membersSoon': 'Club members: once memberships exist',
  'biz.closedAuto': 'Access closed automatically on {date}: 14 days overdue.',
  'biz.closedOwner': 'Access closed by you on {date}.',
  'biz.open': 'Open access',
  'biz.openNote': 'The coach can work again and their clients can book. «+1 month» below opens access too.',
  'biz.closesNotice': 'Access closes automatically in {n} {days} if no payment arrives.',
  'biz.close': 'Close access',
  'biz.closeNote': 'The coach can’t do anything and their clients can’t book (“Booking is closed for now”). Sessions already booked stay.',
  'biz.upcomingTitle': 'Booked sessions',
  'biz.upcomingNone': 'No upcoming sessions.',
  'biz.upcomingNote': 'Read only. These sessions stay booked, and your clients still get their reminders.',
  'biz.clientsW': ['client', 'clients'],
  'biz.activeW': ['active', 'active'],
  'biz.sessionsW': ['session this month', 'sessions this month'],
  'co.hot.openTimes': 'Open times',
  'co.hot.hint': 'Tap a time to mark it 🔥',
  'co.hot.hintBlock': 'Tap a time to block it. Tap it again to open it back up.',
  'co.hot.modeHot': '🔥 Hot',
  'co.hot.modeBlock': '🔒 Block',
  'co.hot.blocked': 'blocked',
  'co.hot.title': '🔥 Hot time',
  'co.hot.markQ': 'Mark {time} as hot',
  'co.hot.isMarked': '{time} is marked 🔥',
  'co.hot.what': 'Players see 🔥 on this time on Book. Same price, nobody gets a message.',
  'co.hot.whatOff': 'Players will see it as a normal time again.',
  'co.hot.mark': 'Mark 🔥',
  'co.hot.unmark': 'Remove 🔥',
  'co.hot.notOpen': 'This time is no longer open.',
  'co.set.addRate': 'Add rate',
  'co.set.addPeakShort': '+ Add',
  'co.set.bandTitle': 'Peak rate',
  'co.set.remove': 'Remove',
  'co.set.removeRate': 'Remove this rate?',
  'co.set.endAfterStart': 'The end time has to be after the start.',
  'co.set.splitNote': 'A session crossing a boundary is split — 09:30 to 10:30 with peak ending at 10:00 costs half an hour at each rate.',
  'co.set.saved': 'Saved.',
  'co.set.hoursCopied': '{n} hours copied.',
  'co.set.close': 'Close',
  'co.set.addBlock': 'Add a block',
  'co.set.saveHours': 'Save hours',
  'co.set.couldNotSave': 'Could not save: {message}',
  'co.set.lastWeekEmpty': 'Last week was empty.',
  'co.set.couldNotCopy': 'Could not copy: {message}',
  'co.set.copyLastWeek': 'Copy last week',
  'co.set.saving': 'Saving…',
  'co.set.saveDays': 'Save {n} {days}',
  'co.set.savedShort': 'Saved',
  'co.set.confirmHours': 'Save these hours? Sessions already booked are not affected.',
  'co.set.pricePerSession': 'Price per session',
  'co.set.length': 'Length',
  'co.set.name': 'Name',
  'co.set.sessions': 'Sessions',
  'co.set.price': 'Price',
  'co.set.validFor': 'Valid for',
  'co.set.sold': ' · {n} sold',
  'co.set.minNotice': 'Minimum notice',
  'co.set.minNoticeHint': 'The latest a client can grab a slot.',
  'co.set.window': 'Booking window',
  'co.set.windowHint': 'How far ahead your calendar is open.',
  'co.set.freeCancel': 'Free cancellation',
  'co.set.freeCancelHint': 'Later than this, the session is used.',
  'co.set.lastCall': 'Last call',
  'co.set.lastCallHint': 'Warns them the free window is closing.',
  'co.set.groupReminders': 'Reminders',
  'co.set.remCutoff': 'Morning lessons — starting before',
  'co.set.remCutoffHint': 'Decides which day the reminder comes.',
  'co.set.remEvening': 'The evening before, at',
  'co.set.remEveningHint': 'For lessons before {time}.',
  'co.set.remDay': 'On the day, at',
  'co.set.remDayHint': 'For lessons from {time}.',
  'co.set.quietHours': 'From 21:00 to 09:00 clients get nothing, except the confirmation of a booking they have just made. A message due then comes at 09:00, or earlier if the lesson is before that.',
  'co.set.remInQuiet': 'Reminder times must be between 09:00 and 20:59.',
  'co.set.remDayAfterCutoff': 'The on-the-day reminder must come before {time}.',
  'co.set.courtHire': 'Court hire',
  'co.set.courtHireHint': 'Your usual rate. Peak hours are set below.',
  'co.set.canInvite': 'Clients can bring a friend',
  'co.set.canInviteHint': 'A 1:1 can become a pair. The price is split, not doubled.',
  'co.set.reviews': 'Watch and review matches',
  'co.set.reviewsHint': 'Clients send a video and you write up what to fix.',
  'co.set.reviewPrice': 'Review price',
  'co.set.each': '€ each',
  'co.set.save': 'Save',
  'co.set.minutes': 'min',
  'co.set.days': 'days',
  'co.set.hours': 'hours',
  'co.set.hBefore': 'h before',
  'co.set.perSessionPrice': '€{amount} per session',
  'co.set.neverExpires': ' · never expires',
  'co.set.groupBooking': 'Booking',
  'co.set.groupCancellations': 'Cancellations',
  'co.set.groupCourt': 'Court',
  'co.set.groupExtras': 'Extras',
  'co.set.freeToCancel': 'Free to cancel',
  'co.set.lastHours': 'Last {n}h · uses the session',
  'co.set.windowStory': 'Last call {warn}h before · session',
  'co.set.hoursNote': 'Removing a block hides that time from new bookings. It does not cancel anything already booked.',
  'co.set.lastCallTooLate': 'The last call has to come before the deadline it warns about. Make it more than {n} hours before.',
  'co.day.sunday': 'Sunday',
  'co.day.monday': 'Monday',
  'co.day.tuesday': 'Tuesday',
  'co.day.wednesday': 'Wednesday',
  'co.day.thursday': 'Thursday',
  'co.day.friday': 'Friday',
  'co.day.saturday': 'Saturday',

  // ---------- coach: how it was paid (sql/040) ----------
  'pm.label': 'Paid by',
  'pm.cash': 'Cash',
  'pm.revolut': 'Revolut',
  'pm.revolutHint': 'and card',
  'pm.bank': 'Bank',
  'pm.bankHint': 'transfer',
  'pm.none': 'Not specified',
  'pm.noneHint': 'recorded before payment methods existed',
  'pm.breakdown': 'How it came in',
  'pm.payments': '{n} {payments}',
  'pm.paymentsW': ['payment', 'payments'],
  'pm.with': '{action} · {method}',

  // ---------- coach: Money ----------
  'co.money.day': 'Day',
  'co.money.week': 'Week',
  'co.money.month': 'Month',
  'co.money.year': 'Year',
  'co.money.today': 'Today',
  'co.money.thisWeek': 'This week',
  'co.money.thisMonth': 'This month',
  'co.money.thisYear': 'This year',
  'co.money.yesterday': 'yesterday',
  'co.money.lastWeek': 'last week',
  'co.money.lastMonth': 'last month',
  'co.money.lastYear': 'last year',
  'co.money.all': 'All',
  'co.money.sinceStart': 'Since {date}',
  'co.money.lineNet': 'After court hire',
  'co.money.lineEarned': 'Earned',
  'co.money.lineIn': 'Received',
  'co.money.prevKey.day': 'Yesterday',
  'co.money.prevKey.week': 'Last week',
  'co.money.prevKey.month': 'Last month',
  'co.money.prevKey.year': 'Last year',
  'co.money.vsPoint': '{change}% vs {date}',
  'co.money.vsYesterday': '{change}% vs {time} yesterday',
  'co.money.weekOf': 'Week of {date}',
  'co.money.afterCourt': ' · yours after court hire',
  'co.money.sessionsCount': '{n} {sessions}',
  'co.money.earnedMinusCourt': ' · €{earned} earned − €{court} courts',
  'co.money.vs': ' · vs {period}',
  'co.money.keyEarned': 'Earned: coaching delivered',
  'co.money.keyReceived': 'Received: money in',
  'co.money.toCollect': 'To collect',
  'co.money.unpaidSessions': '{n} unpaid {sessions}',
  'co.money.allPaidUp': 'All paid up',
  'co.money.paidInAdvance': 'Paid in advance',
  'co.money.stillToTeach': '{n} sessions still to teach',
  'co.money.allTime': 'All time',
  'co.money.calendar': 'Calendar',
  'co.money.close': 'Close',
  'co.money.nobodyOwes': 'Nobody owes you anything.',
  'co.money.nothingBooked': 'Nothing booked.',
  'co.money.projected': 'Projected — if everyone turns up and pays',
  'co.money.whatHappened': 'What happened',
  'co.money.expected': 'Expected',
  'co.money.earned': 'Earned',
  'co.money.wouldKeep': 'Would keep',
  'co.money.yours': 'Yours',
  'co.money.free': 'free',
  'co.money.paid': 'paid',
  'co.money.credit': 'credit',
  'co.money.alsoCovers': ' · also {names}',
  'co.money.doneOf': '{done} of {total} done · €{paid} paid',
  'co.money.bookedAhead': ' · {n} booked ahead',
  'co.money.unbooked': ' · {n} unbooked',
  'co.money.oldest': ' · oldest {n} {days} ago',
  'co.money.gotAmount': 'Got €{amount} · {day}',

  // ---------- coach: cancel sheet, calendar, time off, selling a package ----------
  'co.cs.note': "Add a note (they'll see this)",
  'co.cs.cancelling': 'Cancelling…',
  'co.cal.earned': 'Earned · {n} {sessions}',
  'co.cal.stillToCome': 'Still to come · {n} booked',
  'co.cal.courtHire': 'Court hire',
  'cb.title': 'Court bill',
  'cb.entrySub': 'Sessions on court for a week or a month',
  'cb.byWeek': 'Week', 'cb.byMonth': 'Month',
  'cb.week': 'Week {range}',
  'cb.none': 'No sessions on court in this period.',
  'cb.noShow': 'no-show',
  'cb.min': '{n} min', 'cb.hours': '{n} h',
  'cb.onCourt': 'Hours on court',
  'cb.toPay': 'Total to pay',
  'cb.send': 'Send to myself in Telegram',
  'cb.sent': 'Sent — it’s in your chat with the bot, ready to forward to the club.',
  'cb.carry': 'Balance at the start of the week',
  'cb.copy': 'Copy for spreadsheet',
  'cb.copyHint': 'Paste into Google Sheets — rows as in the club’s sheet',
  'cb.copied': 'Copied — paste it into the sheet.',
  'cb.file': 'Send the file to the chat',
  'cb.fileHint': '{name} — the bot sends it to you',
  'cb.fileSent': 'The bot will send {name} to your chat in a minute.',
  'cb.rateWarn': 'Rates here are not the club’s 20 / 30 € ({rates}). A is the real cost, B and C are hours — so A ≠ 30 × C + 20 × B.',
  'co.set.week1': 'Week 1 starts',
  'co.set.week1Hint': 'For «WEEK nn» in the court bill export. Empty — the ISO week number.',
  'co.set.week1Norm': 'Weeks run Monday to Sunday: counted from Monday {date}.',
  'co.set.week1Clear': 'Clear',
  'co.cal.yours': 'Yours',
  'co.cal.courtToCome': 'Court hire to come',
  'co.cal.ifAllTurnUp': 'If everyone turns up',
  'co.sell.title': 'Package for {name}',
  'co.sell.eachPrice': '{n} {sessions} · €{each} each',
  'co.sell.somethingElse': 'Something else',
  'co.sell.sessionsLabel': 'Sessions',
  'co.sell.theyPaid': 'They paid',
  'co.sell.received': 'Received, €',
  'co.sell.receivedHint': 'Empty: free credits, no money counted',
  'co.sell.receivedLine': 'Received: {amount} · {method}',
  'co.sell.aSession': '€{amount} a session',
  'co.sell.addSessions': 'Add sessions',
  'co.sell.howMany': 'How many sessions?',
  'co.sell.howMuch': 'How much did they pay?',
  'co.sell.confirmCustom': 'Give {n} sessions for €{paid}?\n\nThat is €{each} a session. Only do this once they have paid.',
  'co.sell.confirmPackage': 'Give {n} credits for €{price}?\n\nOnly do this once they have paid.',
  'co.sell.done': 'Done. They now have {n}.',
  'co.sell.failed': 'Could not do that: {message}',
  'co.sell.expiresIn': ' · expires in {n} {days}',
  'co.sell.noExpiry': ' · no expiry',
  'co.sell.backToPackages': 'Back to packages',
  'co.off.title': 'Block time off',
  'co.off.upcoming': 'Upcoming blocks',
  'co.off.from': 'From',
  'co.off.until': 'Until',
  'co.off.nothing': 'Nothing blocked. Your normal hours apply.',
  'co.off.defaultReason': 'Time off',
  'co.off.remove': 'Remove',
  'co.off.clashList': '{name} — {time}',
  'co.off.pickRange': 'Pick a start and an end.',
  'co.off.blockedWithClashes': 'Blocked, but {n} {bookings} already exist in that time.',
  'co.off.cancelThem': '\n\nCancel them from the Calendar tab if you cannot make it.',
  'co.off.blockFailed': 'Could not block that: {message}',
  'co.off.removeBlock': 'Remove this block and open the time back up?',
  'co.off.reason': 'Reason (optional)',
  'co.off.block': 'Block this time',

  // ---------- coach: sheets on a client's page ----------
  // Russian avoids the client's name in the middle of a sentence: it
  // would have to be declined ("Заблокировать Олега"), and the app has
  // no way to decline a name it was given.
  'co.cd.status.attended': 'Came',
  'co.cd.status.noShow': 'No-show',
  'co.cd.status.cancelled': 'Cancelled',
  'co.cd.status.waitlisted': 'Waitlist',
  'co.cd.creditsMoved': '\n\n{n} upcoming unpaid {bookings} updated. Anything already paid is untouched.',
  'co.cd.bookings': ['booking', 'bookings'],
  'co.cd.gifted': 'Gifted. The client gets a message saying it is on you.',
  'co.cd.bookedFor': 'Booked. They get a confirmation and reminders.',
  'co.cd.couldNotBook': 'Could not book: {message}',
  'co.cd.useCredits': 'Use credits',
  'co.cd.payPerSession': 'Pay per session',
  'co.cd.giftWhy': "Why? (optional — 'happy birthday', 'for the referral')",
  'co.cd.booking': 'Booking…',
  'co.cd.bookAt': 'Book {time}',
  'co.cd.pickTime': 'Pick a time',
  'co.cd.outside': 'outside working hours',
  'co.cd.outsideHint': 'Lighter times are outside your working hours — you can still book them.',
  'co.cd.outsideChosen': '{time} is outside your working hours.',
  'co.cd.once': 'Once',
  'co.cd.weekly': 'Every week',
  'co.cd.weeksN': 'Weeks: {n}',
  'co.cd.untilDate': 'until a date',
  'co.cd.takenSkip': 'taken — will skip',
  'co.cd.seriesPay': 'Each lesson is paid the same way: credits while they last, then cash.',
  'co.cd.bookN': 'Book {n} {sessions}',
  'co.cd.seriesDone': 'Booked: {n} {sessions}.',
  'co.cd.seriesSkipped': ' Skipped as taken: {n}.',
  'co.cd.toldThem': '\n\nThe client has been told.',
  'co.cd.timeTaken': 'That time is taken on every date. Try another.',
  'co.cd.couldNotMove': 'Could not move: {message}',
  'co.cd.stale': 'This lesson changed since the screen opened. Updated — try again.',
  'wk.day': 'Day',
  'wk.week': 'Week',
  'wk.title': 'Week {range}',
  'wk.toCollect': 'to collect {amount}',
  'wk.today': 'Today',
  'wk.prev': 'Previous week',
  'wk.next': 'Next week',
  'wk.group': 'Group\u00a0· {n}',
  'wk.hint': 'Tap a session to open it, an empty time to book. Hold a session to drag it to another time.',
  'wk.bookWho': 'Book {when}: who?',
  'co.cd.moveWholeSeries': 'Move the whole weekly series',
  'co.cd.wholeSeries': 'Every upcoming session in this series, not just this one',
  'co.cd.moving': 'Moving…',
  'co.cd.moveTo': 'Move to {time}',
  'co.cd.saved': 'Saved.',
  'co.cd.cleared': 'Cleared.',
  'co.cd.couldNotSave': 'Could not save: {message}',
  'co.cd.couldNot': 'Could not: {message}',
  'co.cd.couldNotDo': 'Could not do that: {message}',
  'co.cd.saving': 'Saving…',
  'co.cd.save': 'Save',
  'co.cd.payerTaken': 'That person is already paid for by someone else.',
  'co.cd.standardRate': 'They now pay the standard rate.',
  'co.cd.ratesTitle': 'Their prices',
  'co.cd.payingTitle': 'Paying',
  'co.cd.standard': 'standard €{amount}',
  'co.cd.useStandard': 'Use standard rate',
  'co.cd.savePrice': 'Save price',
  'co.cd.ratesNote': 'Leave blank for the standard rate. A price set here stays put when you raise your standard prices, and never changes a booking already made.',
  'co.cd.someoneElsePays': 'Someone else pays for this client',
  'co.cd.minutes': '{n} min',
  'co.cd.credits': '{n} credits',
  'co.cd.agoToday': 'today',
  'co.cd.agoYesterday': 'yesterday',
  'co.cd.agoDays': '{n} days ago',
  'co.cd.agoWeeks': '{n} weeks ago',
  'co.cd.agoMonths': '{n} months ago',
  'co.cd.ownRate': 'They pay €{price} for {minutes} minutes.',
  'co.cd.search': 'Search',
  'co.cd.cancel': 'Cancel',
  'co.cd.removePkgTitle': 'Remove this package from the client?\n\n',
  'co.cd.removePkgUsed': 'The {n} already used stay — those sessions happened.',
  'co.cd.removePkgMoney': '\n\nSort out any money between you separately.',
  'co.cd.switchTitle': 'Move their unpaid upcoming sessions onto their credits?\n\n',
  'co.cd.switchBody': 'The bookings stay exactly as they are — only how they are paid changes.',
  'co.cd.switchNone': 'No unpaid upcoming sessions to move.',
  'co.cd.workingOnSaved': 'Saved. The client sees this and gets a message.',
  'co.cd.blockTitle': 'Block this client?\n\nThey will not see your availability or be able to book. Upcoming sessions are cancelled and credits returned. Their history stays.',
  'co.cd.unblockTitle': 'Unblock this client? They can book again.',
  'co.cd.blockedDone': 'Blocked. {n} upcoming {sessions} cancelled.',
  'co.cd.topUpTitle': 'How much did they give you?',
  'co.cd.topUpAmount': 'Enter an amount.',
  'co.cd.topUpDone': '€{amount} at €{rate} a session is {n} {credits}. Their balance is now {balance}.',
  'co.cd.goalSince': ' · SINCE {day}',
  'co.cd.goalTitle': '🎯 WORKING ON',
  'co.cd.topUpPrompt': 'How much did they give you?',
  'co.cd.botLang': 'Message language',
  'co.cd.botLangDefault': 'Default',
  'co.cd.botLangRu': 'Русский',
  'co.cd.botLangEn': 'English',
  'co.cd.botLangHint': 'The language the bot writes to this client in. Default: as now — their own app language. Their app stays in the language they chose.',
  'co.cd.goalPlaceholder': 'Serve, ball control on the backhand, net play under pressure',
  'co.cd.goalSave': 'Save and tell the client',
  'co.cd.goalClear': 'Clear',
  'co.cd.goalMessage': '{name} gets a message when it changes.',
  'co.cd.goalDone': 'Done',
  'co.cd.goalSessions': '{n} {sessions} on it so far',
  'co.cd.goalChange': 'Change',
  'co.cd.goalFinished': 'Done with it',
  'co.cd.standingNotes': 'Standing notes. Private, only you see these.',
  'co.cd.notesPlaceholder': 'Injuries, goals, who referred them…',

  // ---------- coach: a client's page ----------
  'co.profile.back': '‹ Clients',
  'co.profile.backCalendar': '‹ Calendar',
  'co.profile.noUsername': 'no username',
  'co.profile.noMessages': 'Cannot be messaged',
  'co.profile.book': 'Book',
  'co.profile.addCredit': 'Credit',
  'co.profile.message': 'Message',
  'co.profile.tabSessions': 'Sessions',
  'co.profile.tabProgress': 'Progress',
  'co.profile.tabNotes': 'Journal',
  'co.profile.tabSettings': 'Settings',
  'co.start.title': 'Starting scores',
  'co.start.explain': 'The player’s starting point for measuring progress.',
  'co.start.setOn': 'Set on {date}',
  'co.start.set': 'Set',
  'co.profile.sessions': 'Sessions',
  'co.profile.turnedUp': 'Turned up',
  'co.profile.credits': 'Credits',
  'co.profile.since': 'With you since {day}',
  'co.profile.never': 'Has never trained',
  'co.profile.lastSession': ' · last session {ago}',
  'co.profile.upcoming': 'Upcoming',
  'co.profile.past': 'Past',
  'co.profile.bookFor': 'Book a session for {name}',
  'co.profile.cancelAllUpcoming': 'Cancel all {n} upcoming',
  'co.profile.nothingAhead': 'Nothing booked ahead.',
  'co.profile.noHistory': 'No history yet.',
  'co.profile.owed': '€{amount} owed for sessions already taken.',
  'co.profile.packages': 'Packages',
  'co.profile.pkgLeft': '{left} of {total} left',
  'co.profile.pkgExpires': 'Expires in {n} {days}',
  'co.profile.pkgExpired': 'Expired',
  'co.profile.pkgNoExpiry': 'No expiry',
  'co.profile.pkgBought': 'Bought {day} · paid €{amount}',
  'co.profile.pkgMore': 'More for this package',
  'co.profile.pkgRemove': 'Remove package',
  'co.profile.sellPackage': 'Sell a package',
  'co.profile.move': 'Move',
  'co.profile.cancel': 'Cancel',
  'co.profile.series': 'Part of a weekly series',
  'co.profile.rated': 'rated',
  'co.profile.recentSessions': 'Recent sessions',
  'co.profile.allSessions': 'All sessions',
  'co.profile.noSessions': 'No sessions yet.',
  'co.profile.notRated': 'not rated',
  'co.profile.paid': '€{amount} paid',
  'co.profile.owedOne': '€{amount} owed',
  'co.profile.plusCredits': ' + {n} cr.',
  'co.pp.crShort': 'cr.',
  'co.profile.unpaid': '€{amount} unpaid',
  'co.profile.creditsUsed': '{n} {credits}',
  'co.profile.settingsBooking': 'Booking',
  'co.profile.halfHour': 'Half-hour start times',
  'co.profile.halfHourHint': 'Lets them book at :30 as well as on the hour. Leave it off for most people — it strands gaps nothing else fits into.',
  'co.profile.payWithCredits': 'Pay upcoming sessions with credits',
  'co.profile.access': 'Access',
  'co.profile.block': 'Block this client',
  'co.profile.unblock': 'Unblock this client',
  'co.profile.blockHint': 'They stop seeing your availability. History is kept.',
  'co.profile.blocked': 'Blocked — they cannot see your schedule or book.',
  'co.profile.privateNote': 'Everything here is between you and this client. They are never told a setting changed.',
  'co.profile.editName': 'Edit name',
  'co.profile.nicknameHint': 'Clear it to go back to {name}. Only you ever see this.',
  'co.profile.save': 'Save',
  'co.profile.cancelEdit': 'Cancel',
  'co.profile.cancelTitle': 'Cancel this session',
  'co.profile.cancelBody': '{day} at {time}. Full refund, and they are told.',
  'co.profile.cancelAllTitle': 'Cancel all {n} sessions',
  'co.profile.cancelAllBody': 'Every upcoming session. All refunded, all told. This cannot be undone.',
  'co.profile.cancelAllConfirm': 'Cancel all {n}',
  'co.profile.cancelDone': '{n} cancelled, {r} {credits} returned.',

  // Progress
  'progress.title': 'Progress',
  'progress.focusNext': '{skill} is where to focus next.',
  'progress.noRatings': 'No ratings yet.',
  'progress.now': 'Now',
  'progress.lastMonth': 'Last month',
  'progress.matches': 'Matches',
  'progress.upTo': '{skill} up to {score}',

  // Matches
  'match.title': 'Matches',
  'match.log': '+ Log a match',
  'match.explain': 'Log your matches and your coach can see how training holds up under pressure.',
  'match.won': 'Won',
  'match.lost': 'Lost',
  'match.save': 'Save match',
  'match.saving': 'Saving…',
  'match.askReview': 'Ask your coach to review it',
  'match.formTitle': 'Log a match',
  'match.formSub': "Write it down while it's fresh.",
  'match.needResult': 'Did you win or lose?',
  'match.needLink': 'Add the video link for the review.',
  'match.loggedReview': 'Logged. Your coach will watch it and review — €{fee}, paid at your next session.',
  'match.logged': 'Logged.',
  'match.saved': 'Saved.',
  'match.edit': 'Edit',
  'match.editTitle': 'Edit the match',
  'match.editSub': 'You can change this until your coach reviews it.',
  'match.saveEdit': 'Save changes',
  'match.saveFailed': 'Could not save: {message}',
  'match.date': 'Date',
  'match.score': 'Score',
  'match.partner': 'Your partner',
  'match.against': 'Against',
  'match.feel': 'How did it feel?',
  'match.feelHint': 'Nerves, what changed in the second set, anything you noticed.',
  'match.reviewBlurb': 'They watch the video and write what worked and what to fix.',
  'match.pasteLink': 'Paste the video link',
  'match.namePh': 'Name',
  'match.namesPh': 'Names',
  'match.cancel': 'Cancel',
  'match.saveFee': 'Save · €{fee}',
  'match.whyLead': 'More than a record.',
  'match.whyBody': ' Your coach sees how you play in real matches, where training holds up and where it slips, and plans your next sessions around it.',
  'match.vs': 'v {name}',
  'match.with': ' · with {name}',
  'match.whatWorked': 'What worked:',
  'match.toWorkOn': 'To work on:',
  'match.reviewWaiting': 'Review requested · waiting for your coach',
  'match.youWrote': 'You wrote:',
  'match.coachRated': "Your coach's ratings",

  // Invite a friend
  'invite.title': 'Add a friend',
  'invite.username': 'Their Telegram username',
  'invite.whoPays': 'Who pays',
  'invite.coverBoth': "I'll cover both",
  'invite.theyPayNothing': 'They pay nothing',
  'invite.theyPay': 'They pay their own',
  'invite.usualRate': 'At their usual rate',
  'invite.send': 'Send invite',
  'invite.sending': 'Sending…',
  'invite.share': 'Share in Telegram',
  'invite.ready': 'Invite ready',
  'invite.copy': 'Copy',
  'invite.usernamePh': 'username',
  'invite.done': 'Done',
  'invite.copied': 'Copied',
  'invite.tapToCopy': 'Tap to copy. One use, and it stops working when the session starts.',
  'invite.full': 'This session is full.',
  'invite.started': 'This session has already started.',
  'invite.notEnabled': "Your coach hasn't turned this on.",
  'invite.tooMany': 'You already have three invites out for this session.',
  'invite.failed': 'Could not send that invite.',

  // Booking screen
  'book.maxPicks': "That's {n} sessions. Book these first, then pick more.",
  'book.btnWeeks': 'Book {n} {weeks} · {total}',
  'book.btnOne': 'Book · {total}',
  'book.btnMany': 'Book {n} {sessions} · {total}',
  'book.creditTotal': ['{n} credit', '{n} credits'],
  'book.nLeft': '{n} left',
  'book.payPerSession': 'Pay per session',
  'book.yourRate': ' · your rate',
  'book.sessionLength': 'Session length',
  'book.minShort': '{n} min',
  'book.picked': ['session picked', 'sessions picked'],
  'book.clear': 'Clear',
  'book.trainEveryWeek': 'Train every week?',
  'book.justOnce': 'Just this once',
  'book.everyWeek': 'Every week × {n}',
  'book.everyWeekShort': 'every week × {n} · ',
  'book.hi': 'Hi, {name}',
  'book.nextUp': 'Next up',
  'book.nothingYet': 'Nothing booked yet. Pick a time and your coach will be expecting you.',
  'book.morning': 'Morning',
  'book.afternoon': 'Afternoon',
  'book.evening': 'Evening',
  'book.tip': 'Tip: pick several times to book them in one go.',
  'book.payCredits': 'Credits',
  'book.payCash': 'Per session',
  'book.seatsShort': '{n} left',
  'book.classFullShort': 'full',
  'book.doneOne': 'Booked. €{amount} to pay at the session.',
  'book.doneSplit': 'Booked. To pay at the session: €{cash} (€{covered} already on your balance).',
  'book.splitLine': 'To pay at the session: €{cash} (€{covered} already on your balance)',
  'book.doneOneCredits': 'Booked. {n} {credits} left.',
  'book.doneAll': 'Booked all {n} sessions.',
  'book.doneSome': 'Booked {n} of {total}.',
  'book.doneNone': 'None of those could be booked.',
  'book.payEach': ' Pay at each session.',
  'book.creditsLeftSuffix': ' {n} {credits} left.',
  'book.classFull': "{title} is full. Join the waitlist? You won't be charged unless a place opens.",
  'book.joinClass': 'Book {title} at {time}? This uses {n} {credits}.',
  'book.waitlisted': "You're number {n} on the waitlist. We'll message you if a place opens.",
  'book.seeYouThere': 'Booked. See you there.',
  'book.credit': ['credit', 'credits'],
  'book.session': ['session', 'sessions'],

  // Booking errors
  'err.slotTaken': 'Someone booked that slot a moment before you. Pick another time.',
  'err.slotGone': 'That time is no longer open. Pick another.',
  'err.noCredits': "You're out of credits. Switch to pay per session (€{price}) or buy a package.",
  'err.classFull': 'That class filled up.',
  'err.noCreditsPlain': "You're out of credits.",
  'err.alreadyInClass': "You're already in that class.",
  'err.tooSoon': "Sessions need at least {hours} hours' notice.",
  'err.tooFar': 'Bookings open {days} days ahead.',
  'err.alreadyCancelled': 'That booking was already cancelled.',
  'err.lessonStarted': 'This session has already started, so it can no longer be cancelled here. Message your coach.',
  'err.rateLimited': 'That was a lot of bookings at once — wait a minute and try again.',
  'err.signIn': 'Open this from the bot chat to sign in.',
  'err.tryAgain': 'Something went wrong. Try again in a moment.',

  // Time rail / day strip
  'rail.nothingOpen': 'Nothing open this day',
  'rail.tryAnother': 'Try another date on the strip above.',
  'rail.waiting': 'Waiting',
  'rail.booked': 'Booked',
  'rail.creditCost': ['{n} credit', '{n} credits'],
  'day.today': 'Today',

  // Health consent (arrives with the work on main)
  'health.ask': 'Your coach may note injuries or health issues so they can train you safely. Is that OK?',
  'health.yes': 'Yes, that\u2019s OK',
  'health.no': 'No, please don\u2019t',

  // Errors
  'err.reopenApp': 'Close and reopen the app',
  'su.title': "Coach sign-up",
  'su.lead': "Set up PadelDesk in a minute. You can change all of this later in Settings.",
  'su.name': "Your name",
  'su.city': "Club / city",
  'su.cityPh': "e.g. Padel Club Limassol",
  'su.language': "Language",
  'su.sport': 'What do you coach?',
  'co.set.lastSport': 'Tennis is the only sport you teach, so it stays on.',
  'su.timezone': "Time zone",
  'su.slug': "Your link for clients",
  'su.slugOk': "Free",
  'su.slugTaken': "Already taken",
  'su.slugReserved': "This word is reserved",
  'su.slugFormat': "Latin letters, digits and hyphens, 2–31 characters",
  'su.slugChecking': "Checking…",
  'su.trial': "Founding plan: €49 a month, the first 14 days free.",
  'su.start': "Start",
  'su.starting': "Setting up…",
  'su.contactTitle': "Message us",
  'su.contactClosed': "This account was closed. Message us and we’ll sort it out.",
  'su.contactLimit': "Sign-ups are full for today. Message us and we’ll get you started.",
  'su.errName': "Enter your name",
  'su.errCity': "Enter your club or city",
  'su.errTimezone': "Pick a time zone from the list",
  'dr.title': "Exercises",
  'dr.rowStatus': "Your drill library, built from your session plans",
  'dr.build': "Build from plans",
  'dr.buildPending': "{n} new",
  'dr.building': "Reading plans… up to a minute",
  'dr.built': "Plans read: {plans}. New exercises: {created}.",
  'dr.search': "Search exercises",
  'dr.empty': "No exercises yet. Tap “Build from plans”.",
  'dr.nothing': "Nothing found.",
  'dr.used': "used {n} {times} · {m} {players}",
  'dr.times': ["time", "times"],
  'dr.players': ["player", "players"],
  'dr.result': "rating went up for {k} of {m}",
  'dr.general': "general",
  'dr.edit': "Exercise",
  'dr.name': "Name",
  'dr.skills': "Skills (up to two)",
  'dr.skillsMore': 'All skills',
  'dr.skillsLess': 'Fewer',
  'dr.save': "Save",
  'dr.mergeInto': "Merge into another exercise",
  'dr.merge': "Merge",
  'dr.mergeConfirm': "Merge “{a}” into “{b}”? Its sessions and spellings move over.",
  'dr.archive': "Archive",
  'dr.archiveConfirm': "Archive “{name}”?",
  'dr.aliases': "Also written as: {list}",
  'dr.helpTitle': "What helped others with this",
  'dr.helpLoading': "Looking through your library…",
  'dr.helpNone': "No exercises for this skill in your library yet.",
  'dr.new': "new",
  'dr.toPlan': "Add to the next session’s plan",
  'dr.added': "Added to the plan for {when}.",
  'dr.noNext': "This player has no upcoming session booked.",
  'dr.close': "Close",
  'dr.taken': "There is already an exercise with that name.",
  'dr.noAi': "AI is not set up on the server.",
  'dr.all': 'All {n}',
  'dr.noDesc': 'No description {n}',
  'dr.noDescRow': 'No description · add',
  'dr.noSkill': 'No skill',
  'dr.usesShort': '{n}×',
  'dr.resultShort': '{k} of {m} ↑',
  'dr.editBtn': 'Edit',
  'dr.how': 'How to do it',
  'dr.howPh': 'Who feeds, from where, where the ball goes',
  'dr.volume': 'Volume',
  'dr.volumePh': '3 baskets × 20 balls · 12 minutes',
  'dr.watch': 'What to watch',
  'dr.watchPh': 'Technique, footwork, timing',
  'dr.harder': 'Make it harder',
  'dr.harderPh': 'How to step it up',
  'dr.notFilled': 'Not filled in',
  'dr.stats': 'Stats',
  'dr.statsOwn': 'Yours',
  'dr.statsOthers': 'Other coaches',
  'dr.statsOthersSoon': 'Coming with the shared exercise catalogue.',
  'dr.aiDraft': '✨ Draft description with AI',
  'dr.aiWorking': 'Writing a draft…',
  'dr.aiDrafted': 'AI draft — check it and save.',
  'dr.toSession': 'Add to a session plan',
  'dr.pickSession': 'Which session?',
  'dr.noSessions': 'No sessions in the next two weeks.',
  'dr.hasPlan': 'has a plan',
  'dr.cancel': 'Cancel',
  'dr.nameOther': "Name in Russian",
  'dr.tidy': "Tidy up",
  'dr.tidyLoading': "Reading your library… up to a minute",
  'dr.tidyHint': "Nothing is saved until you accept it. Session plans stay as they are.",
  'dr.tidyNone': "Everything is already in order.",
  'dr.tidyNames': "Names",
  'dr.tidyMerges': "Duplicates",
  'dr.accept': "✓ Accept",
  'dr.change': "✎ Edit",
  'dr.skip': "Skip",
  'dr.acceptAll': "Accept all ({n})",
  'dr.accepted': "Saved",
  'dr.skipped': "Skipped",
  'dr.mergedAway': "Merged into another exercise",
  'dr.mergeRow': "The same exercise: merge into “{b}”",
  'dr.addSkills': "+ skills: {list}",
  // «Поделиться окнами»: the free times as an Instagram story (ShareStory.tsx).
  'story.button': 'Share free slots',
  'story.title': 'Share free slots',
  'story.thisWeek': 'This week',
  'story.nextWeek': 'Next week',
  'story.dark': 'Dark',
  'story.light': 'Light',
  'story.hint': 'Tap a time to leave it out of the story.',
  'story.hiddenTitle': 'Left out — tap to put back',
  'story.empty': 'No free times this week.',
  'story.send': 'Send to me',
  'story.sending': 'Sending…',
  'story.sent': 'Sent: the picture and your link are in the chat with the bot.',
  'story.noLink': 'Your link is not ready yet. Try again in a minute.',
  'fs.title': "First steps",
  'fs.left': "{n} of 5 done",
  'fs.rowStatus': "Schedule, prices, hot hours, link for students, exercises",
  'fs.schedule': "Schedule",
  'fs.scheduleHint': "Your working hours: students book only inside them.",
  'fs.link': "Link for students",
  'fs.linkHint': "Send it to your students: they sign up with you through it.",
  'fs.prices': "Prices",
  'fs.pricesHint': "The price of a lesson, on the prices screen.",
  'fs.hot': "Hot hours",
  'fs.hotHint': "Mark 🔥 the open times you most want filled: students see the mark.",
  'fs.noHot': "Not now: no hot hours",
  'fs.open': "Open →",
  'fs.copy': "Copy link",
  'fs.copied': "Copied ✓",
  'br.title': "Daily brief",
  'br.on': "Send the daily brief",
  'br.off': "Off",
  'br.statusEvening': "The evening before, {time}",
  'br.statusMorning': "In the morning, {time}",
  'br.evening': "The evening before",
  'br.morning': "In the morning",
  'br.time': "Time",
  'br.hintEvening': "Tomorrow’s sessions in Telegram: who, payment, what you work on. Only when there are sessions tomorrow.",
  'br.hintMorning': "Today’s sessions in Telegram. When the first one starts earlier, the brief comes 30 minutes before it.",
  'fs.brief': "Brief for tomorrow",
  'fs.briefHint': "Optional: every evening in Telegram, tomorrow’s sessions.",
  'fs.briefOn': "Switch on",
  'cat.share': "Share exercises",
  'cat.shareOn': "On · {n} of {m} in the catalogue",
  'cat.shareOff': "Off · the catalogue and other coaches’ results are hidden",
  'cat.title': "Exercise catalogue",
  'cat.what': "You share the name, skill, description and totals (how many players’ ratings went up). Never plans, clients or rating values.",
  'cat.give': "You see the catalogue and “other coaches” while you share.",
  'cat.author': "Show my name",
  'cat.authorPh': "Igor, Limassol",
  'cat.anon': "Now: anonymous",
  'cat.authorSave': "Save name",
  'cat.contrib': "Your contribution",
  'cat.contribLine': "{n} {coaches} use your exercises",
  'cat.contribPct': " · helped {pct}% of players",
  'cat.contribNone': "Nobody has taken your exercises yet.",
  'cat.badge': "🏅 Exercise of the month: {name}",
  'cat.notShared': "Not in the catalogue: {n}",
  'cat.send': "Send to the catalogue",
  'cat.previewTitle': "To the catalogue",
  'cat.reading': "Checking against the catalogue… up to a minute",
  'cat.sameQ': "Is this the same exercise?",
  'cat.yes': "Yes",
  'cat.no': "No",
  'cat.newOnes': "New in the catalogue: {n}",
  'cat.shareBtn': "Share",
  'cat.shared': "Done: {linked} linked, {created} new in the catalogue.",
  'cat.nothing': "All your exercises are already in the catalogue.",
  'cat.others': "went up for {k} of {m} players · {n} {coaches}",
  'cat.othersNoResult': "no results yet · {n} {coaches}",
  'cat.othersHidden': "Switch on “Share exercises” to see this.",
  'cat.othersNone': "This exercise is not in the catalogue yet.",
  'cat.section': "Catalogue · other coaches",
  'cat.tag': "catalogue",
  'cat.by': "By {name}",
  'cat.byAnon': "By an anonymous coach",
  'cat.add': "Add to mine",
  'cat.added': "Added ✓",
  'cat.result': "up for {k} of {m}",
  'cat.coachesN': "{n} {coaches}",
  'cat.topTitle': "Exercises from the catalogue",
  'cat.topHint': "The best by results with other coaches. Tick what to add.",
  'cat.addN': "Add {n} exercises",
  'cat.addedN': "Added {n} exercises.",
  'cat.turnOn': "Switch on and open the catalogue",
  'cat.empty': "The catalogue is empty for now.",
  'fs.catalogue': "Exercises from the catalogue",
  'fs.catalogueHint': "Take tried exercises from other coaches into your library.",
  'co.axis.shared': "→ {skill}",
  'co.axis.sharedLabel': "Skill in the shared catalogue",
  'co.axis.sharedAi': "proposed by AI",
  'biz.cat': "Exercise catalogue",
  'biz.catEntries': "Exercises in the catalogue",
  'biz.catSharing': "Sharing: {n} · not sharing: {m}",
  'biz.catSeed': "Fill the catalogue from my library",
  'biz.catSeeding': "Filling… up to a minute",
  'biz.catSeeded': "Done: {linked} linked, {created} new.",
  'biz.catMonth': "Exercise of the month ({month}): {name} · up for {k} of {m} · {n} coaches",
  'biz.catMonthNone': "No exercise of the month yet.",
  'cat.coaches': ['coach', 'coaches'],
  'err.unknownLink': "Link not found — ask your coach to send the link again",
  'biz.contactTitle': "Contact for “Message us”",
  'biz.contactPh': "@username",
  'biz.contactHint': "Shown to coaches who can’t sign up (closed account, or the day’s limit is reached).",
  'err.generic': 'Something went wrong. Try again.',
  'err.couldNot': 'Could not: {message}',

  // Text that used to be written straight into the screens
  'app.signInFailed': "Can't sign you in",
  'a11y.chooseDay': 'Choose a day',
  'a11y.remove': 'Remove',
  'safe.couldNotShow': 'Could not show this: {message}',
  'co.month.setCourtRate': 'Set your court rate in Setup to see what you keep after hire.',
  'co.cd.back': 'Back',
  'co.cd.bookFor': 'Book for {name}',
  'co.cd.gift': '🎁 Gift',
  'co.cd.nothingOpen': 'Nothing open in the next 30 days.',
  'co.cd.noCreditsLeft': '{name} has no credits left — book as pay per session instead.',
  'co.cd.moved': '{n} {sessions} moved to {time}.',
  'co.cd.movedSkipped': ' {n} could not move — something was already booked.',
  'co.cd.moveTitle': "Move {name}'s session",
  'co.cd.moveCurrently': 'Currently {day} at {time}.',
  'co.cd.newDay': 'New day',
  'co.cd.newTime': 'New time',
  'co.cd.startTitle': 'Where {name} is now',
  'co.cd.startExplain': 'Their starting point, from what you already know. Session ratings take over as they build up.',
  'co.cd.paysFor': '{payer} pays for {name}',
  'co.cd.paysForBody': "Their bookings use {payer}'s credits.",
  'co.cd.paysSelf': '{name} pays for themselves',
  'co.cd.pkgUnused': '{n} unused {sessions} will be taken back.',
  'co.cd.creditsRemoved': '{n} {credits} removed. They now have {balance}.',
  'co.cd.switched': '{n} {sessions} now paid by credits. {balance} {credits} left.',
  'co.cd.switchedCash': ' {n} still on cash — not enough credits.',
  'co.cd.noCreditsToUse': '{name} has no credits to use.',
  'co.cd.noHealthNotes': 'No health notes — they asked you not to record injuries or health details.',
  'co.today.freeDay': 'A free day',
  'co.today.freeDayHint': 'Nobody has booked it yet.',
  'co.rate.paidInFull': "The session was paid in full already. Nothing owed, nobody's price changes.",
  'co.rate.eachPays': 'Each pays',
  'co.money.heldTitle': '€{amount} of coaching owed',
  'co.money.heldHint': 'Paid for, not yet delivered.',
  'co.money.heldNone': 'Nobody is holding sessions.',
  'co.money.collectTitle': '€{amount} to collect',
  'co.money.collectHint': 'Sessions taught and not yet paid for.',
  'co.money.failed': "Couldn't load your figures.",
  'co.pp.howPlays': 'How {name} plays',
  'co.pp.playtomicLabel': 'Playtomic level (optional, for reference)',
  'co.pp.playtomicPlaceholder': 'e.g. 4.2',
  'co.pp.since': '{delta} since {month}',
  'co.pp.scaleMid': 'Intermediate 3–4.5',
  'co.pp.yourAssessment': 'Your assessment · {date}',
  'co.pp.playtomicRef': 'Playtomic {n} · for reference',
  'co.pp.levelTitle': "{name}'s level",
  'co.pp.lower': 'Lower by 0.1',
  'co.pp.raise': 'Raise by 0.1',
  'co.pp.levelOverTime': 'Level over time',
  'co.pp.aiOffCard': 'Not set up yet. Add ANTHROPIC_API_KEY to the server on Railway and this card turns on.',
  'co.pp.aiPrivate': 'Only you see this. Your notes are sent to Claude to write it.',
  'co.pp.aiStale': 'New notes since',
  'co.pp.aiStaleLang': 'Outdated: tap Refresh',
  'co.pp.aiRefresh': 'Refresh',
  'co.pp.aiThemes': 'Comes up again and again',
  'co.pp.aiHealthPrivate': '🔒 Health notes stay private and are never shown to {name}.',
  'co.pp.blk.overview': "Overall picture",
  'co.pp.blk.better': "What got better",
  'co.pp.blk.stuck': "What is stuck",
  'co.pp.blk.next': "What we do next",
  'co.pp.blk.homework': "Homework",
  'co.pp.aiFocusPlayer': "The player gets: {text}",
  'co.pp.aiFocus': 'Suggested focus',
  'co.pp.aiUseFocus': 'Set as “Working on”',
  'co.pp.src.standing': 'Standing notes',
  'co.pp.src.workingOn': 'Current Working on',
  'co.pp.src.earlierGoal': 'Earlier Working on',
  'co.pp.src.focus': 'Focus',
  'co.pp.src.drills': 'Drills',
  'co.pp.src.private': 'Private note',
  'co.pp.src.shared': 'Note for the player',
  'co.pp.src.common': 'For everyone on the session',
  'co.pp.src.homework': 'Homework',
  'co.pp.src.asked': 'Player asked to work on',
  'co.pp.src.undated': 'Standing',
  'co.set.failed': 'Failed: {message}',
  'co.set.peakNone': 'Every hour costs €{base}. Add a band if courts cost more at certain times.',
  'co.set.bandPerHour': '€{price}/hour',
  'co.set.bandOverlap': 'Overlaps {band}: one rate applies, not the sum',
  'co.set.courtsTitle': 'Courts you have',
  'co.set.courtsHint': 'Only these hours can be booked. Tap an hour or a day to fill the row.',
  'co.set.saveChanges': 'Save changes',
  'err.blocked': 'Your coach has closed your access, so this can’t be done.',

  // Approving new clients
  'co.set.approveNew': 'Approve new clients',
  'co.set.approveNewHint': 'People who open your link can’t see your times or book until you approve them. You get a message for each one. Friends your clients invite are let in straight away.',
  'co.req.title': 'New requests',
  'co.req.waiting': '{names} — waiting for you',
  'co.req.waitingOne': '{names} — waiting for you',
  'co.req.aria': 'New requests: {n} waiting',
  'co.req.intro': 'They opened the bot through your link. Until you approve them they can’t see your times or book.',
  'co.req.noUsername': 'no username',
  'co.req.approve': 'Approve',
  'co.req.block': 'Block',
  'co.req.blockConfirm': 'Block {name}? They won’t be told.',
  'co.req.foot': 'An approved client gets a message that they can book. Blocking is silent — they are not told.',
  'co.req.close': 'Close',
  'book.waitTitle': 'Almost there',
  'book.waitBody': '{coach} approves new clients before their first booking. We’ll message you here in Telegram as soon as you can book.',
  'book.yourCoach': 'Your coach',
  'err.notApproved': 'Your coach hasn’t approved you yet. We’ll message you as soon as you can book.',

  // My profile
  'mp.level': 'Level · Playtomic',
  'mp.levelSetBy': 'Set by {coach}, {date}',
  'mp.skills': 'Your skills',
  'mp.training': 'Training',
  'mp.noRatings': 'Your coach hasn’t rated a session yet. Your skills show here after the first one.',
  'mp.fourWeeks': 'The last four weeks',
  'mp.dayLesson': 'Session',
  'mp.dayNoShow': 'Missed',
  'mp.sessionsCount': '{n} {sessions}',
  'mp.noShowsCount': '{n} missed',

  // Credits, now on Book and at the bottom of My profile
  'credits.title': 'Credits',
  'credits.word': ['credit', 'credits'],
  'credits.leftCount': '{n} {credits} left',
  'ledger.packageRemoved': 'Package removed by your coach',

  // Offering a freed slot
  'co.offer.title': 'Who gets {when}?',
  'co.offer.regulars': 'Usually at this time',
  'co.offer.frequent': 'Come often',
  'co.offer.added': 'Added',
  'co.offer.add': '+ Add a client',
  'co.offer.searchPh': 'Client’s name',
  'co.offer.noneFound': 'Nobody found',
  'co.offer.daysPl': 'Mondays|Tuesdays|Wednesdays|Thursdays|Fridays|Saturdays|Sundays',
  'co.offer.regularChip': '{days} · {n} of the last 8',
  'co.offer.freqChip': '{n} {sessions} in 8 weeks',
  'co.offer.sessionsW': ['session', 'sessions'],
  'co.offer.notInBot': 'not in the bot',
  'co.offer.never': 'Blocked clients and new requests are never listed. Nobody is messaged until you tap Offer.',
  'co.offer.cancel': 'Cancel',
  'co.offer.send': 'Offer to {n}',
  'co.offer.foot': 'Normal price: credits if they have enough, otherwise they pay at the session. The first to tap Take it gets it.',
  'co.offer.sent': 'Offered to {n} {clients}. The first to tap Take it gets it.',
  'co.offer.clientsDat': ['client', 'clients'],
  'co.offer.nobody': 'Nobody to suggest: no sessions in the last 8 weeks. Add a client by name.',
  'co.offer.failed': 'Couldn’t open this offer.',
  'co.offer.notYours': 'This slot is another coach’s. You can’t offer it.',
  'co.offer.state.offered': 'Already offered.',
  'co.offer.state.taken': 'Already taken.',
  'co.offer.state.kept': 'You chose not to offer it.',
  'co.offer.state.closed': 'This offer has closed: it’s too close to the start.',
} as const;

const RU: Partial<Record<keyof typeof EN, string | readonly string[]>> = {
  'app.creditsLeft': ['занятие осталось', 'занятия осталось', 'занятий осталось'],
  'tab.book': 'Записаться',
  'tab.bookings': 'Мои занятия',
  'tab.credits': 'Баланс',
  'vis.me': '🔒 Только вы',
  'vis.player': '👁 Видит игрок',
  'co.rate.sharedLabel': 'Заметка для игрока',
  'co.rate.homeworkLabel': 'Домашнее задание (необязательно)',
  'co.rate.clearWhenDone': 'Очистите, когда закончите.',
  'jr.lessons': ['занятие', 'занятия', 'занятий'],
  'jr.noNotes': 'Без заметок',
  'jr.count': '{n} {lessons}',
  'jr.since': 'с {date} · {n} {sessions}',
  'jr.newGoal': 'Новая цель:',
  'jr.newGoalFrom': 'Новая цель от тренера:',
  'jr.drills': 'Что делали',
  'jr.drillsPlayer': 'Что делали',
  'jr.shared': 'Заметка для игрока',
  'jr.fromCoach': 'От тренера',
  'jr.homework': 'Домашнее задание',
  'jr.common': 'Для всех на занятии',
  'jr.yourTask': 'Ваше задание',
  'jr.ratings': 'Оценки',
  'jr.private': 'Личная заметка',
  'jr.asked': 'Игрок просил поработать над',
  'jr.askedPlayer': 'Вы просили поработать над',
  'jr.fromPlayer': '💬 От игрока',
  'jr.missed': 'Пропущено',
  'jr.min': '{n} мин',
  'jr.nothing': 'К этому занятию ничего не записано.',
  'jr.empty': 'Занятий пока нет. Они появятся здесь после каждого.',
  'lv.back': '‹ Назад',
  'lv.couldNotOpen': 'Не удалось открыть занятие.',
  'lv.credits': ['кредит', 'кредита', 'кредитов'],
  'lv.nCredits': '{n} {credits}',
  'lv.noDrills': 'Пока ничего не записано. Нажмите «Изменить», чтобы добавить упражнения.',
  'lv.ratings': 'Оценки',
  'lv.vsLast': 'по сравнению с прошлым разом',
  'lv.first': 'первая оценка',
  'lv.notRated': 'Пока без оценки.',
  'lv.noNote': 'Заметки пока нет.',
  'lv.noPrivate': 'Личной заметки нет.',
  'lv.homework': 'Домашнее задание:',
  'jr.failed': 'Не удалось загрузить занятия. Откройте вкладку ещё раз.',
  'tab.profile': 'Мой профиль',

  'book.pickTime': 'Выберите время',
  'book.noTimes': 'В ближайшие недели свободного времени нет.',
  'book.repeatWeekly': 'Повторять еженедельно',
  'book.weeks': ['неделя', 'недели', 'недель'],
  'book.payWith': 'Оплата',
  'book.credits': 'Кредитами',
  'book.cash': 'На занятии',
  'book.book': 'Записаться',
  'book.booking': 'Записываем…',
  'book.booked': 'Записано {n} {sessions}.',
  'book.creditsLeft': '\nНа счету: {n} {credits}.',
  'book.totalCash': ' Всего €{amount}, оплата на каждом занятии.',
  'book.skippedTaken': '\n\n{n} {weeks} уже заняты и пропущены.',
  'book.skippedHorizon': '\n\n{n} {weeks} — дальше, чем открыта запись. Запишитесь на них ближе к дате.',
  'book.workingOn': 'Над чем хотите поработать?',

  'note.save': 'Сохранить',
  'note.saving': 'Сохраняем…',
  'note.failed': 'Не удалось сохранить.',
  'bookings.none': 'Пока ничего не записано',
  'bookings.noneHint': 'Выберите время на вкладке «Записаться», и оно появится здесь.',
  'bookings.book': 'Записаться на занятие',
  'bookings.today': 'Сегодня',
  'bookings.tomorrow': 'Завтра',
  'bookings.later': 'Позже',
  'bookings.private': 'Индивидуальное занятие · {min} мин',
  'bookings.in': 'через {time}',
  'cal.feedTitle': 'Календарь',
  'cal.feedName': 'Занятия в календаре',
  'cal.feedHint': 'Обновляется сам: прошедшие остаются, отменённые исчезают.',
  'cal.feedSubscribe': 'Подписаться',
  'cal.which': 'Каким календарём вы пользуетесь?',
  'cal.title': 'Добавить в календарь',
  'cal.google': 'Google',
  'cal.appleOther': 'Apple / другой',
  'cal.confirmGoogle': 'Добавить в Google Календарь? Для Apple или другого календаря нажмите «Отмена».',
  'bookings.toPay': '€{amount} к оплате на занятии',
  'bookings.toPaySplit': 'К оплате на занятии: {amount} € ({covered} € уже на счету)',
  'bookings.freeCancelPassed': 'Время бесплатной отмены прошло',
  'bookings.freeCancelFor': 'Бесплатная отмена ещё {time}',
  'bookings.move': 'Перенести',
  'bookings.cancel': 'Отменить запись',
  'bookings.addFriend': 'Позвать друга',
  'bookings.waitlist': 'Место в очереди: {n} · оплаты нет',

  'cancel.title': 'Отменить это занятие?',
  'cancel.lateWarning': 'Время бесплатной отмены прошло: кредит не вернётся. Всё равно отменить?',
  'cancel.lateWarningCash': 'Время бесплатной отмены прошло: занятие всё равно будет оплачено — €{amount}. Всё равно отменить?',
  'cancel.confirm': 'Отменить {title}, {day}? Кредит вернётся на счёт.',
  'cancel.back': 'Назад',

  'move.title': 'Перенести занятие',
  'move.currently': 'Сейчас {day} в {time}.',
  'move.pickNew': 'Выберите новое время',
  'move.nothingOpen': 'В ближайшие 30 дней свободного времени больше нет.',
  'move.moving': 'Переносим…',
  'move.moveTo': 'Перенести на {time}',
  'move.pickATime': 'Выберите время',
  'move.moved': 'Перенесено на {day}, {time}.',
  'move.tooLate': 'До занятия слишком мало времени, перенести уже нельзя. Напишите тренеру.',
  'move.slotGone': 'Это время только что заняли. Выберите другое.',
  'move.failed': 'Не удалось перенести. Попробуйте ещё раз через минуту.',

  'credits.balance': 'Ваши кредиты',
  'credits.history': 'История',
  'credits.packages': 'Ваши пакеты',
  'credits.anySession': 'Любое занятие',
  'credits.groupOnly': 'Групповые',
  'credits.privateOnly': 'Индивидуальные',
  'credits.expires': 'Срок действия: {day} · осталось {left}',
  'credits.none': 'Кредитов пока нет',
  'credits.noneHint': 'Напишите тренеру, чтобы купить пакет, или записывайтесь и платите за каждое занятие.',
  'ledger.purchase': 'Куплен пакет',
  'ledger.booked': 'Запись: {title} · {time}',
  'ledger.bookedPlain': 'Запись на занятие',
  'ledger.refund': 'Возврат за отмену',
  'ledger.expiry': 'Срок кредитов истёк',
  'ledger.noShow': 'Пропуск занятия',
  'ledger.adjustment': 'Корректировка',

  'co.coachView': 'тренер',
  'co.more': '…ещё',
  'co.tab.today': 'Календарь',
  'co.tab.money': 'Деньги',
  'co.tab.clients': 'Клиенты',
  'co.tab.setup': 'Настройки',
  'co.set.version': 'Версия {v}',
  'co.clients.title': 'Клиенты',
  'co.clients.search': 'Имя, @ник или телефон',
  'co.off.add': "+ Добавить клиента вручную",
  'co.off.addTitle': "Новый клиент",
  'co.off.name': "Имя",
  'co.off.phone': "Телефон",
  'co.off.note': "Заметка для себя",
  'co.off.optional': "необязательно",
  'co.off.onlyYou': "видите только вы",
  'co.off.notePh': "Например: играет по средам с мужем",
  'co.off.info': 'Бот не пишет этому клиенту и не предлагает ему освободившиеся места. Записи, переносы и оплаты — через вас. Когда клиент откроет вашу ссылку в Telegram, нажмите «Привязать», и все данные перейдут в его профиль из бота.',
  'co.off.cancel': "Отмена",
  'co.off.addBtn': "Добавить",
  'co.off.adding': "Добавляем…",
  'co.off.tag': "не в боте",
  'co.off.addedOn': "добавлен вручную · {day}",
  'co.off.linkAsk': 'Клиент открыл вашу ссылку в Telegram?',
  'co.off.linkGo': "Привязать →",
  'co.off.pickTitle': "Привязать к Telegram",
  'co.off.pickWho': "Кто из бота — это {name}?",
  'co.off.pickSearch': "Поиск: имя или @username",
  'co.off.alike': "Похоже",
  'co.off.samePhone': "✓ тот же телефон",
  'co.off.sameName': "✓ то же имя",
  'co.off.others': "Другие, кто открыл вашу ссылку",
  'co.off.waiting': "ждёт вашего одобрения",
  'co.off.since': "в боте с {day}",
  'co.off.onlyBot': "Только клиенты, которые открывали бота или приложение.",
  'co.off.noneFound': "Никого не нашли.",
  'co.off.nobodyYet': "В боте пока некого привязать. Сначала отправьте клиенту вашу ссылку.",
  'co.off.confirmTitle': "Привязать: {name}?",
  'co.off.manual': "добавлен вручную",
  'co.off.inBot': "в боте",
  'co.off.moves': "Перейдёт к {name}",
  'co.off.mvSessions': "{n} {sessions}",
  'co.off.mvUpcoming': ", предстоящих: {n}",
  'co.off.mvCredits': "Кредиты: {n}",
  'co.off.mvPackages': "Пакеты: {n}",
  'co.off.mvPayments': "Оплаты наличными: {n}",
  'co.off.mvOwed': "Долг: {amount}",
  'co.off.mvNotes': "Оценки, заметки и «Над чем работаем»",
  'co.off.mvRest': "Метки, личные цены, ваша заметка и телефон",
  'co.off.keep': 'В профиле останется имя «{name}». Язык и @username — из Telegram. Профиль «вручную» исчезнет. Отменить это будет нельзя.',
  'co.off.clash': "Оба записаны на {when}. Отмените одну из записей, потом привяжите.",
  'co.off.close': "Закрыть",
  'co.off.linkBtn': "Привязать",
  'co.off.done': "{name} теперь в боте.",
  'co.off.doneUpcoming': '{name} теперь в боте. Напоминания о предстоящих занятиях ({n}) придут как обычно.',
  'co.off.reqMatch': "Похоже на клиента, добавленного вручную",
  'co.off.aiOff': 'AI недоступен: клиент не в боте.',
  'co.clients.all': 'Все',
  'co.clients.someOf': '{n} из {total}',
  'co.clients.none': 'Клиентов пока нет.',
  'co.clients.noneFiltered': 'С этой меткой пока никого.',
  'co.clients.onlyShown': 'Показаны только: {tag}.',
  'co.clients.clearFilter': 'Сбросить фильтр',
  'co.clients.firstSession': 'Первое занятие {when}',
  'co.clients.noSessionsYet': 'занятий пока нет',
  'co.clients.editTags': 'Изменить метки',
  'co.status.lapsed': 'Давно не приходит',
  'co.status.slipping': 'Ходит реже',
  'co.status.new': 'Новичок',
  'co.why.lapsed': 'Нет занятий {n} {days}',
  'co.why.slipping': '{n} дней с последнего занятия, обычно раз в {gap}',
  'co.why.new': 'Первые занятия',
  'co.freq.twiceWeekly': 'Около двух раз в неделю',
  'co.freq.weekly': 'Раз в неделю',
  'co.freq.fortnightly': 'Раз в две недели',
  'co.freq.occasional': 'Изредка',
  'co.freq.none': 'Не было занятий 8 недель',
  'co.level': 'Уровень {n}',
  'co.noLevel': 'Уровень не задан',
  'co.progress.up': '{n} за месяц',
  'co.progress.down': '{n} за месяц',
  'co.progress.steady': 'Без изменений за месяц',
  'co.progress.none': 'Пока не с чем сравнить',
  'co.days': ['день', 'дня', 'дней'],
  'co.tag.add': 'Добавить метку',
  'co.tag.addTo': 'Добавить метку: {name}',
  'co.tags.title': 'Метки: {name}',
  'co.tags.hint': 'Нажмите, чтобы добавить или убрать. Видите только вы.',
  'co.tags.new': 'Новая метка',
  'co.tags.newPlaceholder': 'Понедельник 7:00, Турнирная группа…',
  'co.tags.add': 'Добавить',
  'co.tags.done': 'Готово',
  'co.tags.edit': 'Изменить метку',
  'co.tags.name': 'Название',
  'co.tags.colour': 'Цвет',
  'co.tags.save': 'Сохранить',
  'co.tags.cancel': 'Отмена',
  'co.tags.delete': 'Удалить метку',
  'co.tags.deleteTitle': 'Удалить «{tag}»?',
  'co.tags.deleteBody': 'Метка стоит у {n} {clients} — она будет снята. Занятия, заметки и история не изменятся.',
  'co.tags.deleteEmpty': 'Метки нет ни у кого. Удалить?',
  'co.tags.keep': 'Оставить',
  'co.tags.confirmDelete': 'Удалить',
  'co.tags.clients': ['клиента', 'клиентов', 'клиентов'],
  'co.tags.exists': 'Метка с таким названием уже есть.',
  'co.tags.failed': 'Не удалось сохранить метку.',
  'co.tags.newTag': '+ Новая метка',
  'co.tags.none': 'Меток пока нет.',
  'co.colour.teal': 'Бирюзовый',
  'co.colour.blue': 'Синий',
  'co.colour.amber': 'Янтарный',
  'co.colour.purple': 'Фиолетовый',
  'co.colour.red': 'Красный',
  'co.colour.grey': 'Серый',

  'co.today.nextUp': 'Следующее занятие',
  'co.month.open': 'Выбрать дату',
  'co.month.prev': 'Предыдущий месяц',
  'co.month.next': 'Следующий месяц',
  'co.month.sessions': 'есть занятия',
  'co.month.unpaid': 'есть неоплаченные',
  'co.today.onCourt': 'Сейчас на корте',
  'co.today.nothingToday': 'На сегодня записей нет',
  'co.today.nothing': 'Записей нет',
  'co.today.nobodyToTell': 'Записей нет — сообщать некому.',
  'co.today.toCollect': '€{amount} к оплате',
  'co.today.allDone': 'На сегодня всё',
  'co.today.left': 'осталось {n} мин',
  'co.today.group': 'Группа · {n} {players}',
  'co.today.players': ['игрок', 'игрока', 'игроков'],
  'co.today.showSession': 'Показать занятие в {time}',
  'co.today.numTaught': 'проведено',
  'co.today.numEarned': 'заработано',
  'co.today.numToCollect': 'к получению',
  'co.today.tomorrow': 'Завтра: {time} · {who}',
  'co.today.tomorrowNone': 'Завтра записей нет',
  'co.live.open': 'Открыть занятие в {time}',
  'co.live.over': 'Занятие закончилось',
  'co.live.endedAt': 'закончилось в {time}',
  'co.rate.lastTime': 'в прошлый раз {n}',
  'co.rate.lastTimeSet': 'Поставить {n}, как в прошлый раз',
  'co.rate.restored': 'Восстановлено то, что не успело отправиться',
  'co.hub.attendance': 'Посещение: {name}',
  'co.hub.came': 'Пришёл',
  'co.hub.noShow': 'Не пришёл',
  'co.hub.cash': 'Оплата наличными',
  'co.hub.balance': 'Баланс: {n} {credits}',
  'co.hub.balanceOf': 'Баланс ({name}): {n} {credits}',
  'co.hub.write': 'Написать',
  'co.hub.call': 'Позвонить',
  'co.hub.profile': 'Профиль',
  'co.hub.addPlayer': '+ Игрок',
  'co.today.sessionsTaught': 'Проведено занятий сегодня: {n}',
  'co.today.sessions': ['занятие', 'занятия', 'занятий'],
  'co.today.inMin': 'через {n} мин',
  'co.today.until': 'до {time}',
  'co.today.day': 'Сегодня',
  'co.today.count': 'Занятий: {n}{done}',
  'co.today.done': ' · проведено {n}',
  'co.today.cancelDay': 'Отменить день',
  'co.today.openSlot': 'Свободное время',
  'co.today.now': 'Сейчас',
  'co.today.add': '+ Добавить',
  'co.rm.more': 'Ещё: {name}',
  'co.rm.action': 'Убрать из занятия',
  'co.rm.title': 'Убрать из занятия: {name}',
  'co.rm.body': 'Запись была ошибкой и отменится полностью: кредиты вернутся, оплата за занятие снимется. Ни поздней отмены, ни неявки, в посещаемость не попадёт. Остальным цена вернётся к цене на одного меньше.',
  'co.rm.silent': 'Не сообщать',
  'co.rm.silentHint': 'Если человек не знал о записи.',
  'co.rm.willSay': '{name} получит короткое сообщение в Telegram: запись по ошибке, ничего не списано.',
  'co.rm.wontSay': 'Сообщения {name} не будет.',
  'co.rm.noBot': '{name} не в боте — сообщения не будет в любом случае.',
  'co.rm.do': 'Убрать',
  'co.rm.done': '{name}: убран(а) из занятия.',
  'co.rm.doneCredits': ' Вернули {n} {credits}.',
  'co.rm.doneTold': ' Сообщение отправим.',
  'co.rm.doneSilent': ' Без сообщения.',
  'co.rm.rated': '{name}: убрать нельзя — в этом занятии уже есть оценки или заметки. Убрать можно только до оценок, заметок и оплаты.',
  'co.rm.paid': '{name}: убрать нельзя — оплата наличными за это занятие уже получена.',
  'co.rm.last': '{name} — единственный в занятии. Чтобы снять занятие, нажмите «Отменить».',
  'co.today.came': 'Присутствие',
  'co.today.noShow': 'Неявка',
  'co.today.lateCancel': 'Поздняя отмена',
  'co.today.markNoShow': 'Отметить неявку',
  'co.today.rate': 'Оценить',
  'co.today.got': 'Получено €{amount}',
  'co.today.gotDots': 'Получено…',
  'co.today.owes': 'Долг {amount} €',
  'co.today.splitPill': '{amount} € + {n} кр.',
  'co.today.splitLine': '{amount} € наличными + {n} {credits}',
  // «Оплата» as a receipt (components/LessonPayment.tsx).
  'lp.useBalance': 'Использовать баланс',
  'lp.balanceSub': 'На счету {n} кр. · по {rate} за занятие',
  'lp.balanceSubPayer': 'Баланс ({payer}): {n} кр.',
  'lp.rest': 'Осталось оплатить',
  'lp.next': '+ Оплатить следующее занятие',
  'lp.nextCreditsSub': 'Записано на кредиты: {n} кр. на баланс для него',
  'lp.toBalance': '+ {amount} на баланс ({n} кр.)',
  'lp.toBalanceSub': 'Следующее занятие не записано',
  'lp.total': 'Получено {amount} · {method}',
  'lp.partToday': '{amount} сегодня',
  'lp.partNext': '{amount} за {day}',
  'lp.partNextShort': '{amount} за {day} (ещё {left})',
  'lp.partBalance': '{amount} на баланс ({n} кр.)',
  'lp.fromBalance': '+ {n} кр. с баланса · на счету останется {left} кр.',
  'lp.save': 'Получено {amount}',
  'lp.saveBalance': 'Списать с баланса',
  'lp.short': 'За это занятие останется долг {left}',
  'lp.err.prepay_gone': 'Следующее занятие изменилось — откройте оплату заново.',
  'lp.err.changed_since': 'Занятие изменилось после оплаты — отменить нельзя, исправьте вручную',
  'lp.err.credits_used': 'Кредиты из этой оплаты уже использованы — сначала измените то занятие.',
  'lp.err.read_only': 'Старше 60 дней — только просмотр.',
  'lp.err.more_is_new_payment': 'Больше — это новая оплата: «Получено…».',
  'lp.err.method_required': 'Выберите, как заплатили.',
  'lp.err.already_undone': 'Эта оплата уже отменена.',
  'lp.err.nothing_to_pay': 'Нечего записать.',
  'lp.err.other': 'Не удалось: {code}',
  'lp.paidBtn': '✓ Оплачено',
  'lp.prepaidBtn': '✓ Оплачено заранее',
  'lp.editTitle': 'Изменить оплату: {name}',
  'lp.edit': 'Изменить оплату',
  'lp.paidLine': 'Оплачено',
  'lp.byCredits': '{n} кр. с баланса',
  'lp.receivedBy': 'Получено {at} · {who}',
  'lp.sameReceipt': 'в той же оплате: {parts}',
  'lp.ahead': 'заранее',
  'lp.amountThis': 'Сумма за это занятие',
  'lp.amountHint': 'Меньше — разница снова к оплате. Больше — это новая оплата.',
  'lp.methodAll': 'Способ меняется для всей оплаты {amount}.',
  'lp.undo': 'Отменить оплату {amount}',
  'lp.undoConfirm': 'Отменить оплату {amount}? Занятия в ней снова к оплате, кредиты вернутся на баланс.',
  'lp.saveChange': 'Сохранить',
  'lp.back': 'Назад',
  'lp.changes': 'История изменений',
  'lp.ch.method': 'способ: {old} → {new}',
  'lp.ch.amount': 'сумма: {old} → {new}',
  'lp.ch.undo': 'оплата отменена',
  'lp.ch.paid': 'получено {amount}',
  'lp.undoneLine': 'Отменено {at} · {who}',
  'lp.history': 'История оплат',
  'lp.onBalance': 'На счету: {n} кр.',
  'lp.f.all': 'Всё',
  'lp.f.money': 'Деньги',
  'lp.f.credits': 'Кредиты',
  'lp.f.changes': 'Изменения',
  'lp.h.received': 'Получено {amount}',
  'lp.h.partCash': '{amount} за занятие {day}',
  'lp.h.partCredits': '{n} кр. с баланса',
  'lp.h.partAhead': '{amount} за {day} — заранее',
  'lp.h.partToBalance': '{amount} → {n} кр. на баланс',
  'lp.h.forLesson': ' на {day}',
  'lp.h.lesson': 'Занятие {day}',
  'lp.h.topup': 'Пополнение {amount} → {n} кр.',
  'lp.h.package': 'Пакет «{name}» {amount}',
  'lp.h.overpay': 'Переплата {amount} → {n} кр.',
  'lp.h.refunded': 'возвращён',
  'lp.h.use': 'Списано {n} кр.',
  'lp.h.refund': 'Возврат {n} кр.',
  'lp.h.expiry': 'Сгорело {n} кр.',
  'lp.h.adjust': 'Корректировка {n} кр.',
  'lp.h.change': 'Изменено: {what}',
  'lp.h.empty': 'Оплат пока нет.',
  'lp.h.undone': 'отменено',
  'lp.open': 'История оплат',
  'lp.due': 'К оплате {amount}',
  'co.paid.title': 'Оплата: {name}',
  'co.paid.due': 'К оплате',
  'co.paid.stillOwed': 'Осталось оплатить',
  'co.paid.received': 'Получено',
  'co.paid.extra': '+{extra} → {n} {credits} на счёт клиента',
  'co.paid.rate': 'по цене занятия: {rate} за кредит',
  'co.paid.noRate': 'У этого занятия нет цены за кредит, поэтому излишек не перевести в кредиты.',
  'co.paid.short': 'Останется долг: {left}',
  'co.paid.fromCredits': 'Остаток {amount} спишем с баланса ({n} {credits})',
  'co.paid.fromPayerCredits': 'Остаток {amount} спишем с баланса плательщика — {payer} ({n} {credits})',
  'co.paid.mix': '{cash} наличными + {n} {credits} · долг {left}',
  'co.paid.rounding': ' (округление {amount})',
  'co.paid.restRounding': 'Остаток {amount} — округление, долга не будет.',
  'sheet.closeUnsaved': 'Закрыть без сохранения?',
  'co.reg.chip': 'Запись до {date}',
  'co.reg.none': 'Нет записей вперёд',
  'co.reg.filter': 'Постоянные · {n}',
  'co.reg.onNow': '★ {name} теперь постоянный клиент',
  'co.reg.offNow': '{name} больше не отмечен как постоянный',
  'co.reg.mode': 'Напоминания клиентам',
  'co.reg.modeOff': 'Выкл',
  'co.reg.modeManual': 'Вручную',
  'co.reg.modeAuto': 'Автоматически',
  'co.reg.modeOffHint': 'Напоминаний нет.',
  'co.reg.modeManualHint': 'Раз в день бот пришлёт вам список. Клиенту уйдёт только после вашего «Напомнить».',
  'co.reg.modeAutoHint': 'Бот сам напомнит клиенту и сообщит вам.',
  'co.reg.onlyShown': 'Показаны только постоянные клиенты.',
  'co.reg.label': 'Постоянный',
  'co.reg.rowLabel': 'Постоянный клиент',
  'co.reg.labelHint': 'Напомним записаться, когда запись заканчивается',
  'co.reg.remind': 'Напомнить',
  'co.reg.reminded': 'Напоминание уйдёт клиенту.',
  'co.reg.sendTitle': 'Отправить напоминание?',
  'co.reg.sendWho': 'Клиент получит такое сообщение:',
  'co.reg.sendHint': 'Приветствие и строку про занятие добавляет приложение. Ваш текст — в «Настройки → Постоянные клиенты».',
  'co.reg.send': 'Отправить',
  'co.reg.errNoBot': '{name} не открывал бота — отправить некуда.',
  'co.reg.againAsk': 'Напоминание для {name} уходило меньше шести дней назад. Отправить ещё одно?',
  'co.reg.errNothing': 'Напоминать не о чем.',
  'co.reg.rowTitle': 'Постоянные клиенты',
  'co.reg.rowDays': 'предупреждать за {n} {days}',
  'co.reg.setLead': 'Напоминания получают только клиенты с ★: те, кого вы ждёте каждую неделю.',
  'co.reg.setTitle': 'Постоянные',
  'co.reg.setDays': 'Предупреждать, когда запись заканчивается',
  'co.reg.horizon': 'Постоянные записываются вперёд на',
  'co.reg.horizonHint': 'Остальные — на {n} нед. Если снять ★, уже сделанные записи останутся.',
  'co.reg.horizonSame': 'Как все',
  'co.reg.weeksChip': '{n} нед.',
  'co.reg.setDaysHint': 'За сколько дней до последнего записанного занятия',
  'co.reg.dayChip': '{n} дн',
  'co.reg.textAsIs': 'Изменённая строка уходит всем клиентам как есть, без перевода.',
  'co.reg.setReset': 'Вернуть стандартный',
  'co.reg.setSave': 'Сохранить текст',
  'co.reg.previewLine': 'Так увидит клиент. Свою строку можно изменить.',
  'co.sw.title': 'Заменить оплату',
  'co.sw.now': '{name} · {lesson} — сейчас {how}',
  'co.sw.inCash': '{amount} наличными',
  'co.sw.inCredits': '{n} {credits}',
  'co.sw.useFree': 'Взять свободный кредит',
  'co.sw.freeLeft': 'свободно: {n} {credits}',
  'co.sw.taught': 'проведено',
  'co.sw.booked': 'записано',
  'co.sw.becomesCash': 'станет оплатой наличными {price}',
  'co.sw.backToBalance': 'Вернуть кредит на баланс',
  'co.sw.backToBalanceSub': 'кредит снова свободен',
  'co.sw.givesTo': 'заберёт кредит, {amount} платить не нужно',
  'co.sw.confirm': 'Заменить оплату',
  'co.sw.prevFree': 'Свободный кредит оплатит занятие {lesson}. Больше ничего не изменится.',
  'co.sw.prevMove': 'Кредит с занятия {from} перейдёт на занятие {to}; {from} станет оплатой наличными {price}.',
  'co.sw.prevBack': 'Занятие {lesson} станет оплатой наличными {price}, а кредит вернётся на баланс.',
  'co.sw.prevHand': 'Кредит с занятия {lesson} перейдёт на занятие {to}; {lesson} станет оплатой наличными {price}.',
  'co.sw.errPaid': 'Это занятие уже оплачено. Ничего не изменилось.',
  'co.sw.errCancelled': 'Это занятие отменено.',
  'co.sw.errNoCredit': 'Свободных кредитов нет, и ни на одном занятии кредита тоже нет.',
  'co.sw.errNoCreditOnIt': 'Это занятие оплачено не кредитом.',
  'co.sw.errDonorEmpty': 'На том занятии кредита уже нет.',
  'co.sw.errDonorSmall': 'На том занятии кредита меньше, чем стоит это.',
  'co.sw.errReceiverHas': 'На том занятии уже есть кредит.',
  'co.sw.errOtherClient': 'Это занятие другого клиента.',
  'co.sw.errSame': 'Это то же самое занятие.',
  'co.sw.errGone': 'Занятия больше нет — откройте экран заново.',
  'co.sw.action': 'Заменить оплату',
  'co.sw.useCredit': 'Использовать кредит',
  'co.sw.warnLater': 'Внимание: после этого кредитов не хватит ещё на {n} {lessons} впереди — {amount} к оплате на занятии.',
  'co.sw.warnLaterNoPrice': 'Внимание: после этого кредитов не хватит ещё на {n} {lessons} впереди.',
  'co.sw.lessonsW': ['занятие', 'занятия', 'занятий'],
  'co.paid.failed': 'Не сохранено: {message}',
  'co.paid.badAmount': 'Введите сумму больше нуля.',
  'co.paid.needRate': 'Укажите цену занятия, прежде чем принимать больше его стоимости.',
  'co.paid.btn': 'Получено {amount}',
  'co.paid.onBalance': 'На счету: {n} {credits} · {amount}',
  'co.paid.onBalanceNoRate': 'На счету: {n} {credits}',
  'co.paid.fromBalance': 'Списать с баланса',
  'co.paid.fromBalanceDone': 'Списано {n} {credits} · осталось {left}',
  'co.paid.fromBalanceOwed': ' · к оплате наличными {cash}',
  'co.today.paid': 'Оплачено',
  'co.today.credit': 'Кредиты',
  // Срез 2: кредит списывается, когда занятие прошло.
  'co.today.creditTaken': 'Кредит списан',
  'co.today.notCharged': 'Не списано',
  'co.today.notChargedWhy': 'Кредитов на это занятие не было.',
  'cov.planned': 'Запланировано',
  'cov.covered': 'покрыто кредитами',
  'cov.due': 'нужна оплата',
  'cov.legacyMore1': 'Ещё одна записана раньше — кредит за неё уже списан',
  'cov.legacyMore': 'Ещё {n} записаны раньше — кредит за них уже списан',
  'cov.legacyOnly1': 'Одна записана раньше — кредит за неё уже списан',
  'cov.legacyOnly': '{n} записаны раньше — кредит за них уже списан',
  'cov.paidBy': 'Общий счёт · платит {name}',
  'cov.alsoFor': 'Общий счёт · платит и за: {names}',
  'pay.covered': 'покрыто',
  'pay.due': '{amount} на занятии',
  'pay.legacy': 'кредит уже списан',
  'pay.legacyPart1': '1 кредит уже списан',
  'pay.legacyPartN': '{n} {credits} уже списано',
  'me.onAccount': ['кредит на счету', 'кредита на счету', 'кредитов на счету'],
  'me.ahead': 'Записаны вперёд: {n}',
  'me.allCovered': 'все по абонементу',
  'me.coveredPart': '{w} по абонементу',
  'me.duePart1': 'одно оплачивается на занятии ({amount})',
  'me.duePartN': '{w} оплачиваются на занятиях ({amount})',
  'me.dueAll': 'оплачиваются на занятиях ({amount})',
  'book.notCovered': 'Это занятие не покрыто абонементом — {amount} на занятии',
  'book.notCoveredN': 'Не покрыты абонементом: {n} из {total} — {amount} на занятиях',
  'book.displaces': 'Тогда занятие {day} будет оплачиваться на занятии — {amount}',
  'book.displacesN': 'Тогда ещё {n} из уже записанных будут оплачиваться на занятиях — {amount}',
  'co.today.noReminders': ' · без напоминаний',
  'co.today.privateNote': '🔒 Личная заметка, видите только вы',
  'co.today.addPrivateNote': '🔒 Добавить личную заметку',
  'co.today.saveNote': 'Сохранить заметку',
  'co.today.noShowConfirm': 'Отметить как неявку? Занятие всё равно засчитывается — время было занято.',
  'co.today.paidConfirm': 'Отметить €{amount} как полученные?',
  'co.today.cancelTitle': 'Отменить занятие: {name}',
  'co.today.cancel': 'Отменить',
  'co.today.cancelBody': '{day} в {time}. Полный возврат, клиенту сообщат.',
  'co.today.cancelNoMessage': 'Сообщение не дойдёт (клиент не в боте) — позвоните.',
  'co.today.cancelDo': 'Отменить и сообщить',
  'co.today.sessionMore': 'Ещё: занятие {time}',
  'co.today.cancelSession': 'Отменить занятие',
  'co.today.cancelSessionTitle': 'Отменить занятие {time}',
  'co.today.cancelSessionBody': '{day}, {time}. Все в занятии — {names} — получат сообщение, оплата вернётся.',
  'co.today.cancelDayConfirm': 'Отменить занятий: {n} — {day}?\n\nВсем вернут оплату и сообщат причину. Это нельзя будет отменить.',
  'co.today.cancelDayDone': 'Отменено: {n}, возвращено на счёт: {r}.',
  'co.today.cancelDayBlocked': '\n\nДень закрыт, записаться заново нельзя.',
  'co.today.affected': 'Затронуто занятий: {n}',
  'co.today.unreachable': '{names}: сообщение не дойдёт — позвоните.',
  'co.today.why': 'Причина',
  'co.today.blockLabel': 'Закрыть день, чтобы нельзя было записаться',
  'co.today.cancelDayTitle': 'Отменить {day}',
  'co.today.reasonLabel': 'Что добавить (необязательно)',
  'co.today.blockDay': 'Выключите, если переносите на другой корт',
  'co.today.cancelling': 'Отменяем…',
  'co.today.cancelAll': 'Отменить {n} и сообщить всем',
  'co.reason.rain': 'Дождь',
  'co.reason.noCourt': 'Нет корта',
  'co.reason.illness': 'Болезнь',
  'co.reason.emergency': 'Срочное дело',
  'co.reason.other': 'Другое',
  'co.pkg.confirmTitle': 'Подтвердить оплату',
  'co.pkg.waiting': 'Ждут подтверждения оплаты: {n}',
  'co.pkg.packages': ['пакет', 'пакета', 'пакетов'],
  'co.pkg.onlyWhenPaid': 'Подтверждайте, только когда деньги у вас на руках.',
  'co.pkg.sessionsLabel': 'Кредитов',
  'co.pkg.received': 'Получено',
  'co.pkg.decline': 'Отклонить',
  'co.pkg.confirm': 'Подтвердить',
  'co.pkg.back': 'Назад',
  'co.pkg.perSession': '€{amount} за занятие',
  'co.pkg.sessions': '{n} {credits}',
  'co.pkg.granted': 'Готово. У клиента {name} теперь на счету: {n} {credits}.',
  'co.pkg.declineConfirm': 'Отклонить запрос? Списания не будет.',
  'co.rate.searchClient': 'Поиск',
  'co.add.title': 'Добавить в занятие {time}',
  'co.add.eachUses': 'С каждого спишется {n} {credits}: парное занятие стоит половину.',
  'co.add.nobody': 'Никого не найдено.',
  'co.add.confirm': 'Добавить {who} в занятие {time} за {price}?',
  'co.add.confirmCovered': 'Добавить {who} в занятие {time}? Оно уже оплачено — с нового ничего.',
  'co.add.do': 'Добавить',
  'co.rp.action': 'Заменить',
  'co.rp.title': 'Заменить: {name}',
  'co.rp.confirm': 'Заменить {old} → {new}?',
  'co.rp.oldLine': '{old}: ничего не списано.',
  'co.rp.samePrice': 'Место и цена те же: {price}. У остальных цена не меняется.',
  'co.rp.sameCovered': 'Место уже оплачено — с нового ничего. У остальных цена не меняется.',
  'co.rp.recent': '{name} добавлен(а) меньше 10 минут назад — скорее всего, не знает о записи.',
  'co.rp.do': 'Заменить',
  'co.rp.done': 'Готово: {new} вместо {old}.',
  'co.rp.doneCredits': ' {old}: вернули {n} {credits}.',
  'co.rs.title': 'Перенести занятие: {name}',
  'co.rs.hint': 'Занятие уже началось, и у {name} есть: {items}. Выберите нужного клиента — всё перейдёт к нему.',
  'co.rs.confirm': 'Перенести занятие {when} с {old} на {new}: {items}',
  'co.rs.confirmPlain': 'Перенести занятие {when} с {old} на {new}?',
  'co.rs.hintSolo': 'Выберите нужного клиента — вся запись перейдёт к нему: {items}.',
  'co.rs.hintPlain': 'Выберите нужного клиента — запись перейдёт к нему.',
  'co.rs.ratings': 'оценки',
  'co.rs.notes': 'заметки',
  'co.rs.payment': 'оплата {amount}',
  'co.rs.do': 'Перенести',
  'co.rs.done': 'Готово: занятие {when} теперь у {new}.',
  'co.rs.charged': ' С {new} списано {n} {credits}.',
  'co.add.credits': '{n} {credits}',
  'co.countdown.min': 'через {n} мин',
  'co.countdown.hMin': 'через {h} ч {m}',
  'co.countdown.h': 'через {h} ч',
  'co.rate.useCredits': 'Оплатить кредитами',
  'co.rate.cash': 'Наличные',
  'co.rate.alreadyPaid': 'Уже оплачено',
  'co.rate.added': 'Добавлено: {name}. Можно оценивать.',
  'co.rate.addedClient': 'Добавлено: {name}. Клиент получит подтверждение и напоминание.',
  'co.rate.noCredits': 'У {name} нет кредитов — добавьте с оплатой на занятии.',
  'co.rate.alreadyIn': 'Клиент уже записан на это занятие.',
  'co.rate.couldNotAdd': 'Не удалось добавить: {message}',
  'co.rate.drills': 'Бандеха в заднее стекло, 3 × 20',
  'co.rate.sharedNote': 'Сегодня спокойнее у сетки.',
  'co.rate.homework': '50 бандех в стенку',
  'co.rate.workingOn': 'Над чем работать дальше',
  'co.rate.title': 'Как прошло занятие: {name}?',
  'co.rate.dragHint': 'Передвигайте ползунок. Крайнее левое положение (0) — сегодня не отрабатывали.',
  'co.review.dragHint': 'Передвигайте ползунок. Крайнее левое положение (0) — в матче не проявилось.',
  'co.rate.ratedCount': 'Оценено {n} из {total}',
  'co.rate.noAxes': 'Навыки для оценки пока не заданы.',
  'co.rate.aboutLabel': 'Над чем работали',
  'co.rate.aboutHint': 'Навыки с оценкой выше 0 попадают сюда сами. Остальное добавьте вручную.',
  'co.rate.other': '+ другое',
  'co.set.titleAxes': 'Навыки',
  'co.axis.none': 'Навыков пока нет.',
  'co.sport.padel': 'Падел',
  'co.sport.tennis': 'Теннис',
  'co.sport.both': 'Оба',
  'co.cd.sportHint': 'Что клиенту предлагать при записи и как показывать его прогресс.',
  'co.cd.sportUnset': 'Ещё не задан: решит первое занятие.',
  'co.set.linkPadel': 'Ссылка для клиентов · Падел',
  'co.set.linkTennis': 'Ссылка для клиентов · Теннис',
  'co.set.sport': 'Вид спорта',
  'co.set.sportLocked': 'По этому типу уже есть занятия — вид спорта не меняется.',
  'co.set.tennisOn': 'Я провожу и занятия по теннису',
  'co.set.tennisHint': 'Добавит восемь теннисных навыков для оценки (Подача, Приём подачи, Форхенд…) и вид спорта у каждого типа занятия.',
  'co.set.tennisBusy': 'Сначала снимите теннисные типы занятий с продажи.',
  'co.axis.noRu': 'нет названия по-русски',
  'co.axis.guideAll': 'шкала описана',
  'co.axis.guideSome': 'шкала: {n} из {total}',
  'co.axis.newSkill': '+ Новый навык',
  'co.axis.new': 'Новый навык',
  'co.axis.newPlaceholder': 'Подача и приём, Игра у сетки…',
  'co.axis.add': 'Добавить',
  'co.axis.edit': 'Изменить навык',
  'co.axis.nameEn': 'Название по-английски',
  'co.axis.nameRu': 'Название по-русски',
  'co.axis.nameRuPlaceholder': 'Как вы называете это по-русски',
  'co.axis.suggest': 'Предложить',
  'co.axis.thinking': 'Думаю…',
  'co.axis.guides': 'Что значит каждый уровень',
  'co.axis.draft': 'Написать с AI',
  'co.axis.drafting': 'Пишу…',
  'co.axis.linePlaceholderEn': 'Одна строка, по-английски',
  'co.axis.linePlaceholderRu': 'Та же строка, по-русски',
  'co.axis.save': 'Сохранить',
  'co.axis.saving': 'Сохраняю…',
  'co.axis.cancel': 'Отмена',
  'co.axis.exists': 'Навык с таким названием уже есть.',
  'co.axis.failed': 'Не удалось сохранить навык.',
  'co.axis.aiOff': 'AI на сервере не настроен.',
  'co.axis.aiFailed': 'Не получилось. Попробуйте ещё раз.',
  'co.rate.w0': 'Не отрабатывали',
  'co.review.w0': 'В матче не проявилось',
  'co.rate.w1': 'Новичок',
  'co.rate.w3': 'Уверенный новичок',
  'co.rate.w5': 'Средний',
  'co.rate.w7': 'Уверенный средний',
  'co.rate.w9': 'Продвинутый',
  'co.rate.drillsLabel': 'Что делали, по одному упражнению в строке',
  'co.rate.workingOnLabel': 'Над чем работаем',
  'co.rate.clientSees': 'Это видит {name}. Очистите, когда закончите.',
  'co.rate.later': 'Позже',
  'co.gn.loading': 'Заметки к занятию',
  'co.gn.title': 'Занятие {time}',
  'co.gn.saving': 'Сохраняем…',
  'co.gn.saved': 'Сохранено · {time}',
  'co.gn.forEveryone': 'Общее для всех',
  'co.gn.homeworkAll': 'Домашнее задание · для всех',
  'co.gn.commonPh': 'Что касается всех. Или одна общая заметка — AI разделит её по игрокам',
  'co.gn.ai': '✨ Разделить с AI',
  'co.gn.aiWorking': 'Разделяю…',
  'co.gn.aiHint': 'Разделит «Для всех на занятии» на общую часть и личную для каждого — вы проверите перед сохранением.',
  'co.gn.aiDone': 'Разделено с AI — проверьте части ниже.',
  'co.gn.undo': 'Вернуть как было',
  'co.gn.aiOptedOut': 'AI недоступен: для заметок {names} AI отключён. Разделите вручную.',
  'co.gn.aiNotInBot': 'AI недоступен: {names} не в боте (добавлен вручную), такие заметки в AI не отправляются. Разделите вручную.',
  'co.gn.ratingsOf': 'Оценки · {name}',
  'co.gn.fromCoachFor': 'От тренера · {name}',
  'co.gn.notePh': 'Только то, что касается этого игрока',
  'co.gn.personalTask': 'Личное задание',
  'co.gn.addPersonalTask': '+ личное задание',
  'co.gn.finish': 'Завершить →',
  'co.gn.previewTitle': 'Что увидят игроки',
  'co.gn.back': '← Назад',
  'co.gn.confirm': 'Подтвердить ✅',
  'co.gn.stillOn': 'Занятие ещё идёт — игроки увидят в {time}',
  'co.gn.willGet': '{name} получит:',
  'co.gn.fromCoach': 'От тренера',
  'co.gn.noBot': 'Не в боте: сообщение не придёт, заметку увидит в приложении.',
  'co.ss.did': 'Что делали',
  'co.ss.common': 'Заметка для всех',
  'co.ss.commonPh': 'Увидят все игроки',
  'co.pn.common': 'Общее',
  'co.pn.commonPh': 'Что делали вместе — увидят оба',
  'co.pn.personal': 'Лично для {name}',
  'co.pn.personalPh': 'Увидит только {name}',
  'co.pn.homework': 'Домашнее задание',
  'co.pn.plan': 'План занятия и тема',
  'co.pn.goal': '+ Над чем работать дальше',
  'co.pn.preview': 'Что увидят игроки',
  'co.pn.save': 'Сохранить',
  'co.pn.saving': 'Сохраняем…',
  'co.pn.ai': '✨ Разделить с AI',
  'co.pn.aiHint': 'По желанию: напишите одну заметку в «Общее» — AI разделит её на общую часть и личные.',
  'co.pn.failedPlayer': 'Ничего не сохранилось: не получилось записать для {name}. Всё, что вы написали, осталось — попробуйте ещё раз.',
  'co.pn.failed': 'Ничего не сохранилось ({message}). Всё, что вы написали, осталось — попробуйте ещё раз.',
  'co.today.notesWillShow': 'Заметки сохранены · игроки увидят в {time}',
  'co.rate.saving': 'Сохраняем…',
  'co.rate.save': 'Сохранить',
  'co.rate.draftSaving': 'Сохраняем черновик…',
  'co.rate.draftSaved': 'Черновик сохранён · {name} пока ничего не видит',
  'co.rate.duringLesson': 'Занятие ещё идёт: после «Сохранить» {name} увидит в {time}',
  'co.rate.willShow': 'Сохранено · {name} увидит в {time}',
  'co.today.continueRate': 'Продолжить оценку',
  'co.today.editRate': 'Изменить оценку',
  'co.today.rated': 'Оценено ✓',
  'co.today.noteOnly': 'Заметка ✓',
  'co.today.chipNote': 'заметка',
  'co.today.chipHw': 'ДЗ',
  'co.today.rateDraft': 'Черновик оценки · ещё не сохранено',
  'co.today.rateWillShow': 'Оценка сохранена · игрок увидит в {time}',
  'co.rate.couldNotSave': 'Не удалось сохранить: {message}',
  'co.review.watch': 'Посмотреть матч',
  'co.review.title': '{name} · {result}{score}',
  'co.review.versus': 'соперник: {name}',
  'co.review.toReview': 'Матчей на разбор: {n}',
  'co.review.matches': ['матч', 'матча', 'матчей'],
  'co.review.paid': ' оплачено',
  'co.review.open': 'Разобрать',
  'co.review.worked': 'Что получилось',
  'co.review.toWorkOn': 'Над чем поработать',
  'co.review.saveReview': 'Сохранить разбор',
  'co.review.won': 'Победа',
  'co.review.lost': 'Поражение',
  'co.review.saveChanges': 'Сохранить изменения',
  'co.match.title': 'Матчи',
  'co.match.none': 'Матчей пока нет. Как только матч будет записан, он появится здесь.',
  'co.match.reviewed': 'Разобран {day}',
  'co.match.requested': 'Запрошен разбор',
  'co.match.notReviewed': 'Без разбора',
  'co.failed': 'Не получилось: {message}',
  'bill.title': 'Подписка',
  'bill.until': 'Подписка до {date}',
  'bill.noDate': 'Дата не задана',
  'bill.active': 'Оплачено',
  'bill.grace': 'Ждём оплату · ещё {n} дн.',
  'bill.overdue': 'Просрочено · {n} дн.',
  'bill.off': 'Закрыто',
  'bill.unbilled': 'Без оплаты',
  'bill.noPlan': 'Без тарифа',
  'bill.planMonthly': 'Месяц · {price}',
  'bill.planYearly': 'Год · {price}',
  'bill.planFounding': 'Основатель · {price} в месяц',
  'bill.howToPay': 'Как оплатить',
  'bill.noDetails': 'Реквизиты ещё не указаны. Напишите владельцу.',
  'bill.afterPay': 'После оплаты {owner} отметит её, и срок продлится.',
  'bill.overdueTitle': 'Подписка просрочена на {n} {days}',
  'bill.overdueBody': 'Оплатите {amount}, чтобы продолжить. Клиенты этого не видят и записываются как обычно.',
  'bill.offTitle': 'Доступ к PadelDesk закрыт',
  'bill.offBody': 'Клиенты не могут делать новые записи, уже записанные занятия остаются. Оплатите подписку, и доступ откроется, как только {owner} отметит оплату.',
  'bill.offAction': 'Аккаунт отключён: смотреть можно, менять — нет.',
  'bill.you': '(вы)',
  'bill.ownerRow': 'Владелец · без оплаты',
  'bill.detailsTitle': 'Реквизиты в напоминаниях',
  'bill.detailsPlaceholder': 'Revolut, IBAN, назначение…',
  'bill.detailsHint': 'Показываются как есть в напоминаниях бота и в «Как оплатить».',
  'bill.since': 'Тренер с {date}',
  'bill.plan': 'Тариф',
  'bill.row': "Тариф",
  'bill.lineTrial': "Пробный период до {date} · далее {price}",
  'bill.linePaid': "{plan} · {price} · оплачено до {date}",
  'bill.lineOverdue': "Просрочено с {date}",
  'bill.tTrial': "Пробный период до {date}",
  'bill.tPaid': "Оплачено до {date}",
  'bill.tOverdue': "Просрочено с {date}",
  'bill.subTrial': "Первый платёж {date} · {price}",
  'bill.subNext': "Следующий платёж {date} · {price}",
  'bill.trialChip': "Пробный период",
  'bill.perMonthP': "{amount}/мес",
  'bill.perYearP': "{amount}/год",
  'bill.nameMonthly': "Помесячный",
  'bill.nameYearly': "Годовой",
  'bill.nameFounding': "Основатель",
  'bill.sPlan': "Тариф",
  'bill.sPrice': "Цена",
  'bill.sNext': "Следующий платёж",
  'bill.sTrialUntil': "Пробный период до",
  'bill.sWaitingSince': "Просрочено с",
  'bill.sDetails': "Реквизиты для оплаты",
  'bill.iPaid': 'Оплата отправлена',
  'bill.thanks': "Спасибо, проверим и подтвердим",
  'bill.close': "Закрыть",
  'bill.bTrial': "Пробный период до {date}",
  'bill.bTrialBody': "Первый платёж {date} · {price}. Нажмите, чтобы узнать, как оплатить.",
  'bill.bPaid': "Оплачено до {date}",
  'bill.bPaidBody': "Следующий платёж {date} · {price}. Нажмите, чтобы узнать, как оплатить.",
  'bill.bOverdue': "Просрочено с {date}",
  'bill.bOverdueBody': "Клиенты этого не видят. Нажмите, чтобы узнать, как оплатить.",
  'bill.bOverdueCloses': "Без оплаты доступ закроется через {n} {days}. Нажмите, чтобы узнать, как оплатить.",
  'bill.bWaiting': "Ожидаем подтверждения",
  'bill.monthly': 'Месяц',
  'bill.yearly': 'Год',
  'bill.founding': 'Основатель',
  'bill.perMo': '{price}/мес',
  'bill.monthlyPrice': 'Цена в месяц',
  'bill.yearNote': 'Год стоит как десять месяцев: {year}.',
  'bill.paidUntil': 'Оплачено до',
  'bill.graceLine': 'Ждём оплату · {late} дн. после даты, осталось {left} дн.',
  'bill.overdueLine': 'Просрочено · {n} дн. · у тренера красный баннер',
  'bill.setDate': 'Изменить дату',
  'bill.markPaid': 'Отметить оплату',
  'bill.plusMonth': '+1 месяц · {amount}',
  'bill.plusYear': '+1 год · {amount}',
  'bill.becomes': 'Одна оплата продлит до {date} (от даты окончания, а не от сегодня).',
  'bill.confirmPaid': 'Отметить оплату {amount} от {name}? Новая дата — {date}; тренер получит сообщение в боте.',
  'bill.confirmOff': 'Закрыть доступ ({name})? Тренер ничего не сможет делать, клиенты не смогут делать новые записи. Уже записанные занятия и напоминания остаются.',
  'bill.confirmOn': 'Открыть доступ ({name}) снова?',
  'bill.history': 'История оплат',
  'bill.noPayments': 'Оплат пока нет.',
  'bill.to': 'до {date}',
  'book.closedTitle': 'Запись временно закрыта',
  'book.closedBody': 'Напишите тренеру, чтобы записаться. Ваши занятия сохранены.',
  'bill.days': ['день', 'дня', 'дней'],

  'co.pp.sideRight': 'Ревес · правая сторона',
  'co.pp.sideLeft': 'Драйв · левая сторона',
  'co.pp.sideBoth': 'Обе стороны',
  'co.pp.handRight': 'Правша',
  'co.pp.handLeft': 'Левша',
  'co.pp.lvl.beginner': 'Начинающий',
  'co.pp.lvl.improver': 'Продолжающий',
  'co.pp.lvl.intermediate': 'Средний',
  'co.pp.lvl.intermediatePlus': 'Средний+',
  'co.pp.lvl.advanced': 'Продвинутый',
  'co.pp.lvl.competition': 'Турнирный',
  'co.pp.lvl.pro': 'Профи',
  'co.pp.edit': 'Изменить',
  'co.pp.addSideHand': '+ Сторона и рука',
  'co.pp.playtomicRange': 'Уровни Playtomic — от 0 до 7.',
  'co.pp.right': 'Справа',
  'co.pp.reves': 'Ревес',
  'co.pp.left': 'Слева',
  'co.pp.drive': 'Драйв',
  'co.pp.both': 'Обе',
  'co.pp.either': 'Любая',
  'co.pp.level': 'Уровень',
  'co.pp.levelSetByYou': 'Уровень · задан вами',
  'co.pp.side': 'Сторона',
  'co.pp.hand': 'Рука',
  'co.pp.basedOn': 'Основа: {list}',
  'co.pp.goals': ['цель', 'цели', 'целей'],
  'co.pp.plans': ['план занятия', 'плана занятий', 'планов занятий'],
  'co.pp.requests': ['запрос игрока', 'запроса игрока', 'запросов игрока'],
  'co.pp.ratingsOn': 'оценки по {n} {skills}',
  'co.pp.skillsDat': ['навыку', 'навыкам', 'навыкам'],
  'co.pp.notes': ['заметка', 'заметки', 'заметок'],
  'co.pp.setLevel': 'Задать уровень',
  'co.pp.levelHint': 'От 0 до 7 — шкала, знакомая по Playtomic.',
  'co.pp.playtomicSays': ' В Playtomic — {n}.',
  'co.pp.levelWhy': 'Почему (необязательно, видите только вы)',
  'co.pp.levelWhyPlaceholder': 'Бандеха наконец ложится глубоко',
  'co.pp.levelKept': 'Все изменения сохраняются, поэтому видно, как уровень меняется со временем.',
  'co.pp.saveLevel': 'Сохранить уровень',
  'co.pp.levelFromTo': 'Уровень с {from} до {to}',
  'co.pp.filterAll': 'Все',
  'co.pp.filterGroup': 'Групповые',
  'co.pp.filterNotes': 'С заметками',
  'co.pp.groupOf': 'Группа: {n}',
  'co.pp.whereTime': 'На что уходит время',
  'co.pp.focusHint': 'Отмечайте, над чем работали, когда ставите оценку — и здесь будет видно, на что уходит время.',
  'co.pp.nothingYet': 'Пока ничего нет.',
  'co.pp.sessions': 'Занятий',
  'co.pp.onCourt': 'На корте',
  'co.pp.turnedUp': 'Посещаемость',
  'co.tbl.day': 'День',
  'co.tbl.time': 'Время',
  'co.tbl.session': 'Занятие',
  'co.tbl.rating': 'Оценка',
  'co.tbl.payment': 'Оплата',
  'co.tbl.rated': 'Оценено',
  'co.tbl.rate': 'Оценить',
  'co.tbl.missed': 'Пропуск',
  'co.tbl.came': 'Посещено {came} из {total}',
  'co.tbl.missedN': 'пропущено {n}',
  'co.pp.playersSee': 'Клиент это видит',
  'co.pp.onlyYouForNow': '🔒 Пока видите только вы',
  'co.pp.drillsPlaceholder': 'Бандеха в заднее стекло, 3 × 20\n2 в 1 у сетки со счёта 4-4',
  'co.pp.sharedPlaceholder': 'Сегодня спокойнее у сетки. Бандеху заканчивать выше.',
  'co.pp.homeworkPlaceholder': '50 бандех в стенку',
  'co.pp.enter': 'Ввод',
  'co.pp.aiNotSetUp': 'AI на сервере ещё не настроен.',
  'co.pp.aiNothing': 'Заметок и оценок по этому клиенту пока нет.',
  'co.pp.aiRefused': 'AI не смог разобрать эти заметки.',
  'co.pp.aiFailed': 'Сейчас не получилось. Попробуйте через минуту.',
  'co.pp.aiTitle': '✦ Разбор ваших заметок',
  'co.pp.aiBlurb': 'Прочитает всё, что вы записали об этом клиенте, вместе с оценками, и скажет, что повторяется.',
  'co.pp.aiNotYet': 'Когда наберётся несколько заметок или оценок, их можно будет прочитать разом.',
  'co.pp.aiReading': 'Читаем ваши заметки…',
  'co.pp.aiRead': 'Прочитать мои заметки',
  'co.pp.hideSources': 'Скрыть источники',
  'co.pp.showSources': 'Показать источники',
  'co.pp.saving': 'Сохраняем…',
  'co.pp.cancel': 'Отмена',
  'co.pp.couldNotSave': 'Не удалось сохранить: {message}',

  'co.set.menuMe': 'Я',
  'co.set.menuWork': 'Работа',
  'co.set.menuClients': 'Клиенты',
  'co.set.menuApp': 'Приложение',
  'co.set.rowProfile': 'Профиль',
  'co.set.rowLink': 'Ссылка для клиентов',
  'co.set.rowPrices': 'Цены и пакеты',
  'co.set.rowCourt': 'Корт',
  'co.set.rowClients': 'Новые клиенты и метки',
  'co.set.rowAi': 'AI и дневник',
  'co.set.rowCalendar': 'Мой календарь',
  'co.set.sumHoursNone': 'Часы не заданы',
  'co.set.sumLinkNone': 'Ещё никому не отправлена',
  'co.set.sumLinkSent': 'Отправлена клиентам',
  'co.set.sumCourtNone': 'Стоимость корта не указана',
  'co.set.sumAiNone': 'Навыки не выбраны',
  'co.set.sumCalendar': 'Apple · Google',
  'co.set.sumRules': 'Отмена за {cutoff} ч · запись за {days} дн.',
  'co.set.sumPeak': 'пик {price}',
  'co.set.noPrice': 'Цена не указана',
  'co.set.minutesN': '{n} мин',
  'co.set.creditsN': '{n} {sessions}',
  'co.set.sessionsW': ['занятие', 'занятия', 'занятий'],
  'co.set.daysN': '{n} {days}',
  'co.set.tagsN': '{n} {tags}',
  'co.set.tagsW': ['метка', 'метки', 'меток'],
  'co.set.axesN': '{n} {axes}',
  'co.set.axesW': ['навык', 'навыка', 'навыков'],
  'co.set.perHourN': '{n} €/час',
  'co.set.hBeforeN': 'за {n} ч',
  'co.set.pkgsN': '{n} {pkgs}',
  'co.set.pkgsW': ['пакет', 'пакета', 'пакетов'],
  'co.set.approveManual': 'Подтверждаю вручную',
  'co.set.approveAuto': 'Записываются сразу',
  'co.set.aiLead': 'Что вы оцениваете после занятия, ваши упражнения и вечерняя сводка.',
  'co.set.pricesLead': 'Что клиент видит при записи и что вы ему продаёте.',
  'co.set.hoursLead': 'Когда вас можно записать. Нажмите на интервал, чтобы изменить, «+» — второй интервал за день.',
  'co.set.dayOff': 'выходной',
  'co.set.addInterval': 'Добавить интервал',
  'co.set.interval': 'Интервал',
  'co.set.intervalHint': 'Клиенты видят слоты внутри него, если время не занято.',
  'co.set.from': 'С',
  'co.set.until': 'До',
  'co.set.copyMon': 'Пн–Пт как в понедельник',
  'co.set.copyMonConfirm': 'Поставить вторнику–пятнице часы понедельника?',
  'co.set.copyMonEmpty': 'Сначала задайте понедельник.',
  'co.set.newSession': 'Новое занятие',
  'co.set.addSession': '+ Добавить занятие',
  'co.set.newPackage': 'Новый пакет',
  'co.set.addPackage': '+ Добавить пакет',
  'co.set.chkLeft': 'Осталось {n} {steps}',
  'co.set.chkOf': '{done} из {total}',
  'co.set.stepsW': ['шаг', 'шага', 'шагов'],
  'co.set.chkHours': 'Рабочие часы',
  'co.set.chkPrice': 'Цена занятия',
  'co.set.chkCourt': 'Стоимость корта',
  'co.set.chkAddress': 'Адрес клуба',
  'co.set.chkLink': 'Отправить ссылку клиенту',
  'co.set.titleLanguage': 'Язык',
  'co.set.titleInfo': 'Инфо для клиентов',
  'co.info.hint': 'Это видят ваши клиенты в «Мой профиль» и получают в боте после одобрения.',
  'co.info.empty': 'Пока не заполнено — клиенты не видят «Инфо», и после одобрения карточка не приходит. Заполните хотя бы адрес или «Как найти».',
  'co.info.address': 'Адрес',
  'co.info.addressPh': 'Например: Padel Club, ул. …',
  'co.info.maps': 'Ссылка на Google Maps',
  'co.info.mapsPh': 'https://maps.app.goo.gl/…',
  'co.info.directions': 'Как найти',
  'co.info.directionsPh': 'Вход, номер корта, кого спросить',
  'co.info.notes': 'Что ещё знать (что взять, парковка…)',
  'co.info.notesPh': 'Ракетка, вода, обувь, парковка',
  'co.info.auto': 'Заполняется само',
  'co.info.autoFrom': 'из занятий и правил',
  'co.info.preview': 'Посмотреть как клиент',
  'co.info.hidePreview': 'Скрыть',
  'co.info.askSend': 'Отправить Инфо всем клиентам? Получат: {n}.',
  'co.info.sent': 'Отправляем {n} {clients}, понемногу.',
  'co.info.saved': 'Сохранено. Клиенты видят это в «Мой профиль».',
  'co.info.savedEmpty': 'Сохранено. Клиенты увидят «Инфо», когда будет заполнен адрес или «Как найти».',
  'co.info.badUrl': 'Ссылка на карту не похожа на ссылку (https://…), поэтому не сохранена.',
  'co.info.sendAll': 'Отправить обновлённое Инфо всем →',
  'co.info.reach': 'Получат {n} {clients} в боте.',
  'co.info.notInBot': ' Ещё {n} не запускали бота.',
  'co.info.history': 'Отправлено',
  'co.info.statusFilled': 'Заполнено',
  'co.info.statusEmpty': 'Не заполнено',
  'co.info.statusSent': 'отправлено {when}',
  'co.info.statusNotSent': 'ещё не отправлялось',
  'co.set.tagsCount': 'Метки · {n}',
  'co.set.axesCount': 'Навыки · {n}',
  'co.set.tagsStatus': 'Группы для клиентов',
  'co.set.axesStatus': 'Что оцениваете после занятия',
  'co.set.titleSetup': 'Клиенты и занятия',
  'co.set.done': 'Готово',
  'co.info.historyInfo': 'Инфо',
  'co.info.clientsW': ['клиент', 'клиента', 'клиентов'],
  'info.title': 'Инфо · {coach}',
  'info.close': 'Свернуть',
  'info.openMap': 'Открыть карту',
  'info.prices': 'Стоимость',
  'info.hour': '1 час',
  'info.where': 'Где тренируемся',
  'info.find': 'Как найти',
  'info.openMaps': 'Открыть в Google Maps',
  'info.always': 'всегда актуальны',
  'info.cancel': 'Отмена занятия',
  'info.cancelFree': 'Бесплатно — не позднее чем за {h} {hours}.',
  'info.cancelLate': 'Позже — занятие оплачивается полностью.',
  'info.hoursW': ['час', 'часа', 'часов'],
  'info.goodToKnow': 'Что ещё знать',
  'co.set.titleTags': 'Метки',
  'co.set.appLanguage': 'Язык приложения',
  'co.set.languageHint': 'Меняет это приложение и сообщения бота для вас.',
  'co.set.titlePrices': 'Цены занятий',
  'co.set.titlePackages': 'Пакеты',
  'co.set.titleRules': 'Правила записи',
  'co.set.titleCourts': 'Корты',
  'co.set.titleHours': 'Рабочие часы',
  'co.set.titlePeak': 'Пиковые тарифы корта',
  'co.set.titleCalendar': 'Ваш календарь',
  'co.set.subscribe': 'Подписаться в моём календаре',
  'co.set.subscribeHint': 'Календарь обновляется сам: новые записи появляются, прошедшие занятия и неявки хранятся два года. Отмены, в том числе поздние, из календаря удаляются.',
  'co.set.sendToCalendar': 'Отправить занятия в мой календарь',
  'co.set.sendToCalendarHint': 'Все ближайшие занятия — с кем и сколько получить наличными. Запустите снова, когда нужно обновить.',
  'co.set.setCourts': 'Указать, какие корты у вас есть',
  'co.set.onlyWithCourt': 'Продавать только часы, на которые есть корт',
  'co.set.onlyWithCourtHint': 'Не включайте, пока не заполните несколько недель, иначе клиенты не увидят свободного времени.',
  'co.set.priceNote': 'Новая цена действует только для новых записей. У кого есть пакет, остаются на своей цене.',
  'co.set.hoursUpdated': 'Часы обновлены',
  'co.set.everyDay': 'Каждый день',
  'co.set.dayOnly': 'только {day}',
  'co.set.courtCosts': 'Аренда корта',
  'co.set.perHour': '€ / час',
  'co.set.cancel': 'Отмена',
  'bill.overdueCloses': 'Оплатите {amount}, иначе через {n} {days} доступ закроется и клиенты не смогут записываться.',
  'biz.title': 'Бизнес',
  'biz.never': 'никогда',
  'biz.today': 'сегодня, {time}',
  'biz.yesterday': 'вчера',
  'biz.daysAgo': '{n} {days} назад',
  'biz.income': 'Доход с тарифов тренеров',
  'biz.incomeSub': 'в месяц · год считается как ÷12',
  'biz.paid': 'Оплачено',
  'biz.grace': 'Ждём оплату',
  'biz.overdue': 'Просрочено',
  'biz.closed': 'Закрыто',
  'biz.clientsAll': 'Клиентов у всех тренеров',
  'biz.activeAll': 'активных за 30 дней: {n}',
  'biz.coaches': 'Тренеры',
  'biz.urgentFirst': 'сначала требующие внимания',
  'biz.closesIn': 'закроется через {n} {days}',
  'biz.autoClosed': 'доступ закрыт автоматически',
  'biz.members': 'члены клуба',
  'biz.lastSeen': 'последний вход: {when}',
  'biz.membersSoon': 'Члены клуба: появятся с абонементами',
  'biz.closedAuto': 'Доступ закрыт автоматически {date}: 14 дней просрочки.',
  'biz.closedOwner': 'Доступ закрыт вами {date}.',
  'biz.open': 'Открыть доступ',
  'biz.openNote': 'Тренер снова сможет работать, клиенты — записываться. «+1 месяц» ниже тоже откроет доступ.',
  'biz.closesNotice': 'Доступ закроется автоматически через {n} {days}, если оплата не придёт.',
  'biz.close': 'Закрыть доступ',
  'biz.closeNote': 'Тренер ничего не сможет делать, клиенты тренера не смогут записаться («Запись временно закрыта»). Уже записанные занятия остаются.',
  'biz.upcomingTitle': 'Записанные занятия',
  'biz.upcomingNone': 'Предстоящих занятий нет.',
  'biz.upcomingNote': 'Только просмотр. Эти занятия остаются в силе, клиенты получат напоминания.',
  'biz.clientsW': ['клиент', 'клиента', 'клиентов'],
  'biz.activeW': ['активный', 'активных', 'активных'],
  'biz.sessionsW': ['занятие в этом месяце', 'занятия в этом месяце', 'занятий в этом месяце'],
  'co.hot.openTimes': 'Свободное время',
  'co.hot.hint': 'Нажмите на время, чтобы отметить 🔥',
  'co.hot.hintBlock': 'Нажмите на время, чтобы поставить блокировку. Нажмите ещё раз, чтобы снова открыть.',
  'co.hot.modeHot': '🔥 Горячее',
  'co.hot.modeBlock': '🔒 Блок',
  'co.hot.blocked': 'блокировка',
  'co.hot.title': '🔥 Горячее время',
  'co.hot.markQ': 'Отметить {time} как горячее',
  'co.hot.isMarked': '{time} отмечено 🔥',
  'co.hot.what': 'Игроки увидят 🔥 на этом времени во вкладке «Записаться». Цена та же, сообщений никто не получит.',
  'co.hot.whatOff': 'Игроки снова увидят его как обычное время.',
  'co.hot.mark': 'Отметить 🔥',
  'co.hot.unmark': 'Снять 🔥',
  'co.hot.notOpen': 'Это время уже не свободно.',
  'co.set.addRate': 'Добавить тариф',
  'co.set.addPeakShort': '+ Добавить',
  'co.set.bandTitle': 'Пиковый тариф',
  'co.set.remove': 'Убрать',
  'co.set.removeRate': 'Убрать этот тариф?',
  'co.set.endAfterStart': 'Конец должен быть позже начала.',
  'co.set.splitNote': 'Занятие на границе делится: с 09:30 до 10:30 при пике до 10:00 — полчаса по одному тарифу и полчаса по другому.',
  'co.set.saved': 'Сохранено.',
  'co.set.hoursCopied': 'Скопировано часов: {n}.',
  'co.set.close': 'Закрыть',
  'co.set.addBlock': 'Добавить интервал',
  'co.set.saveHours': 'Сохранить часы',
  'co.set.couldNotSave': 'Не удалось сохранить: {message}',
  'co.set.lastWeekEmpty': 'Прошлая неделя пустая.',
  'co.set.couldNotCopy': 'Не удалось скопировать: {message}',
  'co.set.copyLastWeek': 'Скопировать прошлую неделю',
  'co.set.saving': 'Сохраняем…',
  'co.set.saveDays': 'Сохранить дней: {n}',
  'co.set.savedShort': 'Сохранено',
  'co.set.confirmHours': 'Сохранить эти часы? На уже записанные занятия это не влияет.',
  'co.set.pricePerSession': 'Цена занятия',
  'co.set.length': 'Длительность',
  'co.set.name': 'Название',
  'co.set.sessions': 'Кредитов',
  'co.set.price': 'Цена',
  'co.set.validFor': 'Срок',
  'co.set.sold': ' · продано: {n}',
  'co.set.minNotice': 'Минимальный срок',
  'co.set.minNoticeHint': 'За сколько до начала ещё можно записаться.',
  'co.set.window': 'Окно записи',
  'co.set.windowHint': 'На сколько вперёд открыто ваше расписание.',
  'co.set.freeCancel': 'Бесплатная отмена',
  'co.set.freeCancelHint': 'Если отменить позже, занятие засчитывается.',
  'co.set.lastCall': 'Последнее предупреждение',
  'co.set.lastCallHint': 'Предупреждает, что бесплатная отмена заканчивается.',
  'co.set.groupReminders': 'Напоминания',
  'co.set.remCutoff': 'Утренние занятия — начало до',
  'co.set.remCutoffHint': 'От этого зависит, в какой день придёт напоминание.',
  'co.set.remEvening': 'Накануне вечером в',
  'co.set.remEveningHint': 'Для занятий до {time}.',
  'co.set.remDay': 'В день занятия в',
  'co.set.remDayHint': 'Для занятий с {time}.',
  'co.set.quietHours': 'С 21:00 до 09:00 клиентам ничего не приходит, кроме подтверждения записи, которую они только что сделали. Сообщение на это время придёт в 09:00 — или раньше, если занятие раньше.',
  'co.set.remInQuiet': 'Время напоминаний — с 09:00 до 20:59.',
  'co.set.remDayAfterCutoff': 'Напоминание в день занятия должно приходить раньше {time}.',
  'co.set.courtHire': 'Аренда корта',
  'co.set.courtHireHint': 'Обычная ставка. Пиковые часы — ниже.',
  'co.set.canInvite': 'Клиенты могут звать друга',
  'co.set.canInviteHint': 'Индивидуальное занятие может стать парным. Цена делится, а не удваивается.',
  'co.set.reviews': 'Разбор матчей по видео',
  'co.set.reviewsHint': 'Клиент присылает видео, вы пишете, что исправить.',
  'co.set.reviewPrice': 'Цена разбора',
  'co.set.each': '€ за разбор',
  'co.set.save': 'Сохранить',
  'co.set.minutes': 'мин',
  'co.set.days': 'дней',
  'co.set.hours': 'ч',
  'co.set.hBefore': 'ч до начала',
  'co.set.perSessionPrice': '€{amount} за занятие',
  'co.set.neverExpires': ' · без срока',
  'co.set.groupBooking': 'Запись',
  'co.set.groupCancellations': 'Отмены',
  'co.set.groupCourt': 'Корт',
  'co.set.groupExtras': 'Дополнительно',
  'co.set.freeToCancel': 'Бесплатная отмена',
  'co.set.lastHours': 'Последние {n} ч · занятие засчитывается',
  'co.set.windowStory': 'Предупреждение за {warn} ч · занятие',
  'co.set.hoursNote': 'Снятие блокировки убирает это время из новых записей. Уже сделанные записи не отменяются.',
  'co.set.lastCallTooLate': 'Предупреждение должно приходить раньше срока, о котором предупреждает. Сделайте больше {n} ч.',
  'co.day.sunday': 'воскресенье',
  'co.day.monday': 'понедельник',
  'co.day.tuesday': 'вторник',
  'co.day.wednesday': 'среда',
  'co.day.thursday': 'четверг',
  'co.day.friday': 'пятница',
  'co.day.saturday': 'суббота',

  'pm.label': 'Как заплатили',
  'pm.cash': 'Наличные',
  'pm.revolut': 'Revolut',
  'pm.revolutHint': 'и карта',
  'pm.bank': 'Банк',
  'pm.bankHint': 'перевод',
  'pm.none': 'Не указано',
  'pm.noneHint': 'записаны до того, как появился способ оплаты',
  'pm.breakdown': 'Как получено',
  'pm.payments': '{n} {payments}',
  'pm.paymentsW': ['оплата', 'оплаты', 'оплат'],
  'pm.with': '{action} · {method}',
  'co.money.day': 'День',
  'co.money.week': 'Неделя',
  'co.money.month': 'Месяц',
  'co.money.year': 'Год',
  'co.money.today': 'Сегодня',
  'co.money.thisWeek': 'Эта неделя',
  'co.money.thisMonth': 'Этот месяц',
  'co.money.thisYear': 'Этот год',
  'co.money.yesterday': 'вчера',
  'co.money.lastWeek': 'прошлой неделей',
  'co.money.lastMonth': 'прошлым месяцем',
  'co.money.lastYear': 'прошлым годом',
  'co.money.all': 'Всё',
  'co.money.sinceStart': 'С {date}',
  'co.money.lineNet': 'После аренды корта',
  'co.money.lineEarned': 'Заработано',
  'co.money.lineIn': 'Получено',
  'co.money.prevKey.day': 'Вчера',
  'co.money.prevKey.week': 'Прошлая неделя',
  'co.money.prevKey.month': 'Прошлый месяц',
  'co.money.prevKey.year': 'Прошлый год',
  'co.money.vsPoint': '{change}% к {date}',
  'co.money.vsYesterday': '{change}% к {time} вчера',
  'co.money.weekOf': 'Неделя с {date}',
  'co.money.afterCourt': ' · ваше после аренды корта',
  'co.money.sessionsCount': 'занятий: {n}',
  'co.money.earnedMinusCourt': ' · заработано €{earned} − корты €{court}',
  'co.money.vs': ' · в сравнении с {period}',
  'co.money.keyEarned': 'Заработано: занятия проведены',
  'co.money.keyReceived': 'Получено: деньги на руках',
  'co.money.toCollect': 'К получению',
  'co.money.unpaidSessions': 'неоплаченных занятий: {n}',
  'co.money.allPaidUp': 'Всё оплачено',
  'co.money.paidInAdvance': 'Оплачено вперёд',
  'co.money.stillToTeach': 'ещё провести занятий: {n}',
  'co.money.allTime': 'За всё время',
  'co.money.calendar': 'Календарь',
  'co.money.close': 'Закрыть',
  'co.money.nobodyOwes': 'Никто ничего не должен.',
  'co.money.nothingBooked': 'Записей нет.',
  'co.money.projected': 'Прогноз — если все придут и оплатят',
  'co.money.whatHappened': 'Как прошло',
  'co.money.expected': 'Ожидается',
  'co.money.earned': 'Заработано',
  'co.money.wouldKeep': 'Останется',
  'co.money.yours': 'Ваше',
  'co.money.free': 'свободно',
  'co.money.paid': 'оплачено',
  'co.money.credit': 'кредит',
  'co.money.alsoCovers': ' · также платит за: {names}',
  'co.money.doneOf': 'проведено {done} из {total} · оплачено €{paid}',
  'co.money.bookedAhead': ' · записано вперёд: {n}',
  'co.money.unbooked': ' · без записи: {n}',
  'co.money.oldest': ' · самый давний {n} {days} назад',
  'co.money.gotAmount': 'Получено €{amount} · {day}',

  'co.cs.note': 'Добавить пояснение (видно клиенту)',
  'co.cs.cancelling': 'Отменяем…',
  'co.cal.earned': 'Заработано · занятий: {n}',
  'co.cal.stillToCome': 'Впереди · записей: {n}',
  'co.cal.courtHire': 'Аренда корта',
  'cb.title': 'Счёт за корт',
  'cb.entrySub': 'Занятия на корте за неделю или месяц',
  'cb.byWeek': 'Неделя', 'cb.byMonth': 'Месяц',
  'cb.week': 'Неделя {range}',
  'cb.none': 'За этот период занятий на корте нет.',
  'cb.noShow': 'неявка',
  'cb.min': '{n} мин', 'cb.hours': '{n} ч',
  'cb.onCourt': 'Часов на корте',
  'cb.toPay': 'Итого к оплате',
  'cb.send': 'Отправить себе в Telegram',
  'cb.sent': 'Отправлено — счёт в чате с ботом, его можно переслать клубу.',
  'cb.carry': 'Остаток на начало недели',
  'cb.copy': 'Копировать для таблицы',
  'cb.copyHint': 'Вставить в Google Sheets — строки как в таблице клуба',
  'cb.copied': 'Скопировано — вставьте в таблицу.',
  'cb.file': 'Прислать файл в чат',
  'cb.fileHint': '{name} — бот пришлёт его вам',
  'cb.fileSent': 'Бот пришлёт {name} в чат в течение минуты.',
  'cb.rateWarn': 'Тарифы не клубные 20 / 30 € ({rates}). A — настоящая стоимость, B и C — часы, поэтому A ≠ 30 × C + 20 × B.',
  'co.set.week1': 'Неделя 1 начинается',
  'co.set.week1Hint': 'Для «WEEK nn» в выгрузке счёта за корт. Пусто — номер недели по ISO.',
  'co.set.week1Norm': 'Недели — с понедельника по воскресенье: считаем с понедельника {date}.',
  'co.set.week1Clear': 'Очистить',
  'co.cal.yours': 'Ваше',
  'co.cal.courtToCome': 'Аренда впереди',
  'co.cal.ifAllTurnUp': 'Если все придут',
  'co.sell.title': 'Пакет для клиента: {name}',
  'co.sell.eachPrice': '{n} {credits} · по €{each}',
  'co.sell.somethingElse': 'Другое',
  'co.sell.sessionsLabel': 'Кредитов',
  'co.sell.theyPaid': 'Оплачено',
  'co.sell.received': 'Сколько получено, €',
  'co.sell.receivedHint': 'Пусто — кредиты без оплаты, деньги не считаются',
  'co.sell.receivedLine': 'Получено: {amount} · {method}',
  'co.sell.aSession': '€{amount} за занятие',
  'co.sell.addSessions': 'Начислить кредиты',
  'co.sell.howMany': 'Сколько кредитов?',
  'co.sell.howMuch': 'Сколько оплачено?',
  'co.sell.confirmCustom': 'Начислить {n} {credits} за €{paid}?\n\nЭто €{each} за занятие. Подтверждайте только после получения денег.',
  'co.sell.confirmPackage': 'Начислить {n} {credits} за €{price}?\n\nПодтверждайте только после получения денег.',
  'co.sell.done': 'Готово. Теперь на счету: {n}.',
  'co.sell.failed': 'Не получилось: {message}',
  'co.sell.expiresIn': ' · сгорает через {n} {days}',
  'co.sell.noExpiry': ' · без срока',
  'co.sell.backToPackages': 'Назад к пакетам',
  'co.off.title': 'Закрыть время',
  'co.off.upcoming': 'Ближайшие блокировки',
  'co.off.from': 'С',
  'co.off.until': 'До',
  'co.off.nothing': 'Ничего не закрыто. Действуют обычные часы.',
  'co.off.defaultReason': 'Перерыв',
  'co.off.remove': 'Снять',
  'co.off.clashList': '{name} — {time}',
  'co.off.pickRange': 'Выберите начало и конец.',
  'co.off.blockedWithClashes': 'Время закрыто, но записей в нём уже есть: {n}.',
  'co.off.cancelThem': '\n\nОтмените их на вкладке «Календарь», если не сможете.',
  'co.off.blockFailed': 'Не удалось закрыть время: {message}',
  'co.off.removeBlock': 'Снять блокировку и снова открыть это время?',
  'co.off.reason': 'Причина (необязательно)',
  'co.off.block': 'Закрыть это время',

  'co.cd.status.attended': 'Присутствие',
  'co.cd.status.noShow': 'Неявка',
  'co.cd.status.cancelled': 'Отменено',
  'co.cd.status.waitlisted': 'Очередь',
  'co.cd.creditsMoved': '\n\nОбновлено неоплаченных записей: {n}. Уже оплаченное не тронуто.',
  'co.cd.bookings': ['запись', 'записи', 'записей'],
  'co.cd.gifted': 'Подарено. Клиенту придёт сообщение, что занятие за ваш счёт.',
  'co.cd.bookedFor': 'Записано. Клиент получит подтверждение и напоминания.',
  'co.cd.couldNotBook': 'Не удалось записать: {message}',
  'co.cd.useCredits': 'Оплатить кредитами',
  'co.cd.payPerSession': 'Оплата за занятие',
  'co.cd.giftWhy': 'Повод? (необязательно — «с днём рождения», «за рекомендацию»)',
  'co.cd.booking': 'Записываем…',
  'co.cd.bookAt': 'Записать на {time}',
  'co.cd.pickTime': 'Выберите время',
  'co.cd.outside': 'вне рабочих часов',
  'co.cd.outsideHint': 'Светлые — вне рабочих часов, но записать можно.',
  'co.cd.outsideChosen': '{time} — вне рабочих часов.',
  'co.cd.once': 'Один раз',
  'co.cd.weekly': 'Каждую неделю',
  'co.cd.weeksN': 'Недель: {n}',
  'co.cd.untilDate': 'до даты',
  'co.cd.takenSkip': 'занято — пропустим',
  'co.cd.seriesPay': 'Каждое занятие оплачивается так же: кредитами, пока они есть, потом наличными.',
  'co.cd.bookN': 'Записать {n} {sessions}',
  'co.cd.seriesDone': 'Записано: {n} {sessions}.',
  'co.cd.seriesSkipped': ' Пропущено (занято): {n}.',
  'co.cd.toldThem': '\n\nКлиенту сообщили.',
  'co.cd.timeTaken': 'Это время занято во все даты. Выберите другое.',
  'co.cd.couldNotMove': 'Не удалось перенести: {message}',
  'co.cd.stale': 'Занятие изменилось, пока экран был открыт. Обновили — попробуйте ещё раз.',
  'wk.day': 'День',
  'wk.week': 'Неделя',
  'wk.title': 'Неделя {range}',
  'wk.toCollect': 'к получению {amount}',
  'wk.today': 'Сегодня',
  'wk.prev': 'Предыдущая неделя',
  'wk.next': 'Следующая неделя',
  'wk.group': 'Группа\u00a0· {n}',
  'wk.hint': 'Нажмите на занятие, чтобы открыть его, на пустое время — чтобы записать. Удерживайте занятие, чтобы перетащить на другое время.',
  'wk.bookWho': 'Записать на {when}: кого?',
  'co.cd.moveWholeSeries': 'Перенести всю серию',
  'co.cd.wholeSeries': 'Все ближайшие занятия серии, не только это',
  'co.cd.moving': 'Переносим…',
  'co.cd.moveTo': 'Перенести на {time}',
  'co.cd.saved': 'Сохранено.',
  'co.cd.cleared': 'Очищено.',
  'co.cd.couldNotSave': 'Не удалось сохранить: {message}',
  'co.cd.couldNot': 'Не получилось: {message}',
  'co.cd.couldNotDo': 'Не получилось: {message}',
  'co.cd.saving': 'Сохраняем…',
  'co.cd.save': 'Сохранить',
  'co.cd.payerTaken': 'За этого клиента уже платит другой клиент.',
  'co.cd.standardRate': 'Теперь клиент платит по стандартной цене.',
  'co.cd.ratesTitle': 'Личные цены',
  'co.cd.payingTitle': 'Оплата',
  'co.cd.standard': 'обычная €{amount}',
  'co.cd.useStandard': 'Вернуть обычную цену',
  'co.cd.savePrice': 'Сохранить цену',
  'co.cd.ratesNote': 'Оставьте пустым — будет обычная цена. Цена, заданная здесь, не меняется при повышении обычных цен и никогда не меняет уже сделанную запись.',
  'co.cd.someoneElsePays': 'За клиента платит кто-то другой',
  'co.cd.minutes': '{n} мин',
  'co.cd.credits': '{n} {credits}',
  'co.cd.agoToday': 'сегодня',
  'co.cd.agoYesterday': 'вчера',
  'co.cd.agoDays': '{n} дн. назад',
  'co.cd.agoWeeks': '{n} нед. назад',
  'co.cd.agoMonths': '{n} мес. назад',
  'co.cd.ownRate': 'Клиент платит €{price} за {minutes} мин.',
  'co.cd.search': 'Поиск',
  'co.cd.cancel': 'Отмена',
  'co.cd.removePkgTitle': 'Убрать этот пакет у клиента?\n\n',
  'co.cd.removePkgUsed': 'Использованные занятия остаются — они уже прошли: {n}.',
  'co.cd.removePkgMoney': '\n\nВопрос денег решите с клиентом отдельно.',
  'co.cd.switchTitle': 'Перевести ближайшие неоплаченные записи на оплату кредитами?\n\n',
  'co.cd.switchBody': 'Записи останутся как есть — изменится только способ оплаты.',
  'co.cd.switchNone': 'Неоплаченных ближайших записей нет.',
  'co.cd.workingOnSaved': 'Сохранено. Клиент это увидит и получит сообщение.',
  'co.cd.blockTitle': 'Заблокировать клиента?\n\nКлиент перестанет видеть ваше расписание и не сможет записаться. Ближайшие записи отменятся, кредиты вернутся на счёт. История сохранится.',
  'co.cd.unblockTitle': 'Разблокировать клиента? Записываться снова будет можно.',
  'co.cd.blockedDone': 'Заблокировано. Отменено ближайших занятий: {n}.',
  'co.cd.topUpTitle': 'Сколько получено от клиента?',
  'co.cd.topUpAmount': 'Введите сумму.',
  'co.cd.topUpDone': '€{amount} по €{rate} за занятие — это {n} {credits}. На счету теперь: {balance}.',
  'co.cd.goalSince': ' · С {day}',
  'co.cd.goalTitle': '🎯 РАБОТАЕМ НАД',
  'co.cd.topUpPrompt': 'Сколько получено от клиента?',
  'co.cd.botLang': 'Язык сообщений',
  'co.cd.botLangDefault': 'По умолчанию',
  'co.cd.botLangRu': 'Русский',
  'co.cd.botLangEn': 'English',
  'co.cd.botLangHint': 'На каком языке бот пишет этому клиенту. По умолчанию — как сейчас: на языке его приложения. Само приложение остаётся на языке, который выбрал клиент.',
  'co.cd.goalPlaceholder': 'Подача, контроль мяча на бэкхенде, игра у сетки под давлением',
  'co.cd.goalSave': 'Сохранить и сообщить клиенту',
  'co.cd.goalClear': 'Очистить',
  'co.cd.goalMessage': 'Игрок получит сообщение, когда цель изменится.',
  'co.cd.goalDone': 'Готово',
  'co.cd.goalSessions': 'Уже {n} {sessions} над этим',
  'co.cd.goalChange': 'Изменить',
  'co.cd.goalFinished': 'Цель достигнута',
  'co.cd.standingNotes': 'Постоянные заметки. Личные, видите только вы.',
  'co.cd.notesPlaceholder': 'Травмы, цели, кто порекомендовал…',

  'co.profile.back': '‹ Клиенты',
  'co.profile.backCalendar': '‹ Календарь',
  'co.profile.noUsername': 'без имени пользователя',
  'co.profile.noMessages': 'Сообщение не дойдёт',
  'co.profile.book': 'Записать',
  'co.profile.addCredit': 'Кредиты',
  'co.profile.message': 'Написать',
  'co.profile.tabSessions': 'Занятия',
  'co.profile.tabProgress': 'Прогресс',
  'co.profile.tabNotes': 'Дневник',
  'co.profile.tabSettings': 'Параметры',
  'co.start.title': 'Начальные оценки',
  'co.start.explain': 'Отправная точка игрока, от которой считается прогресс.',
  'co.start.setOn': 'Заданы {date}',
  'co.start.set': 'Задать',
  'co.profile.sessions': 'Занятий',
  'co.profile.turnedUp': 'Посещаемость',
  'co.profile.credits': 'На счету',
  'co.profile.since': 'Первое занятие: {day}',
  'co.profile.never': 'Занятий ещё не было',
  'co.profile.lastSession': ' · последнее занятие {ago}',
  'co.profile.upcoming': 'Ближайшие',
  'co.profile.past': 'Прошедшие',
  'co.profile.bookFor': 'Записать клиента: {name}',
  'co.profile.cancelAllUpcoming': 'Отменить все ближайшие ({n})',
  'co.profile.nothingAhead': 'Впереди записей нет.',
  'co.profile.noHistory': 'Истории пока нет.',
  'co.profile.owed': '€{amount} за уже проведённые занятия.',
  'co.profile.packages': 'Пакеты',
  'co.profile.pkgLeft': 'осталось {left} из {total}',
  'co.profile.pkgExpires': 'Действует ещё {n} {days}',
  'co.profile.pkgExpired': 'Срок истёк',
  'co.profile.pkgNoExpiry': 'Без срока',
  'co.profile.pkgBought': 'Куплен {day} · оплачено €{amount}',
  'co.profile.pkgMore': 'Ещё про этот пакет',
  'co.profile.pkgRemove': 'Убрать пакет',
  'co.profile.sellPackage': 'Продать пакет',
  'co.profile.move': 'Перенести',
  'co.profile.cancel': 'Отменить',
  'co.profile.series': 'Еженедельная серия',
  'co.profile.rated': 'оценено',
  'co.profile.recentSessions': 'Последние занятия',
  'co.profile.allSessions': 'Все занятия',
  'co.profile.noSessions': 'Занятий пока нет.',
  'co.profile.notRated': 'без оценки',
  'co.profile.paid': '€{amount} оплачено',
  'co.profile.owedOne': '€{amount} к оплате',
  'co.profile.plusCredits': ' + {n} кр.',
  'co.pp.crShort': 'кр.',
  'co.profile.unpaid': '€{amount} не оплачено',
  'co.profile.creditsUsed': '{n} {credits}',
  'co.profile.settingsBooking': 'Запись',
  'co.profile.halfHour': 'Занятия с получаса',
  'co.profile.halfHourHint': 'Можно записаться и на :30, не только на целый час. Большинству лучше не включать — остаются дыры, в которые ничего не помещается.',
  'co.profile.payWithCredits': 'Оплатить будущие записи кредитами',
  'co.profile.access': 'Доступ',
  'co.profile.block': 'Заблокировать клиента',
  'co.profile.unblock': 'Разблокировать клиента',
  'co.profile.blockHint': 'Перестанет видеть ваше расписание. История сохранится.',
  'co.profile.blocked': 'Заблокирован — не видит расписание и не может записаться.',
  'co.profile.privateNote': 'Всё здесь — только между вами и клиентом. Клиенту об изменениях не сообщают.',
  'co.profile.editName': 'Изменить имя',
  'co.profile.nicknameHint': 'Очистите, чтобы вернуть «{name}». Видите только вы.',
  'co.profile.save': 'Сохранить',
  'co.profile.cancelEdit': 'Отмена',
  'co.profile.cancelTitle': 'Отменить занятие',
  'co.profile.cancelBody': '{day} в {time}. Полный возврат, клиенту сообщат.',
  'co.profile.cancelAllTitle': 'Отменить все занятия ({n})',
  'co.profile.cancelAllBody': 'Все ближайшие занятия. Оплата вернётся, клиенту сообщат. Это нельзя будет отменить.',
  'co.profile.cancelAllConfirm': 'Отменить все ({n})',
  'co.profile.cancelDone': 'Отменено: {n}, возвращено на счёт: {r}.',

  'progress.title': 'Прогресс',
  'progress.focusNext': 'Следующее, над чем стоит поработать: {skill}.',
  'progress.noRatings': 'Оценок пока нет.',
  'progress.now': 'Сейчас',
  'progress.lastMonth': 'Прошлый месяц',
  'progress.matches': 'Матчи',
  'progress.upTo': '{skill}: рост до {score}',

  'match.title': 'Матчи',
  'match.log': '+ Записать матч',
  'match.explain': 'Записывайте матчи, и тренер увидит, как тренировки работают под давлением.',
  'match.won': 'Победа',
  'match.lost': 'Поражение',
  'match.save': 'Сохранить матч',
  'match.saving': 'Сохраняем…',
  'match.askReview': 'Попросить тренера разобрать матч',
  'match.formTitle': 'Записать матч',
  'match.formSub': 'Запишите, пока свежо в памяти.',
  'match.needResult': 'Вы выиграли или проиграли?',
  'match.needLink': 'Добавьте ссылку на видео для разбора.',
  'match.loggedReview': 'Записано. Тренер посмотрит и разберёт — €{fee}, оплата на следующем занятии.',
  'match.logged': 'Записано.',
  'match.saved': 'Сохранено.',
  'match.edit': 'Изменить',
  'match.editTitle': 'Изменить матч',
  'match.editSub': 'Можно менять, пока тренер не сделал разбор.',
  'match.saveEdit': 'Сохранить изменения',
  'match.saveFailed': 'Не удалось сохранить: {message}',
  'match.date': 'Дата',
  'match.score': 'Счёт',
  'match.partner': 'Ваш партнёр',
  'match.against': 'Соперники',
  'match.feel': 'Как ощущалось?',
  'match.feelHint': 'Волнение, что изменилось во втором сете, что вы заметили.',
  'match.reviewBlurb': 'Тренер посмотрит видео и напишет, что сработало и что исправить.',
  'match.pasteLink': 'Вставьте ссылку на видео',
  'match.namePh': 'Имя',
  'match.namesPh': 'Имена',
  'match.cancel': 'Отмена',
  'match.saveFee': 'Сохранить · €{fee}',
  'match.whyLead': 'Это не просто статистика.',
  'match.whyBody': ' Тренер видит, как вы играете в настоящих матчах, где тренировки срабатывают, а где нет, и строит следующие занятия вокруг этого.',
  'match.vs': 'соперник: {name}',
  'match.with': ' · партнёр: {name}',
  'match.whatWorked': 'Что сработало:',
  'match.toWorkOn': 'Над чем поработать:',
  'match.reviewWaiting': 'Разбор запрошен · ждём тренера',
  'match.youWrote': 'Ваша запись:',
  'match.coachRated': 'Оценки тренера',

  'invite.title': 'Позвать друга',
  'invite.username': '@username друга в Telegram',
  'invite.whoPays': 'Кто платит',
  'invite.coverBoth': 'Я плачу за обоих',
  'invite.theyPayNothing': 'Ничего не платит',
  'invite.theyPay': 'Платит за себя',
  'invite.usualRate': 'По своему тарифу',
  'invite.send': 'Отправить приглашение',
  'invite.sending': 'Отправляем…',
  'invite.share': 'Поделиться в Telegram',
  'invite.ready': 'Приглашение готово',
  'invite.copy': 'Копировать',
  'invite.usernamePh': '@username',
  'invite.done': 'Готово',
  'invite.copied': 'Скопировано',
  'invite.tapToCopy': 'Нажмите, чтобы скопировать. Одна ссылка на один раз, действует до начала занятия.',
  'invite.full': 'На это занятие нет мест.',
  'invite.started': 'Занятие уже началось.',
  'invite.notEnabled': 'У тренера эта возможность пока не включена.',
  'invite.tooMany': 'У вас уже три приглашения на это занятие.',
  'invite.failed': 'Не удалось отправить приглашение.',

  'book.maxPicks': 'Это уже {n} занятий. Запишитесь на них, потом выберите ещё.',
  'book.btnWeeks': 'Записаться на {n} {weeks} · {total}',
  'book.btnOne': 'Записаться · {total}',
  'book.btnMany': 'Записаться на {n} {sessions} · {total}',
  'book.creditTotal': ['{n} кредит', '{n} кредита', '{n} кредитов'],
  'book.nLeft': 'осталось {n}',
  'book.payPerSession': 'Оплата за занятие',
  'book.yourRate': ' · ваш тариф',
  'book.sessionLength': 'Длительность',
  'book.minShort': '{n} мин',
  'book.picked': ['занятие выбрано', 'занятия выбрано', 'занятий выбрано'],
  'book.clear': 'Очистить',
  'book.trainEveryWeek': 'Заниматься каждую неделю?',
  'book.justOnce': 'Только один раз',
  'book.everyWeek': 'Каждую неделю × {n}',
  'book.everyWeekShort': 'каждую неделю × {n} · ',
  'book.hi': 'Привет, {name}',
  'book.nextUp': 'Следующее занятие',
  'book.nothingYet': 'Пока ничего не записано. Выберите время, и тренер будет ждать.',
  'book.morning': 'Утро',
  'book.afternoon': 'День',
  'book.evening': 'Вечер',
  'book.tip': 'Можно выбрать сразу несколько занятий.',
  'book.payCredits': 'Кредиты',
  'book.payCash': 'За занятие',
  'book.seatsShort': 'свободно {n}',
  'book.classFullShort': 'мест нет',
  'book.doneOne': 'Записано. €{amount} к оплате на занятии.',
  'book.doneSplit': 'Записано. К оплате на занятии: {cash} € ({covered} € уже на счету).',
  'book.splitLine': 'К оплате на занятии: {cash} € ({covered} € уже на счету)',
  'book.doneOneCredits': 'Записано. Осталось {n} {credits}.',
  'book.doneAll': 'Записано всё: {n}.',
  'book.doneSome': 'Записано {n} из {total}.',
  'book.doneNone': 'Ничего не удалось записать.',
  'book.payEach': ' Оплата на каждом занятии.',
  'book.creditsLeftSuffix': ' Осталось {n} {credits}.',
  'book.classFull': 'На «{title}» мест нет. Встать в очередь? Оплата только если место освободится.',
  'book.joinClass': 'Записаться на «{title}» в {time}? Стоимость: {n} {credits}.',
  'book.waitlisted': 'Ваш номер в очереди: {n}. Мы напишем, если место освободится.',
  'book.seeYouThere': 'Записано. До встречи.',
  'book.credit': ['кредит', 'кредита', 'кредитов'],
  'book.session': ['занятие', 'занятия', 'занятий'],

  'err.slotTaken': 'Это время заняли за мгновение до вас. Выберите другое.',
  'err.slotGone': 'Это время уже недоступно. Выберите другое.',
  'err.noCredits': 'Кредиты закончились. Запишитесь с оплатой на занятии (€{price}) или купите пакет.',
  'err.classFull': 'Мест на это занятие больше нет.',
  'err.noCreditsPlain': 'Кредиты закончились.',
  'err.alreadyInClass': 'Вы уже записаны на это занятие.',
  'err.tooSoon': 'Записаться можно не позднее чем за {hours} ч.',
  'err.tooFar': 'Запись открыта на {days} дней вперёд.',
  'err.alreadyCancelled': 'Эта запись уже отменена.',
  'err.lessonStarted': 'Занятие уже началось — отменить его здесь нельзя. Напишите тренеру.',
  'err.rateLimited': 'Слишком много записей подряд — подождите минуту и попробуйте снова.',
  'err.signIn': 'Откройте приложение из чата с ботом, чтобы войти.',
  'err.tryAgain': 'Что-то пошло не так. Попробуйте ещё раз.',

  'rail.nothingOpen': 'В этот день свободного времени нет',
  'rail.tryAnother': 'Выберите другую дату на полосе выше.',
  'rail.waiting': 'В очереди',
  'rail.booked': 'Записаны',
  'rail.creditCost': ['{n} кредит', '{n} кредита', '{n} кредитов'],
  'day.today': 'Сегодня',

  'health.ask': 'Тренер может записывать травмы и особенности здоровья, чтобы занятия были безопасными. Вы не возражаете?',
  'health.yes': 'Да, можно',
  'health.no': 'Нет, не надо',

  'err.reopenApp': 'Закройте и откройте приложение заново',
  'su.title': "Регистрация тренера",
  'su.lead': "Настройка займёт минуту. Всё это можно поменять потом в Настройках.",
  'su.name': "Ваше имя",
  'su.city': "Клуб / город",
  'su.cityPh': "Например: Padel Club Limassol",
  'su.language': "Язык",
  'su.sport': 'Что вы тренируете?',
  'co.set.lastSport': 'Теннис — ваш единственный вид спорта, он остаётся включённым.',
  'su.timezone': "Часовой пояс",
  'su.slug': "Ваша ссылка для клиентов",
  'su.slugOk': "Свободна",
  'su.slugTaken': "Уже занята",
  'su.slugReserved': "Это слово занято системой",
  'su.slugFormat': "Латиница, цифры и дефис, 2–31 символ",
  'su.slugChecking': "Проверяем…",
  'su.trial': "Тариф «Основатель»: 49 € в месяц, первые 14 дней бесплатно.",
  'su.start': "Начать",
  'su.starting': "Создаём…",
  'su.contactTitle': "Напишите нам",
  'su.contactClosed': "Этот аккаунт закрыт. Напишите нам — разберёмся.",
  'su.contactLimit': "На сегодня регистрации закончились. Напишите нам — подключим.",
  'su.errName': "Введите имя",
  'su.errCity': "Укажите клуб или город",
  'su.errTimezone': "Выберите часовой пояс из списка",
  'dr.title': "Упражнения",
  'dr.rowStatus': "Ваша библиотека упражнений из планов занятий",
  'dr.build': "Собрать из планов",
  'dr.buildPending': "новых: {n}",
  'dr.building': 'Читаем планы… это займёт до минуты',
  'dr.built': "Прочитано планов: {plans}. Новых упражнений: {created}.",
  'dr.search': "Поиск упражнений",
  'dr.empty': "Пока пусто. Нажмите «Собрать из планов».",
  'dr.nothing': "Ничего не нашлось.",
  'dr.used': "использовано {n} {times} · {m} {players}",
  'dr.times': ["раз", "раза", "раз"],
  'dr.players': ["игрок", "игрока", "игроков"],
  'dr.result': "у {k} из {m} оценка выросла",
  'dr.general': "общее",
  'dr.edit': "Упражнение",
  'dr.name': "Название",
  'dr.skills': "Навыки (до двух)",
  'dr.skillsMore': 'Все навыки',
  'dr.skillsLess': 'Свернуть',
  'dr.save': "Сохранить",
  'dr.mergeInto': "Объединить с другим упражнением",
  'dr.merge': "Объединить",
  'dr.mergeConfirm': "Объединить «{a}» с «{b}»? Занятия и варианты названия перейдут.",
  'dr.archive': "В архив",
  'dr.archiveConfirm': "Убрать «{name}» в архив?",
  'dr.aliases': "Также пишут: {list}",
  'dr.helpTitle': "Что помогало с этим у других",
  'dr.helpLoading': "Смотрим вашу библиотеку…",
  'dr.helpNone': "В вашей библиотеке пока нет упражнений для этого навыка.",
  'dr.new': "новое",
  'dr.toPlan': "В план следующего занятия",
  'dr.added': "Добавлено в план занятия {when}.",
  'dr.noNext': "У игрока нет записи на следующее занятие.",
  'dr.close': "Закрыть",
  'dr.taken': "Упражнение с таким названием уже есть.",
  'dr.noAi': "AI на сервере не настроен.",
  'dr.all': 'Все {n}',
  'dr.noDesc': 'Без описания {n}',
  'dr.noDescRow': 'Без описания · добавить',
  'dr.noSkill': 'Без навыка',
  'dr.usesShort': '{n}×',
  'dr.resultShort': '{k} из {m} ↑',
  'dr.editBtn': 'Изменить',
  'dr.how': 'Как выполнять',
  'dr.howPh': 'Кто набрасывает, откуда, куда летит мяч',
  'dr.volume': 'Объём',
  'dr.volumePh': '3 корзины × 20 мячей · 12 минут',
  'dr.watch': 'На что смотреть',
  'dr.watchPh': 'Техника, ноги, тайминг',
  'dr.harder': 'Усложнение',
  'dr.harderPh': 'Как сделать сложнее',
  'dr.notFilled': 'Не заполнено',
  'dr.stats': 'Статистика',
  'dr.statsOwn': 'У вас',
  'dr.statsOthers': 'У других тренеров',
  'dr.statsOthersSoon': 'Появится вместе с общим каталогом упражнений.',
  'dr.aiDraft': '✨ Черновик описания с AI',
  'dr.aiWorking': 'Пишем черновик…',
  'dr.aiDrafted': 'Черновик от AI — проверьте и сохраните.',
  'dr.toSession': 'В план занятия',
  'dr.pickSession': 'В какое занятие?',
  'dr.noSessions': 'В ближайшие две недели занятий нет.',
  'dr.hasPlan': 'план есть',
  'dr.cancel': 'Отмена',
  'dr.nameOther': "Название на английском",
  'dr.tidy': "Привести в порядок",
  'dr.tidyLoading': "Смотрим библиотеку… это займёт до минуты",
  'dr.tidyHint': "Ничего не сохранится, пока вы не примете. Планы занятий не меняются.",
  'dr.tidyNone': "Всё уже в порядке.",
  'dr.tidyNames': "Названия",
  'dr.tidyMerges': "Повторы",
  'dr.accept': "✓ Принять",
  'dr.change': "✎ Изменить",
  'dr.skip': "Пропустить",
  'dr.acceptAll': "Принять все ({n})",
  'dr.accepted': "Сохранено",
  'dr.skipped': "Пропущено",
  'dr.mergedAway': "Объединено с другим упражнением",
  'dr.mergeRow': "Одно и то же упражнение: объединить с «{b}»",
  'dr.addSkills': "+ навыки: {list}",
  'story.button': 'Поделиться окнами',
  'story.title': 'Поделиться окнами',
  'story.thisWeek': 'Эта неделя',
  'story.nextWeek': 'Следующая неделя',
  'story.dark': 'Тёмная',
  'story.light': 'Светлая',
  'story.hint': 'Нажмите на время, чтобы убрать его из сторис.',
  'story.hiddenTitle': 'Убрано — нажмите, чтобы вернуть',
  'story.empty': 'На этой неделе свободных окон нет.',
  'story.send': 'Отправить мне',
  'story.sending': 'Отправляем…',
  'story.sent': 'Готово: картинка и ваша ссылка — в чате с ботом.',
  'story.noLink': 'Ссылка ещё не готова. Попробуйте через минуту.',
  'fs.title': "Первые шаги",
  'fs.left': "Готово {n} из 5",
  'fs.rowStatus': 'Расписание, цены, горячие часы, ссылка для клиентов, упражнения',
  'fs.schedule': "Расписание",
  'fs.scheduleHint': 'Рабочие часы: клиенты записываются только в них.',
  'fs.link': 'Ссылка для клиентов',
  'fs.linkHint': 'Отправьте её клиентам: по ней они записываются к вам.',
  'fs.prices': "Цены",
  'fs.pricesHint': "Цена занятия — на экране цен.",
  'fs.hot': "Горячие часы",
  'fs.hotHint': 'Отметьте 🔥 свободное время, которое хотите заполнить в первую очередь: клиенты увидят отметку.',
  'fs.noHot': "Пока без горячих часов",
  'fs.open': "Открыть →",
  'fs.copy': "Скопировать ссылку",
  'fs.copied': "Скопировано ✓",
  'br.title': "Сводка",
  'br.on': "Присылать сводку",
  'br.off': "Выключена",
  'br.statusEvening': "Вечером накануне, {time}",
  'br.statusMorning': "Утром, {time}",
  'br.evening': "Вечером накануне",
  'br.morning': "Утром",
  'br.time': "Время",
  'br.hintEvening': "Занятия на завтра в Telegram: кто, оплата, над чем работаете. Только если завтра есть занятия.",
  'br.hintMorning': "Занятия на сегодня в Telegram. Если первое начинается раньше, сводка придёт за 30 минут до него.",
  'fs.brief': "Сводка на завтра",
  'fs.briefHint': "По желанию: каждый вечер в Telegram — занятия на завтра.",
  'fs.briefOn': "Включить",
  'cat.share': "Делиться упражнениями",
  'cat.shareOn': "Вкл · в каталоге {n} из {m}",
  'cat.shareOff': "Выкл · каталог и результаты других скрыты",
  'cat.title': "Каталог упражнений",
  'cat.what': "Вы делитесь названием, навыком, описанием и итогами (у скольких игроков выросла оценка). Никогда — планами, клиентами или оценками.",
  'cat.give': "Каталог и «у других тренеров» видны, пока делитесь вы.",
  'cat.author': "Указать моё имя",
  'cat.authorPh': "Игорь, Лимассол",
  'cat.anon': "Сейчас: анонимно",
  'cat.authorSave': "Сохранить имя",
  'cat.contrib': "Ваш вклад",
  'cat.contribLine': "{n} {coaches} используют ваши упражнения",
  'cat.contribPct': " · помогло у {pct} % игроков",
  'cat.contribNone': "Пока никто не взял ваши упражнения.",
  'cat.badge': "🏅 Упражнение месяца: {name}",
  'cat.notShared': "Не в каталоге: {n}",
  'cat.send': "Отправить в каталог",
  'cat.previewTitle': "В каталог",
  'cat.reading': "Сверяем с каталогом… до минуты",
  'cat.sameQ': "Это то же упражнение?",
  'cat.yes': "Да",
  'cat.no': "Нет",
  'cat.newOnes': "Новые в каталоге: {n}",
  'cat.shareBtn': "Поделиться",
  'cat.shared': "Готово: связано {linked}, новых в каталоге {created}.",
  'cat.nothing': "Все ваши упражнения уже в каталоге.",
  'cat.others': "у {k} из {m} игроков выросла · {n} {coaches}",
  'cat.othersNoResult': "пока без результатов · {n} {coaches}",
  'cat.othersHidden': "Включите «Делиться упражнениями», чтобы видеть.",
  'cat.othersNone': "Этого упражнения пока нет в каталоге.",
  'cat.section': "Каталог · другие тренеры",
  'cat.tag': "каталог",
  'cat.by': "Автор: {name}",
  'cat.byAnon': "Автор: анонимный тренер",
  'cat.add': "Добавить к себе",
  'cat.added': "Добавлено ✓",
  'cat.result': "у {k} из {m} выросла",
  'cat.coachesN': "{n} {coaches}",
  'cat.topTitle': "Упражнения из каталога",
  'cat.topHint': "Лучшие по результатам у других тренеров. Отметьте, что добавить.",
  'cat.addN': "Добавить упражнений: {n}",
  'cat.addedN': "Добавлено упражнений: {n}.",
  'cat.turnOn': "Включить и открыть каталог",
  'cat.empty': "В каталоге пока пусто.",
  'fs.catalogue': "Упражнения из каталога",
  'fs.catalogueHint': "Возьмите проверенные упражнения других тренеров в свою библиотеку.",
  'co.axis.shared': "→ {skill}",
  'co.axis.sharedLabel': "Навык в общем каталоге",
  'co.axis.sharedAi': "предложено AI",
  'biz.cat': "Каталог упражнений",
  'biz.catEntries': "Упражнений в каталоге",
  'biz.catSharing': "Делятся: {n} · не делятся: {m}",
  'biz.catSeed': "Заполнить каталог из моей библиотеки",
  'biz.catSeeding': "Заполняем… до минуты",
  'biz.catSeeded': "Готово: связано {linked}, новых {created}.",
  'biz.catMonth': "Упражнение месяца ({month}): {name} · у {k} из {m} · тренеров: {n}",
  'biz.catMonthNone': "Упражнения месяца пока не было.",
  'cat.coaches': ['тренер', 'тренера', 'тренеров'],
  'err.unknownLink': "Ссылка не найдена — попросите тренера прислать ссылку ещё раз",
  'biz.contactTitle': "Контакт для связи",
  'biz.contactPh': "@username",
  'biz.contactHint': "Показывается тренерам, которые не могут зарегистрироваться (аккаунт закрыт или дневной лимит исчерпан).",
  'err.generic': 'Что-то пошло не так. Попробуйте ещё раз.',
  'err.couldNot': 'Не получилось: {message}',

  'app.signInFailed': 'Не удаётся войти',
  'a11y.chooseDay': 'Выберите день',
  'a11y.remove': 'Убрать',
  'safe.couldNotShow': 'Не удалось показать: {message}',
  'co.month.setCourtRate': 'Укажите стоимость корта в настройках, чтобы видеть, что остаётся вам после аренды.',
  'co.cd.back': 'Назад',
  'co.cd.bookFor': 'Записать: {name}',
  'co.cd.gift': '🎁 Подарок',
  'co.cd.nothingOpen': 'В ближайшие 30 дней свободного времени нет.',
  'co.cd.noCreditsLeft': 'У клиента {name} не осталось кредитов на счету — запишите с оплатой за занятие.',
  'co.cd.moved': 'Перенесено на {time}: {n} {sessions}.',
  'co.cd.movedSkipped': ' Не удалось перенести: {n} — это время уже занято.',
  'co.cd.moveTitle': 'Перенести занятие: {name}',
  'co.cd.moveCurrently': 'Сейчас: {day}, {time}.',
  'co.cd.newDay': 'Новый день',
  'co.cd.newTime': 'Новое время',
  'co.cd.startTitle': 'Где {name} сейчас',
  'co.cd.startExplain': 'Отправная точка — по тому, что вы уже знаете. Дальше её сменят оценки за занятия.',
  'co.cd.paysFor': '{payer} платит за клиента {name}',
  'co.cd.paysForBody': 'Записи оплачиваются кредитами со счёта {payer}.',
  'co.cd.paysSelf': '{name} платит за себя',
  'co.cd.pkgUnused': 'Неиспользованные кредиты будут списаны: {n} {credits}.',
  'co.cd.creditsRemoved': 'Списано: {n} {credits}. Осталось на счету: {balance}.',
  'co.cd.switched': 'Переведено на оплату со счёта: {n}. Осталось на счету: {balance}.',
  'co.cd.switchedCash': ' Остались на оплате за занятие: {n} — не хватило кредитов на счету.',
  'co.cd.noCreditsToUse': 'У клиента {name} нет кредитов на счету.',
  'co.cd.noHealthNotes': 'Без заметок о здоровье — клиент попросил не записывать травмы и особенности здоровья.',
  'co.today.freeDay': 'Свободный день',
  'co.today.freeDayHint': 'Пока никто не записался.',
  'co.rate.paidInFull': 'Занятие уже оплачено полностью. Никто ничего не должен, цены не меняются.',
  'co.rate.eachPays': 'Каждый платит',
  'co.money.heldTitle': 'Оплачено, но не проведено: €{amount}',
  'co.money.heldHint': 'Занятия оплачены заранее и ещё не проведены.',
  'co.money.heldNone': 'Ни у кого нет оплаченных занятий впрок.',
  'co.money.collectTitle': 'К получению: €{amount}',
  'co.money.collectHint': 'Проведённые, но ещё не оплаченные занятия.',
  'co.money.failed': 'Не удалось загрузить цифры.',
  'co.pp.howPlays': 'Как играет {name}',
  'co.pp.playtomicLabel': 'Уровень в Playtomic (необязательно, для справки)',
  'co.pp.playtomicPlaceholder': 'например, 4.2',
  'co.pp.since': '{delta} с {month}',
  'co.pp.scaleMid': 'Средний 3–4.5',
  'co.pp.yourAssessment': 'Ваша оценка · {date}',
  'co.pp.playtomicRef': 'Playtomic {n} · для справки',
  'co.pp.levelTitle': 'Уровень: {name}',
  'co.pp.lower': 'Понизить на 0.1',
  'co.pp.raise': 'Повысить на 0.1',
  'co.pp.levelOverTime': 'Уровень со временем',
  'co.pp.aiOffCard': 'Пока не настроено. Добавьте ANTHROPIC_API_KEY на сервер в Railway, и карточка заработает.',
  'co.pp.aiPrivate': 'Это видите только вы. Чтобы написать разбор, ваши заметки отправляются в Claude.',
  'co.pp.aiStale': 'Есть новые заметки',
  'co.pp.aiStaleLang': 'Устарело: нажмите «Обновить»',
  'co.pp.aiRefresh': 'Обновить',
  'co.pp.aiThemes': 'Повторяется снова и снова',
  'co.pp.aiHealthPrivate': '🔒 Заметки о здоровье остаются личными, {name} их не увидит.',
  'co.pp.blk.overview': "Общая картина",
  'co.pp.blk.better': "Что стало лучше",
  'co.pp.blk.stuck': "Требует внимания",
  'co.pp.blk.next': "Что делаем дальше",
  'co.pp.blk.homework': "Домашнее задание",
  'co.pp.aiFocusPlayer': "Игрок получит: {text}",
  'co.pp.aiFocus': 'Предлагаемая цель',
  'co.pp.aiUseFocus': 'Сделать целью «Работаем над»',
  'co.pp.src.standing': 'Постоянные заметки',
  'co.pp.src.workingOn': 'Текущая цель',
  'co.pp.src.earlierGoal': 'Прежняя цель',
  'co.pp.src.focus': 'Над чем работали',
  'co.pp.src.drills': 'Упражнения',
  'co.pp.src.private': 'Личная заметка',
  'co.pp.src.shared': 'Заметка для игрока',
  'co.pp.src.common': 'Для всех на занятии',
  'co.pp.src.homework': 'Домашнее задание',
  'co.pp.src.asked': 'Игрок просил поработать над',
  'co.pp.src.undated': 'Постоянно',
  'co.set.failed': 'Не получилось: {message}',
  'co.set.peakNone': 'Каждый час стоит €{base}. Добавьте тариф, если в какие-то часы корт дороже.',
  'co.set.bandPerHour': '€{price}/час',
  'co.set.bandOverlap': 'Пересекается с {band}: действует одна ставка, не сумма',
  'co.set.courtsTitle': 'Какие корты у вас есть',
  'co.set.courtsHint': 'Записаться можно только на эти часы. Нажмите на час или день, чтобы заполнить весь ряд.',
  'co.set.saveChanges': 'Сохранить изменения',
  'err.blocked': 'Тренер закрыл вам доступ, поэтому это действие недоступно.',

  'co.set.approveNew': 'Одобрять новых клиентов',
  'co.set.approveNewHint': 'Кто открыл вашу ссылку, не видит расписание и не может записаться, пока вы его не одобрите. О каждом придёт сообщение. Друзей, которых позвали ваши клиенты, пускаем сразу.',
  'co.req.title': 'Новые заявки',
  'co.req.waiting': '{names} — ждут вашего ответа',
  'co.req.waitingOne': '{names} — ждёт вашего ответа',
  'co.req.aria': 'Новые заявки: ждут {n}',
  'co.req.intro': 'Открыли бота по вашей ссылке. Пока вы их не одобрите, они не видят расписание и не могут записаться.',
  'co.req.noUsername': 'без имени пользователя',
  'co.req.approve': 'Одобрить',
  'co.req.block': 'Заблокировать',
  'co.req.blockConfirm': 'Заблокировать: {name}? Клиенту об этом не сообщат.',
  'co.req.foot': 'Одобренному клиенту придёт сообщение, что можно записываться. Блокировка — без уведомления.',
  'co.req.close': 'Закрыть',
  'book.waitTitle': 'Почти готово',
  'book.waitBody': '{coach} одобряет новых клиентов перед первой записью. Мы напишем вам здесь, в Telegram, как только можно будет записаться.',
  'book.yourCoach': 'Тренер',
  'err.notApproved': 'Тренер ещё не одобрил вас. Мы напишем, как только можно будет записаться.',

  'mp.level': 'Уровень · Playtomic',
  'mp.levelSetBy': 'Оценка тренера {coach}, {date}',
  'mp.skills': 'Ваши навыки',
  'mp.training': 'Тренировки',
  'mp.noRatings': 'Тренер ещё не оценивал занятия. Навыки появятся здесь после первой оценки.',
  'mp.fourWeeks': 'Последние четыре недели',
  'mp.dayLesson': 'Занятие',
  'mp.dayNoShow': 'Пропуск',
  'mp.sessionsCount': '{n} {sessions}',
  'mp.noShowsCount': 'пропусков: {n}',

  'credits.title': 'Кредиты',
  'credits.word': ['кредит', 'кредита', 'кредитов'],
  'credits.leftCount': 'На счету: {n} {credits}',
  'ledger.packageRemoved': 'Пакет убран тренером',

  'co.offer.title': 'Кому предложить {when}?',
  'co.offer.regulars': 'Обычно в это время',
  'co.offer.frequent': 'Часто ходят',
  'co.offer.added': 'Добавлены',
  'co.offer.add': '+ Добавить клиента',
  'co.offer.searchPh': 'Имя клиента',
  'co.offer.noneFound': 'Никого не нашли',
  'co.offer.daysPl': 'Понедельники|Вторники|Среды|Четверги|Пятницы|Субботы|Воскресенья',
  'co.offer.regularChip': '{days} · {n} из последних 8',
  'co.offer.freqChip': '{n} {sessions} за 8 недель',
  'co.offer.sessionsW': ['занятие', 'занятия', 'занятий'],
  'co.offer.notInBot': 'не в боте',
  'co.offer.never': 'Заблокированные клиенты и новые заявки сюда не попадают. Никому ничего не придёт, пока вы не нажмёте «Предложить».',
  'co.offer.cancel': 'Отмена',
  'co.offer.send': 'Предложить ({n})',
  'co.offer.foot': 'Обычная цена: кредитами, если хватает, иначе оплата на занятии. Кто первым нажмёт «Беру», тот и записан.',
  'co.offer.sent': 'Предложено {n} {clients}. Кто первым нажмёт «Беру», тот и записан.',
  'co.offer.clientsDat': ['клиенту', 'клиентам', 'клиентам'],
  'co.offer.nobody': 'Некого предложить: за 8 недель не было занятий. Добавьте клиента по имени.',
  'co.offer.failed': 'Не удалось открыть предложение.',
  'co.offer.notYours': 'Это время другого тренера — предложить его нельзя.',
  'co.offer.state.offered': 'Уже предложено.',
  'co.offer.state.taken': 'Уже занято.',
  'co.offer.state.kept': 'Вы решили не предлагать это время.',
  'co.offer.state.closed': 'Предложение закрыто: до начала осталось слишком мало времени.',
};

export type Key = keyof typeof EN;

/**
 * «{n} {credits}» agrees by itself: a string that names credits after a
 * number gets the right form from `n` when the caller does not pass one —
 * 1 кредит, 2 кредита, 5 кредитов, 21 кредит, 0,25 кредита.
 */
function withCredits(lang: Lang, text: string, vars?: Vars): Vars | undefined {
  if (!vars || vars.credits !== undefined || !text.includes('{credits}') || vars.n === undefined) return vars;
  const n = Number(String(vars.n).replace(',', '.'));
  return Number.isNaN(n) ? vars : { ...vars, credits: wordOf(lang, 'book.credit', n) };
}

export function translate(lang: Lang, key: Key, vars?: Vars): string {
  // Every coach screen reports a refused change as "Failed: <code>". For a
  // coach the owner switched off, say what is going on instead.
  if (key === 'co.failed' && vars?.message === 'coach_switched_off') return translate(lang, 'bill.offAction');
  const ru = lang === 'ru' ? RU[key] : undefined;
  const value = (typeof ru === 'string' ? ru : undefined) ?? (EN[key] as unknown as string);
  const text = typeof value === 'string' ? value : String(value);
  vars = withCredits(lang, text, vars);
  if (lang === 'ru') return fill(...russianNumbers(text, vars));
  return fill(text, vars);
}

export function wordOf(lang: Lang, key: Key, n: number): string {
  const ru = lang === 'ru' ? RU[key] : undefined;
  const forms = (Array.isArray(ru) ? ru : (EN[key] as unknown as readonly string[]));
  return plural(lang, n, forms);
}

/** const t = useT();  t('book.book') */
export function useT() {
  const lang = useLang();
  const fn = (key: Key, vars?: Vars) => translate(lang, key, vars);
  fn.lang = lang;
  fn.word = (key: Key, n: number) => wordOf(lang, key, n);
  fn.longDay = (d: Date) => fmtLongDayL(d, lang);
  fn.shortDay = (d: Date) => fmtShortDayL(d, lang);
  fn.time = (d: Date | string) => fmtTimeL(d, lang);
  fn.at = () => atWord(lang);
  return fn;
}
